import type { Machine, MachineType, MachineStatus, AppState, SensorConfig, Alert, Alarm, MoldCounter } from './types';
import type { RandDiagnosticCases, RandMachineAlarms, RandMachines, RandMaintenanceIntervals } from './tipuri-baza';
import { aplicaTema, temaD } from './tema';
import { buildLineCardD, buildUtilitiesCardD, antetProductieD, incarcaPozeD, deschideLinieD } from './linie-d';
import { state, api, $, $$, toast, escHtml, truncate, todayStr, _timeAgo, recordHoursHistory, buildSparklineSVG, computeAlerts, updateAlarmBadge, renderAlarmPanel, _updateHelpBadge, _allMachinesCache, intervalHoursProgress, shouldShowMaintenanceInterval, shouldSuppressLineMaintenanceAlerts, filterMaintenanceAlertsForLine, formatDateTimeRO, openAlarmHistoryForMachine, openAlarmHistoryForLine, badgeStare, oreRamaseStare } from './core';
import type { StareLucrare } from './core';
import { askText, askConfirm } from './dialogs';
import { openControllerAlarmDetailModal } from './alarm-manual';
import { fetchLinesPanel, buildLineCardHtml, buildUtilitiesCardHtml, wireLinePanelContainer } from './line-panel';
import type { PanelData, PanelLine } from './line-panel';

declare function _loadQualityAlerts(): Promise<AlertaCalitate[]>;
declare function _updateEnergyLiveValues(): void;
declare function openLineDetail(eqId: string | undefined, name?: string, mode?: string): void;
declare function openLineSummary(lineId: string): Promise<void>;
declare function openEditEquipModal(eq: unknown, fromLineId: string): void;
declare function loadAlarmHistory(page?: number): void;
declare function openAddLineModal(): void;
declare function openAddEquipModal(): void;
declare function openSharedSensorsModal(): void;
declare function openCountersModal(): void;
declare function openCollectorModal(): void;
declare function loadFactoryLayout(): void;
declare let detailTabActive: string | undefined;
declare function _updateSensorsTabLive(lineId: string, cfgs: SensorCfg[], isMold: boolean): void;
/** parseFloat/parseInt primesc aici și numere (orele, ciclurile, kW vin ca număr din /api/factory_structure și din
 *  contoare): JS face întâi text din argument (ToString), deci rezultatul e același. Doar tip — la rulare sunt cele globale. */
declare function parseFloat(s: string | number): number;
declare function parseInt(s: string | number, radix?: number): number;

/** API sensor row shape (runtime; richer than types.SensorConfig). */
type SensorCfg = SensorConfig & {
    sensor_key: string;
    enabled: boolean;
    warn_threshold: number;
    crit_threshold: number;
    base_value?: number;
    variance_pct?: number;
};

type OverviewState = AppState & {
    sensorsFromServer: Record<string, Record<string, CitireSenzor>>;
    _em: { machineId: string | null; period: string; param: string; historyData: unknown; loading: boolean };
    alarmSummary: Record<string, { total_active?: number; critical_count?: number }>;
    activeMoldIds?: Set<string>;
    /** puse de loadOverview: intervalele după cheia alertei (`<utilaj>:<id>`), starea de operare și GO-LIVE după utilaj */
    maintenanceIntervalsMap?: Record<string, IntervalCuStare>;
    opStateMap?: Record<string, StareOperare>;
    goliveMachineMap?: Record<string, GoliveUtilaj>;
    goliveLineMap?: Record<string, GoliveLinie>;
    /** /api/lines/status după linie (loadDashboard, loadOverview) */
    linesStatusMap?: Record<string, StareLinie>;
    /** ultima eroare din loadOverview, pentru depanare */
    lastOverviewError?: { message: string; stack: string; ts: number };
};

/* ═══ CE TRIMITE SERVERUL (rutele citite aici) ═════════════════ */
/** o citire din /api/telemetry/sensors/latest (routes/sensors.py): număr (sensor_readings, senzorii comuni)
 *  sau text (sensor_text_readings: machine_status, current_mold) */
interface CitireSenzor { value: number | string; updated_at: string | null; }
/** ce dă getSensorRaw: `value` din citire, sau numărul din state.sensors. Intrarea întreagă iese doar pe ramura
 *  „formă veche” (intrare fără `value`), pe care serverul de azi n-o mai trimite */
type ValoareSenzor = number | string | CitireSenzor | undefined;
/** /api/alarms (routes/other.py, fără `grupat`): rândul din machine_alarms + numele utilajului */
interface AlarmaListata extends RandMachineAlarms { machine_name: string | null; }
/** /api/alarms/summary (routes/other.py): alarmele active (fără cele de mentenanță), numărate pe utilaj */
interface SumarAlarmeUtilaj {
    machine_id: string; total_active: number; critical_count: number; warning_count: number; unacked_count: number;
}
/** /api/telemetry/sensors/hourly_avg (routes/sensors.py): media puterii pe ultima oră, sau `manual_power_kw` */
interface MedieOra { avg_power_kw: number; samples: number; source: 'meter' | 'manual'; }
/** cheia `__lines__` din același răspuns: puterea atribuită liniei (power_aggregation.compute_line_active_power_map) */
interface PutereLinie {
    power_kw: number; power_kw_live: number; status: 'running' | 'stopped' | 'unknown'; active: boolean;
    decision_power_kw: number | null;
}
/** GET /api/config/machines/<id>/counters (routes/config.py) */
interface ContoareUtilaj { cycle_count: number; operating_hours: number; linked_machine_id: string; status: string; }
/** răspunsul unei scrieri (PUT/POST/DELETE) refuzate: `{error}` din rută sau din auth.py:require_role */
interface RaspunsScriere { error?: string; }
/** POST …/mark_maintenance_stop, …/clear_maintenance_stop (routes/maintenance/stare_masina.py): `detail` nu-l trimite nicio rută */
interface RaspunsOprire extends RaspunsScriere { detail?: undefined; }

/** un utilaj din /api/factory_structure (routes/machines.py): rândul din machines, cu orele și ciclurile făcute număr și
 *  numărătorile adăugate. `serves`/`shared` doar pe utilajele de lângă mașina principală, `is_active` doar pe matrițe */
interface UtilajStructura extends Omit<RandMachines, 'operating_hours' | 'cycle_count'> {
    operating_hours: number; cycle_count: number;
    parts_count: number; intervals_count: number; errors_count: number; docs_count: number;
    consumption_kwh_total: number; consumption: number;
    assigned_counter: { id: number; name: string; protocol: string; host: string | null; port: number | null;
                        slave_id: number | null } | null;
    serves?: string[]; shared?: boolean; is_active?: boolean;
}
/** o linie din /api/factory_structure: coloanele din production_lines, utilajele și puterea atribuită (power_aggregation) */
interface LinieStructura {
    id: string; product: string; line_type: string; group_type: string; icon: string | null;
    is_active: number; started_at: string | null;
    main_machine: UtilajStructura | null; equipment: UtilajStructura[];
    production_rate: number; consumption_kwh_total: number; consumption: number;
    power_kw_attributed: number | null; power_kw_attributed_live: number | null;
    power_status: 'running' | 'stopped' | 'unknown' | null; power_active: boolean | null;
    operating_hours: number; operating_hours_source: string; manual_hours_set_at: string | null; last_active_at: string | null;
    totals: { parts: number; intervals: number; errors: number; docs: number };
}
/** un grup de utilități din /api/factory_structure (`utilities`, după id-ul liniei de tip utility) */
interface GrupUtilitati { label: string; icon: string | null; machines: UtilajStructura[]; }
/** /api/factory_structure (routes/machines.py) */
interface StructuraFabrica {
    production_lines: LinieStructura[]; utilities: Record<string, GrupUtilitati>; total_machines: number;
    /** nu-l trimite ruta — rezerva `struct.lines` din loadDashboard e moartă */
    lines?: undefined;
}
/** /api/maintenance_intervals (routes/maintenance/intervale.py): rândul + ultima lucrare potrivită + starea utilajului */
interface IntervalCuStare extends RandMaintenanceIntervals {
    last_log_hours: number | null; last_log_date: string | null; last_log_synthetic: number;
    machine_state: StareMentenanta; should_hide_alert: boolean; workflow_id?: number | null;
    /** 1 nov 2026: starea din sursa unică (routes/maintenance/_stare.py) — n-o mai socotim în pagină */
    stare?: StareLucrare;
    /** ruta NU trimite numele utilajului (SELECT * din maintenance_intervals): fereastra alertei cade pe id */
    machine_name?: undefined;
}
/** /api/ai/proactive_alerts (routes/chat.py): textul e în `message` — `action` nu există */
interface AlertaProactiva {
    machine_id: string; type: 'anomaly' | 'overdue' | 'status' | 'controller_alarm'; severity: 'high' | 'medium';
    message: string; sensor_key?: string; value?: number; alarm_code?: string; alarm_type?: string | null;
    action?: undefined;
}
/** /api/quality_alerts (routes/other.py), prin quality.ts:_loadQualityAlerts: doar verificările scadente */
interface AlertaCalitate {
    type: 'quality_check'; line_id: string; machine_id: string; mold_id: string | null; template_name: string;
    severity: 'critical' | 'warning'; remaining: string; action: string;
}
/** /api/lines/status (routes/config.py): starea live a liniei (colector, putere, running_state) */
interface StareLinie {
    id: string; product: string; group_type: string; icon: string | null; sort_order: number | null;
    power_gating_enabled: boolean; power_threshold_kw: number; power_decision_machine_id: string | null;
    collector_status: 'green' | 'yellow' | 'red' | 'unknown'; collector_last_seen: string | null;
    collector_hb_age_sec: number | null; last_sensor_reading_at: string | null; data_age_sec: number | null;
    power_kw: number | null; power_at: string | null; power_meter_id: string | null;
    running_state: 'running' | 'stopped' | 'unknown'; running_source: 'putere' | 'semnal_linie' | null;
    attributed_power_kw: number | null; attributed_power_kw_live: number | null;
}
/** /api/factory/setup-todo (routes/machines.py): golurile de configurare, pe categorii */
interface TodoConfigurare { total: number; categories: TodoCategorie[]; }
interface TodoCategorie { key: string; label: string; icon: string; items: TodoElement[]; }
interface TodoElement { machine_id: string; label: string; detail: string; fix: string; }
/** /api/stats (routes/other.py): cifrele citite aici (ruta trimite și mold_components, log_entries) */
interface Statistici { parts: number; errors: number; intervals: number; documents: number; }
/** starea de mentenanță a utilajului (services/technical_pause.py:get_maintenance_state) */
type StareMentenanta = 'active' | 'maintenance' | 'technical_pause' | 'inactive' | 'no_data';
/** /api/machines/operational-state/all (routes/maintenance/stare_masina.py, services/technical_pause.py) */
interface StareOperare {
    machine_id: string; name: string | null; line: string | null; machine_type: string | null;
    is_running_now: boolean; offline_since: string | null; offline_duration_hours: number;
    is_technical_pause: boolean; technical_pause_threshold_hours: number;
    maintenance_state: StareMentenanta;
    zile_fara_telemetrie: number | null; ultima_telemetrie: string | null;
    workflow_id: number | null; flagged_by_user: boolean; flag_started_at: string | null; flag_reason: string;
    line_active: boolean;
}
/** un utilaj din /api/golive/audit (services/golive.py:audit_summary); `hypothetical` doar cu ?hypothetical=1,
 *  la cele gata de GO-LIVE */
interface GoliveUtilaj {
    id: string; name: string; line: string | null; machine_type: string | null; operating_hours: number;
    manual_hours_set_at: string | null; auto_hours_source: string | null; golive_at: string | null;
    requires_hours_input: number; line_active: number; intervals_total: number;
    status: 'live' | 'line_not_started' | 'ready_to_golive' | 'needs_hours';
    hypothetical?: IpotezaGolive;
}
/** ce s-ar întâmpla la GO-LIVE (run_machine_golive cu dry_run) */
interface IpotezaGolive { intervals_baselined: number; synthetic_events_added: number; history_used: number; }
/** o linie din /api/golive/audit; `main_machine_with_hours` doar la liniile nepornite */
interface GoliveLinie {
    id: string; product: string; group_type: string; is_active: number; started_at: string | null;
    started_by_user_id: number | null; machines_count: number; machines_with_hours: number; machines_live: number;
    ready_to_start: boolean; main_machine_with_hours?: boolean;
}
/** /api/golive/audit (routes/maintenance/golive_linii.py) */
interface AuditGolive {
    machines: GoliveUtilaj[]; lines: GoliveLinie[];
    summary: { machines_total: number; machines_live: number; machines_pending_hours: number;
               machines_line_inactive: number; lines_active: number; lines_pending: number };
    golive_date: string; history_anchor_date: string;
}
/** /api/golive/status (routes/maintenance/golive_linii.py): cifrele din `summary` + data GO-LIVE */
interface StareGolive {
    machines_live: number; machines_pending_hours: number; machines_line_inactive: number; machines_total: number;
    lines_active: number; lines_pending: number; golive_date: string;
}
/** POST /api/golive/machine/<id> (services/golive.py:run_machine_golive): cifrele la `ok`, `message`/`error` la refuz */
interface RezultatGolive extends RaspunsScriere {
    status?: 'ok' | 'already_live' | 'no_hours' | 'not_found' | 'line_inactive' | 'error'; message?: string;
    intervals_baselined?: number; synthetic_events_added?: number; history_used?: number;
}
/** POST /api/golive/line/<id>/start (services/golive.py:start_line): `summary` doar la `ok` */
interface RezultatStartLinie extends RaspunsScriere {
    status?: 'ok' | 'not_found' | 'already_active' | 'main_machine_no_hours' | 'error'; message?: string;
    summary?: RezumatStartLinie;
}
interface RezumatStartLinie {
    machines_total: number; machines_golive: number; machines_skipped_no_hours: number;
    intervals_baselined: number; synthetic_events_added: number; history_used: number;
}
/** `machine` din /api/line_detail/<id> (routes/machines.py:api_line_detail), ținut de line-detail.ts în `window._detailData`:
 *  rândul din machines cu orele și ciclurile făcute număr. Ruta NU trimite `power_kw_attributed`/`power_status` (le are
 *  doar /api/factory_structure, pe linie), deci ramura lor din antetul detaliului e moartă */
interface UtilajDetaliu extends Omit<RandMachines, 'operating_hours' | 'cycle_count'> {
    operating_hours: number; cycle_count: number; consumption_kwh_total: number; consumption: number;
    assigned_counter: { id: number; name: string; protocol: string; host: string | null; port: number | null;
                        slave_id: number | null; connection_extra: string | null } | null;
    power_kw_attributed?: undefined; power_status?: undefined;
}
/** utilajul din fereastra de editare a contoarelor: rândul din /api/machines/all (orele și ciclurile sunt TEXT în bază),
 *  luat din cache-ul din core.ts — tipat acolo `Machine`, cu număr — sau direct din rută */
type UtilajEditat = Pick<RandMachines, 'id' | 'name' | 'cavities'>
    & Partial<Pick<RandMachines, 'nominal_cycle_time_sec' | 'nominal_cph'>>
    & { operating_hours: string | number | null; cycle_count: string | number | null };
/** o linie pe tabloul vechi (loadDashboard, când /api/lines/panel nu răspunde): linia din /api/factory_structure sau
 *  un grup de utilități prefăcut în linie (fără GO-LIVE și fără putere atribuită) */
interface LinieTablou extends Pick<LinieStructura, 'id' | 'product' | 'line_type' | 'group_type' | 'equipment'
        | 'production_rate' | 'consumption'>,
    Partial<Pick<LinieStructura, 'is_active' | 'started_at' | 'power_kw_attributed' | 'power_status'>> {
    icon: string | null; main_machine: UtilajStructura | null;
}
/** linia din fereastra live (openLineLiveDetail): o linie din /api/factory_structure sau un grup de utilități,
 *  prefăcut în linie fără mașină principală */
type LinieLive = Pick<LinieStructura, 'id' | 'product' | 'equipment'> & { main_machine?: UtilajStructura | null };
/** ce primesc ajutoarele din core.ts: acolo `interval_hours`/`critical` n-au null, deși coloanele îl permit. Le dăm
 *  rândul întreg — la null, intervalHoursProgress face Number(null) = 0, iar shouldShowMaintenanceInterval îl ia ca 0 */
type IvProgres = Parameters<typeof intervalHoursProgress>[0];
type IvVizibil = Parameters<typeof shouldShowMaintenanceInterval>[0];
/** ce pun alte module pe `window` și citește modulul ăsta */
type FereastraOverview = Window & {
    webkitAudioContext?: typeof AudioContext;
    _detailData?: { machine: UtilajDetaliu }; _detailLineId?: string; detailTabActive?: string;
    openLineDetail?: typeof openLineDetail; showSection?: typeof showSection; loadAlarmHistory?: typeof loadAlarmHistory;
    refreshLinePanels?: () => void; loadOverview?: typeof loadOverview;
    openLineLayoutDetail?: typeof import('./line-layout').openLineLayoutDetail; _lpDashTimer?: number | null;
    openMachinePanel?: typeof import('./machine-panel').openMachinePanel;
    openLineConfig?: (lineId: string) => void; openLineSummary?: typeof openLineSummary;
};

const ost = state as OverviewState;

/* ═══ SENSOR SIMULATION (config-driven) ═══════════════════════ */
state.allEquipmentIds = [];
state.equipmentTypes = {};
state.sensorConfigs = {};
state.sensors = {};
ost.sensorsFromServer = {};
state.sensorHistory = state.sensorHistory || JSON.parse(localStorage.getItem('sensorHistory') || '{}');
state.consumptionHistory = state.consumptionHistory || {};
state.sensorAlarms = [];
state.controllerAlarms = [];
ost._em = { machineId: null, period: '1h', param: 'power', historyData: null, loading: false };
ost.alarmSummary = {};

export async function refreshControllerAlarms(): Promise<void> {
    if (!state.currentUser?.username) return;   // înainte de login dădea 401 la fiecare 5 s (audit 4 sep 2026)
    try {
        const [alarmsRes, summaryRes]: [{ total: number; alarms: AlarmaListata[] }, Record<string, SumarAlarmeUtilaj>] = await Promise.all([
            api('/api/alarms?active=1&limit=100'),
            api('/api/alarms/summary'),
        ]);
        const prev = state.controllerAlarms || [];
        // 11 oct 2026: alarma prin care mașina CERE mentenanță nu e defect — nu intră în panou și nici în insignă;
        // o arată Istoric Alarme (cu starea lucrărilor noastre), cardul liniei și panoul utilajului
        const noi = ((alarmsRes.alarms || []) as AlarmaListata[]).filter((a: AlarmaListata) => !a.e_mentenanta).map((a: AlarmaListata) => ({
            id: `ctrl:${a.id}`,
            db_id: a.id,
            machine_id: a.machine_id,
            machine_name: a.machine_name,
            action: a.description_ro || `Alarma ${a.alarm_code}`,
            severity: a.severity === 'critical' ? 'critical' : (a.severity === 'info' ? 'info' : 'warning'),
            category: 'CONTROLER',
            alarm_code: a.alarm_code,
            alarm_type: a.alarm_type,
            cause_ro: a.cause_ro,
            solution_ro: a.solution_ro,
            par1: a.par1,
            par2: a.par2,
            acked: a.acked,
            created_at: a.created_at,
            source: 'controller',
        }));
        state.controllerAlarms = noi as unknown as Alarm[];   // obiecte de panou (id „ctrl:N”), nu rânduri Alarm
        ost.alarmSummary = summaryRes || {};

        if (prev.length < noi.length) {
            const newCritical = noi.filter(
                a => a.severity === 'critical' && !prev.find(p => String(p.id) === String(a.id))   // id-urile din panou sunt „ctrl:N”
            );
            if (newCritical.length > 0) {
                _playAlarmSound();
            }
        }

        const all = [...(state.allAlerts || []), ...(state.sensorAlarms || []),
                      ...(state.openCaseAlerts || []), ...noi];
        const unacked = all.filter(a => {
            if (a.source === 'controller') return !a.acked;
            return !state.ackedAlarms.includes(a.id);
        });
        updateAlarmBadge(unacked.length);
    } catch (e: unknown) {}
}

export let _alarmAudioCtx: AudioContext | null = null;
export function _playAlarmSound(): void {
    try {
        const w = window as FereastraOverview;
        if (!_alarmAudioCtx) _alarmAudioCtx = new (w.AudioContext || w.webkitAudioContext)();
        const ctx = _alarmAudioCtx;
        const osc = ctx!.createOscillator();
        const gain = ctx!.createGain();
        osc.connect(gain);
        gain.connect(ctx!.destination);
        osc.frequency.value = 880;
        osc.type = 'square';
        gain.gain.value = 0.15;
        osc.start();
        gain.gain.exponentialRampToValueAtTime(0.001, ctx!.currentTime + 0.5);
        osc.stop(ctx!.currentTime + 0.5);
    } catch (e: unknown) {}
}

refreshControllerAlarms();
setInterval(refreshControllerAlarms, 5000);

export async function loadSensorConfigs(): Promise<void> {
    state.sensorConfigs = await api('/api/config/sensors_all');
}

export async function refreshSensorReadingsFromServer(): Promise<void> {
    try {
        const data: Record<string, Record<string, CitireSenzor>> = await api('/api/telemetry/sensors/latest');
        if (data && typeof data === 'object') {
            ost.sensorsFromServer = data;
            Object.entries(data).forEach(([eqId, readings]) => {
                if (!state.sensorHistory[eqId]) state.sensorHistory[eqId] = {};
                Object.entries(readings as Record<string, CitireSenzor>).forEach(([key, obj]) => {
                    const val = obj && typeof obj.value !== 'undefined' ? obj.value : obj;
                    if (val === undefined) return;
                    if (!state.sensorHistory[eqId][key]) state.sensorHistory[eqId][key] = [];
                    state.sensorHistory[eqId][key].push(val);
                    if (state.sensorHistory[eqId][key].length > 40) state.sensorHistory[eqId][key].shift();
                });
            });
            updateSensorDisplays();
            refreshSensorAlarmsFromReadings();
        }
    } catch (e: unknown) {}
}

export async function refreshHourlyAvg(): Promise<void> {
    try {
        const data: Record<string, MedieOra | Record<string, PutereLinie>> = await api('/api/telemetry/sensors/hourly_avg');
        if (data && typeof data === 'object') {
            state.hourlyAvg = data;
        }
    } catch (e: unknown) {}
}

export function getHourlyAvgPower(eqId: string): number | null {
    const h: MedieOra | undefined = state.hourlyAvg[eqId];
    if (h && h.avg_power_kw != null) return h.avg_power_kw;
    return null;
}

// Bug fix 2026-05-13: line-level attributed power.
// The backend `/api/telemetry/sensors/hourly_avg` embeds a `__lines__` map
// with per-line aggregated power, with shared chillers distributed only to
// ACTIVE lines and manual_power_kw gated by line activity. UI must read
// from here — do NOT sum equipment locally.
export function getLineAttributedPower(lineId: string): {
    power_kw: number | null;
    power_kw_live: number | null;
    status: 'running' | 'stopped' | 'unknown' | null;
    active: boolean | null;
} {
    const lines: Record<string, PutereLinie> | undefined = state.hourlyAvg.__lines__;
    if (!lines || !lines[lineId]) {
        return { power_kw: null, power_kw_live: null, status: null, active: null };
    }
    const l = lines[lineId];
    return {
        power_kw: typeof l.power_kw === 'number' ? l.power_kw : null,
        power_kw_live: typeof l.power_kw_live === 'number' ? l.power_kw_live : null,
        status: l.status || null,
        active: typeof l.active === 'boolean' ? l.active : null,
    };
}

// Returns the human-friendly consumption label for a line, using the
// authoritative backend attribution. Falls back to the legacy local sum
// while the first hourly_avg fetch is still in flight.
export function formatLinePowerLabel(
    lineId: string,
    fallbackKw?: number | null,
    fallbackHas?: boolean,
): string {
    const attr = getLineAttributedPower(lineId);
    if (attr.power_kw != null) {
        if (attr.status === 'stopped') {
            return attr.power_kw > 0.01
                ? attr.power_kw.toFixed(2) + ' kW (linie OPRITĂ — utilaje proprii)'
                : '0.00 kW (linie OPRITĂ)';
        }
        return attr.power_kw.toFixed(2) + ' kW (medie 1h)';
    }
    if (fallbackHas && fallbackKw != null) {
        return fallbackKw.toFixed(2) + ' kW (medie 1h)';
    }
    return '— kW';
}

export function getSensorValue(eqId: string, key: string): number | undefined {
    const fromServer = ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId][key];
    let serverVal = fromServer && (typeof fromServer.value !== 'undefined' ? fromServer.value : fromServer);
    if (serverVal === undefined || serverVal === null) {
        // Pentru senzorul "consum" (consum curent): dacă telemetria trimite "power", folosim ca fallback
        if (key === 'consum' && ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId].power != null)
            serverVal = ost.sensorsFromServer[eqId].power.value ?? ost.sensorsFromServer[eqId].power;
        else if (key === 'power' && ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId].consum != null)
            serverVal = ost.sensorsFromServer[eqId].consum.value ?? ost.sensorsFromServer[eqId].consum;
    }
    if (serverVal !== undefined && serverVal !== null) return Number(serverVal);
    const s = state.sensors![eqId];
    if (s && s[key] !== undefined) return s[key];
    if (key === 'consum' && s && s.power !== undefined) return s.power;
    if (key === 'power' && s && s.consum !== undefined) return s.consum;
    return undefined;
}

// Returns raw sensor value (number, string, or undefined). Useful for text values
// like machine_status="0A001" or current_mold="m48 turcoaz Liten ml 57" that
// must NOT be coerced to NaN by Number().
export function getSensorRaw(eqId: string, key: string): ValoareSenzor {
    const fromServer = ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId][key];
    let serverVal: ValoareSenzor = fromServer && (typeof fromServer.value !== 'undefined' ? fromServer.value : fromServer);
    if (serverVal === undefined || serverVal === null) {
        if (key === 'consum' && ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId].power != null)
            serverVal = ost.sensorsFromServer[eqId].power.value ?? ost.sensorsFromServer[eqId].power;
        else if (key === 'power' && ost.sensorsFromServer[eqId] && ost.sensorsFromServer[eqId].consum != null)
            serverVal = ost.sensorsFromServer[eqId].consum.value ?? ost.sensorsFromServer[eqId].consum;
    }
    if (serverVal !== undefined && serverVal !== null) return serverVal;
    const s = state.sensors![eqId];
    if (s && s[key] !== undefined) return s[key];
    if (key === 'consum' && s && s.power !== undefined) return s.power;
    if (key === 'power' && s && s.consum !== undefined) return s.consum;
    return undefined;
}

// ─── SENSOR FRESHNESS (audit fix 2026-05-14) ─────────────────────
// Aligns the UI with collector behavior: a sensor reading is only "LIVE"
// if its `updated_at` timestamp is younger than SENSOR_FRESH_MAX_AGE_SEC.
// Older readings are considered stale (still useful for context) or offline
// (so old the value should be treated as missing). Threshold matches the
// collector-side REPORT_STALE_AFTER_SEC = 300 (5 min).
export const SENSOR_FRESH_MAX_AGE_SEC = 300;   // 5 min → LIVE
export const SENSOR_STALE_MAX_AGE_SEC = 1800;  // 30 min → ÎNTÂRZIAT; older → offline

export type SensorFreshness = 'live' | 'stale' | 'offline';

// Parse a server timestamp (ISO 8601 with optional 'Z'). Returns epoch ms or null.
function _parseSensorUpdatedAt(updatedAt: unknown): number | null {
    if (updatedAt == null) return null;
    const s = String(updatedAt).trim();
    if (!s) return null;
    // Collector emits both '2026-05-13T22:38:24Z' and '2026-05-13T22:38:24' (UTC).
    // Treat the trailing-Z and no-suffix forms as UTC.
    const iso = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z';
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : null;
}

// Returns 'live' (< 5 min), 'stale' (5–30 min) or 'offline' (> 30 min / missing).
export function getSensorFreshness(updatedAt: unknown, nowMs?: number): SensorFreshness {
    const ts = _parseSensorUpdatedAt(updatedAt);
    if (ts == null) return 'offline';
    const now = (typeof nowMs === 'number') ? nowMs : Date.now();
    const ageSec = (now - ts) / 1000;
    if (!Number.isFinite(ageSec) || ageSec < 0) {
        // Clock skew between server and client → assume LIVE (be permissive).
        return 'live';
    }
    if (ageSec <= SENSOR_FRESH_MAX_AGE_SEC) return 'live';
    if (ageSec <= SENSOR_STALE_MAX_AGE_SEC) return 'stale';
    return 'offline';
}

// Returns the `updated_at` string for a sensor reading (if present).
// Falls back to the equivalent `power`↔`consum` key, the same as getSensorValue.
export function getSensorUpdatedAt(eqId: string, key: string): string | null {
    const sf = ost.sensorsFromServer[eqId];
    if (!sf) return null;
    let entry = sf[key];
    if ((entry == null || typeof entry !== 'object' || entry.updated_at == null) && key === 'consum' && sf.power != null) {
        entry = sf.power;
    } else if ((entry == null || typeof entry !== 'object' || entry.updated_at == null) && key === 'power' && sf.consum != null) {
        entry = sf.consum;
    }
    if (entry && typeof entry === 'object' && entry.updated_at) return String(entry.updated_at);
    return null;
}

// Returns true if this sensor key has a FRESH value coming from telemetry
// (PAC3220 power meter, EM63 IMM, SFM12LD, etc.).
// Audit fix 2026-05-14: previously returned true for every server value, even
// readings days old (M5 machine_state 5 days stale was treated as LIVE).
// Now we only return true when the `updated_at` is younger than 5 min.
// Readings without `updated_at` (legacy shape) are still treated as LIVE
// to avoid regressions for shared-sensor sources missing timestamps.
export function isSensorRealtime(eqId: string, key: string): boolean {
    const sf = ost.sensorsFromServer[eqId];
    if (!sf) return false;
    const candidate = sf[key] != null ? key
        : (key === 'consum' && sf.power != null) ? 'power'
        : (key === 'power' && sf.consum != null) ? 'consum'
        : null;
    if (!candidate) return false;
    const entry = sf[candidate];
    // Legacy shape (raw scalar, no `updated_at`) → keep prior behavior.
    if (!entry || typeof entry !== 'object') return true;
    if (entry.updated_at == null) return true;
    return getSensorFreshness(entry.updated_at) === 'live';
}

// AUDIT 2026-05-09 — Eliminare TOTALĂ a simulărilor client-side.
// Înainte: sensorRnd + generateSensors generau valori RANDOM care apăreau ca
// "demo" în UI. Tehnicianii din fabrică au început să lucreze live cu sistemul,
// deci nu mai sunt acceptabile valori inventate.
//
// Sursa de adevăr unica: ost.sensorsFromServer (popultă din /api/telemetry/sensors/latest)
// care vine de la colectoarele REALE (PAC3220 Modbus, EM63 IMM, SFM12LD).
//
// Pentru chei configurate dar fără date reale, getSensorRaw() întoarce undefined,
// iar UI afișează "—" + badge "neconectat" (vezi line-detail.ts _renderSensorsTab).
export function sensorRnd(_base: number, _variancePct: number, _spike: boolean): number {
    return 0;
}

export function generateSensors(_eqId: string): Record<string, number> {
    return {};
}

export function getSensorSeverity(val: number, warn: number, crit: number): 'normal' | 'warning' | 'critical' {
    if (warn < crit) { if (val >= crit) return 'critical'; if (val >= warn) return 'warning'; }
    else { if (val <= crit) return 'critical'; if (val <= warn) return 'warning'; }
    return 'normal';
}

export function getEquipSeverity(eqId: string): 'normal' | 'warning' | 'critical' {
    const cfgs = state.sensorConfigs![eqId] as SensorCfg[] | undefined;
    if (!cfgs) return 'normal';
    let worst: 'normal' | 'warning' | 'critical' = 'normal';
    cfgs.forEach((cfg: SensorCfg) => {
        if (!cfg.enabled) return;
        const val = getSensorValue(eqId, cfg.sensor_key);
        if (val === undefined) return;
        const sev = getSensorSeverity(val, cfg.warn_threshold, cfg.crit_threshold);
        if (sev === 'critical') worst = 'critical';
        else if (sev === 'warning' && worst !== 'critical') worst = 'warning';
    });
    return worst;
}

/* ═══ MOLD CYCLE COUNTER ══════════════════════════════════════
 * Contoarele (cicluri, ore) vor fi alimentate de la mașini după legarea
 * aplicației cu colectarea datelor (OPC UA, PLC etc.) via POST /api/telemetry/counters.
 * Până atunci: valorile se citesc din DB și se pot simula local.
 */
state.moldCounters = JSON.parse(localStorage.getItem('moldCounters') || '{}');
state.moldLinks = {};

export async function loadMoldCounters(): Promise<void> {
    const allMachines: RandMachines[] = await api('/api/machines/all');
    allMachines.forEach((m: RandMachines) => {
        if (m.machine_type === 'mold' && m.linked_machine_id) {
            state.moldLinks![m.id] = m.linked_machine_id;
            const fromServer = {
                cycles: parseInt(String(m.cycle_count)) || 0,
                hours: parseFloat(String(m.operating_hours)) || 0,
            };
            state.moldCounters![m.id] = fromServer;
        }
    });
    try { localStorage.setItem('moldCounters', JSON.stringify(state.moldCounters)); } catch(e) {}
}

export async function refreshMoldCountersFromServer(): Promise<void> {
    if (!Object.keys(state.moldLinks || {}).length) return;
    const ids = Object.keys(state.moldLinks || {});
    const results: (ContoareUtilaj | null)[] = await Promise.all(
        ids.map(id => fetch(`/api/config/machines/${encodeURIComponent(id)}/counters`).then(r => r.ok ? r.json() : null).catch(() => null))
    );
    ids.forEach((id, i) => {
        const d = results[i];
        if (d && (d.cycle_count !== undefined || d.operating_hours !== undefined)) {
            state.moldCounters![id] = {
                cycles: parseInt(d.cycle_count) || 0,
                hours: parseFloat(d.operating_hours) || 0,
            };
            if (state.sensors![id]) {
                state.sensors![id].cycles = state.moldCounters![id].cycles;
                state.sensors![id].hours = state.moldCounters![id].hours;
            }
        }
    });
    try { localStorage.setItem('moldCounters', JSON.stringify(state.moldCounters)); } catch(e) {}
    updateSensorDisplays();
}

// AUDIT 2026-05-09 — Eliminare increment RANDOM + PUT pe server.
// Înainte: la fiecare 3 secunde se adăugau 3-7 cicluri RANDOM la fiecare matriță
// activă și se PUT-uia rezultatul în DB → corupea machines.cycle_count REAL.
// DB curățat cu UPDATE machines SET cycle_count='0' WHERE machine_type='mold'.
//
// Acum: contoarele matriței trebuie alimentate ori de la PLC mașinii (via colector
// EM63 cu @ActCntCycMachTotal), ori prin endpoint POST /api/telemetry/counters
// dintr-un colector dedicat. Până atunci, valorile sunt 0 (placeholder).
export function updateMoldCounters(): void {
    // No-op intenționat — vezi comentariu mai sus.
    return;
}

export let _moldSaveTimer: number = 0;
export function saveMoldCountersToServer(): void {
    // No-op intenționat — nu mai scriem valori simulate în DB.
    return;
}

export function updateSensors(): void {
    // AUDIT 2026-05-09 — eliminat:
    //   1) generateSensors() (genera valori RANDOM per cheie configurată)
    //   2) Math.random() < 0.08 alarme false categorie SENZOR
    //   3) updateMoldCounters() cu increment random + PUT pe server
    //
    // Acum populăm state.sensors DOAR cu valori reale primite de la server
    // (ost.sensorsFromServer). Pentru chei fără date reale, valoarea rămâne
    // undefined și UI afișează placeholder "neconectat".
    (state.allEquipmentIds || []).forEach((eqId: string) => {
        if (state.moldLinks![eqId]) return;
        const sf = ost.sensorsFromServer[eqId] || {};
        const realKeys: Record<string, number> = {};
        const cfgs = (state.sensorConfigs![eqId] || []) as SensorCfg[];
        if (!state.sensorHistory[eqId]) state.sensorHistory[eqId] = {};
        cfgs.forEach((cfg: SensorCfg) => {
            const k = cfg.sensor_key;
            const fromServer = sf[k];
            const numericVal = fromServer && typeof fromServer === 'object' && 'value' in fromServer
                ? Number(fromServer.value)
                : Number(fromServer);
            if (Number.isFinite(numericVal)) {
                realKeys[k] = numericVal;
                if (!state.sensorHistory[eqId][k]) state.sensorHistory[eqId][k] = [];
                state.sensorHistory[eqId][k].push(numericVal);
                if (state.sensorHistory[eqId][k].length > 40) state.sensorHistory[eqId][k].shift();
            }
        });
        state.sensors![eqId] = realKeys;
    });
    try { localStorage.setItem('sensorHistory', JSON.stringify(state.sensorHistory)); } catch(e) {}
    (state.allEquipmentIds || []).forEach((eqId: string) => {
        if (state.moldLinks![eqId]) return;
        // DEFECT (tipare 11 oct 2026, nereparat): codul e scris pentru numărul simplu (forma veche), dar `power`/`consum`
        // vin ca {value, updated_at} — `v === ''` nu prinde nimic și parseFloat(obiect) pune NaN în consumptionHistory
        // (pe care nu-l citește nimeni). Tipul de mai jos ține și forma veche, ca să se vadă ce presupune codul.
        let v: CitireSenzor | string | number | null = null;
        if (ost.sensorsFromServer && ost.sensorsFromServer[eqId]) {
            const s: Record<string, CitireSenzor | string | number> = ost.sensorsFromServer[eqId];
            v = s.power ?? s.consum;
        }
        if (v == null || v === '') return;
        state.consumptionHistory[eqId] = state.consumptionHistory[eqId] || [];
        state.consumptionHistory[eqId].push(typeof v === 'number' ? v : parseFloat(v as string));
        if (state.consumptionHistory[eqId].length > 60) state.consumptionHistory[eqId].shift();
    });
    updateSensorDisplays();
    refreshSensorAlarmsFromReadings();
}

export function updateSensorDisplays(): void {
    $$('.sensor-val-live').forEach((el: Element) => {
        const hel = el as HTMLElement;
        const eqId = hel.dataset.eq;
        const key = hel.dataset.sensor;
        if (!eqId || !key) return;
        const val = getSensorValue(eqId, key);
        if (val === undefined) return;
        if (key === 'cycles') {
            hel.textContent = Number(val).toLocaleString('ro-RO');
        } else if (key === 'hours') {
            hel.textContent = Number(val).toFixed(1);
        } else {
            hel.textContent = String(val);
        }
        const cfgs = (state.sensorConfigs![eqId!] || []) as SensorCfg[];
        const cfg = cfgs.find((c: SensorCfg) => c.sensor_key === key);
        if (cfg) {
            const cls = hel.classList.contains('mold-ctr-val') ? 'mold-ctr-val sensor-val-live' : 'sensor-val-live';
            hel.className = `${cls} ${getSensorSeverity(val, cfg.warn_threshold, cfg.crit_threshold)}`;
        }
    });

    $$('.eq-consum-val').forEach((el: Element) => {
        const hel = el as HTMLElement;
        const eqId = hel.dataset.eq;
        if (!eqId) return;
        const avg = getHourlyAvgPower(eqId);
        if (avg != null) {
            // Drift fix 2026-05-11: kW/h era dimensional GREȘIT. Putere medie pe 1h = "kW (medie 1h)".
            hel.textContent = parseFloat(String(avg)).toFixed(2) + ' kW (medie 1h)';
        } else {
            const vPower = getSensorValue(eqId, 'power') ?? getSensorValue(eqId, 'consum');
            if (vPower != null && !isNaN(parseFloat(String(vPower)))) {
                hel.textContent = parseFloat(String(vPower)).toFixed(2) + ' kW';
            } else {
                // Drift 2 fix: păstrăm fallback-ul `manual_power_kw` din build (data-manual-kw)
                // ca să nu apară "— kW" când avem valoare de plăcuță cunoscută.
                const mkw = parseFloat(hel.dataset.manualKw || '0');
                if (mkw > 0) hel.textContent = mkw.toFixed(2) + ' kW (manual)';
                else hel.textContent = '— kW';
            }
        }
    });

    $$('.equip-card[data-eq]').forEach((card: Element) => {
        const eqId = (card as HTMLElement).dataset.eq;
        if (!eqId) return;
        const sev = getEquipSeverity(eqId);
        (card as HTMLElement).classList.remove('sensor-critical', 'sensor-warning');
        if (state.machineStatus[eqId] === 'stopped' || state.machineStatus[eqId] === 'warning') return;
        if (sev === 'critical') (card as HTMLElement).classList.add('sensor-critical');
        else if (sev === 'warning') (card as HTMLElement).classList.add('sensor-warning');
    });

    $$('.line-power-val').forEach((el: Element) => {
        const hel = el as HTMLElement;
        const lineId = hel.dataset.lineId || '';
        // Bug fix 2026-05-13: use backend-attributed per-line power so
        // shared chillers are not credited to OFF lines. Local-sum fallback
        // is kept only for the first ~1s after page load (until the first
        // hourly_avg fetch lands), so the UI never flashes "— kW".
        if (lineId) {
            const attr = getLineAttributedPower(lineId);
            if (attr.power_kw != null) {
                hel.textContent = attr.status === 'stopped'
                    ? (attr.power_kw > 0.01
                        ? attr.power_kw.toFixed(2) + ' kW (linie OPRITĂ — utilaje proprii)'
                        : '0.00 kW (linie OPRITĂ)')
                    : attr.power_kw.toFixed(2) + ' kW (medie 1h)';
                hel.classList.toggle('line-power-off', attr.status === 'stopped');
                if (attr.status === 'stopped') {
                    hel.title = 'Linie inactivă — utilajele partajate sunt atribuite altor linii';
                } else {
                    hel.title = 'Consum energie mediu/oră (cu distribuție utilaje partajate)';
                }
                return;
            }
        }
        const eqIds = (hel.dataset.lineEq || '').split(',').filter(Boolean);
        let eqData: Array<{ id: string; mp: number }> = [];
        try { eqData = JSON.parse(hel.dataset.lineEqJson || '[]'); } catch (e: unknown) {}
        let sum = 0, has = false;
        eqIds.forEach((id: string) => {
            const a = getHourlyAvgPower(id);
            const p = a ?? getSensorValue(id, 'power') ?? getSensorValue(id, 'consum');
            const eqInfo = eqData.find((e: { id: string; mp: number }) => e.id === id);
            const mp = eqInfo ? eqInfo.mp : 0;
            if (p != null && !isNaN(Number(p))) { sum += parseFloat(String(p)); has = true; }
            else if (mp > 0) { sum += parseFloat(String(mp)); has = true; }
        });
        hel.textContent = has ? sum.toFixed(2) + ' kW (medie 1h)' : '— kW';
    });

    const combinedAlerts = [...(state.controllerAlarms || []), ...state.allAlerts, ...(state.sensorAlarms || [])];
    const unacked = combinedAlerts.filter(a => {
        if (a.source === 'controller') return !a.acked;
        return !state.ackedAlarms.includes(a.id);
    });
    updateAlarmBadge(unacked.length);

    const w = window as FereastraOverview;
    if (w._detailData && typeof detailTabActive !== 'undefined' && detailTabActive === 'consum') {
        _updateEnergyLiveValues();
    }

    _refreshDetailHeader();
}

export function _refreshDetailHeader(): void {
    const w = window as FereastraOverview;
    if (!w._detailData || !w._detailLineId) return;
    const lineId = w._detailLineId as string;
    const cfgs = (state.sensorConfigs![lineId] || []) as SensorCfg[];
    if (!cfgs.length) return;
    const m = w._detailData.machine as UtilajDetaliu;
    if (!m) return;
    const isMold = m.machine_type === 'mold';

    if (typeof detailTabActive !== 'undefined' && detailTabActive === 'sensors') {
        _updateSensorsTabLive(lineId, cfgs, isMold);
    }

    const consumValEl = document.querySelector('#detail-machine-info .detail-consum-val') as HTMLElement | null;
    if (consumValEl) {
        // Bug fix 2026-05-13: prefer backend-attributed line power for
        // consistency with Dashboard / Overview Fabrică.
        const attrPwrLive = getLineAttributedPower(lineId);
        const attrPwrServer = m.power_kw_attributed;
        const attrPwr = (attrPwrLive.power_kw != null) ? attrPwrLive.power_kw : (typeof attrPwrServer === 'number' ? attrPwrServer : null);
        const attrStatus = attrPwrLive.status || m.power_status || null;
        const avgPwr = getHourlyAvgPower(lineId);
        const pv = getSensorValue(lineId, 'power') ?? getSensorValue(lineId, 'consum');
        if (attrPwr != null) {
            consumValEl.textContent = attrStatus === 'stopped'
                ? (attrPwr > 0.01
                    ? attrPwr.toFixed(2) + ' kW (linie OPRITĂ — utilaje proprii)'
                    : '0.00 kW (linie OPRITĂ)')
                : attrPwr.toFixed(2) + ' kW (medie 1h)';
        } else if (avgPwr != null) {
            consumValEl.textContent = parseFloat(String(avgPwr)).toFixed(2) + ' kW (medie 1h)';
        } else if (pv != null) {
            consumValEl.textContent = parseFloat(String(pv)).toFixed(2) + ' kW';
        } else if ((m.manual_power_kw || 0) > 0) {
            consumValEl.textContent = parseFloat(String(m.manual_power_kw)).toFixed(2) + ' kW (manual)';
        } else {
            consumValEl.textContent = '— kW';
        }
    }

    let staleEl = document.getElementById('detail-stale-banner');
    const serverData = ost.sensorsFromServer[lineId];
    if (serverData) {
        let latestTs = 0;
        Object.values(serverData).forEach((s: CitireSenzor) => {
            if (s && s.updated_at) {
                const t = new Date(s.updated_at).getTime();
                if (t > latestTs) latestTs = t;
            }
        });
        const ageSec = latestTs ? Math.round((Date.now() - latestTs) / 1000) : 0;
        if (ageSec > 30) {
            const ageStr = ageSec < 60 ? `${ageSec}s` : ageSec < 3600 ? `${Math.floor(ageSec / 60)}m ${ageSec % 60}s` : `${Math.floor(ageSec / 3600)}h ${Math.floor((ageSec % 3600) / 60)}m`;
            if (!staleEl) {
                staleEl = document.createElement('div');
                staleEl.id = 'detail-stale-banner';
                staleEl.className = 'detail-stale-banner';
                const panel = document.querySelector('#detail-machine-info .detail-consum-banner');
                if (panel) panel.parentNode!.insertBefore(staleEl, panel.nextSibling);
            }
            if (staleEl) staleEl.innerHTML = `<span class="stale-icon">⚠</span> Date vechi de <strong>${ageStr}</strong> — colectorul nu trimite citiri noi`;
        } else if (staleEl) {
            staleEl.remove();
        }
    }
}

setInterval(updateSensors, 3000);
setInterval(saveMoldCountersToServer, 60000);
setInterval(refreshMoldCountersFromServer, 15000);
setInterval(refreshSensorReadingsFromServer, 5000);
setInterval(refreshHourlyAvg, 30000);
setTimeout(updateSensors, 800);
setTimeout(refreshSensorReadingsFromServer, 2000);
setTimeout(refreshHourlyAvg, 3000);

/* ═══ OVERVIEW ═════════════════════════════════════════════════ */
export const TYPE_LABELS: Record<string, string> = {
    'injection': 'INJECȚIE', 'compression': 'COMPRESIE', 'chiller': 'CHILLER',
    'cooler': 'RĂCITOR', 'tampoprint': 'TAMPO', 'auxiliary': 'AUXILIAR',
    'inspection': 'INSPECȚIE', 'control': 'CONTROL', 'compressor': 'COMPRESOR',
    'dryer': 'USCĂTOR', 'reservoir': 'REZERVOR', 'mold': 'MATRIȚĂ',
};

export function equipmentTypeLabel(m: Machine): string {
    return TYPE_LABELS[m.machine_type] || m.machine_type;
}

export function buildGaugeSVG(pct: number, label: string, color: string): string {
    pct = Math.max(0, Math.min(100, pct));
    const r = 22, cx = 28, cy = 28, sw = 4;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    const bgColor = 'rgba(255,255,255,.06)';
    return `<svg width="56" height="56" viewBox="0 0 56 56">
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${bgColor}" stroke-width="${sw}"/>
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${color}" stroke-width="${sw}"
            stroke-dasharray="${circ}" stroke-dashoffset="${offset}"
            stroke-linecap="round" transform="rotate(-90 ${cx} ${cy})"
            style="transition:stroke-dashoffset .6s ease"/>
        <text x="${cx}" y="${cy - 2}" text-anchor="middle" fill="${color}" font-family="var(--mono)" font-size="11" font-weight="700">${pct}%</text>
        <text x="${cx}" y="${cy + 9}" text-anchor="middle" fill="#6b7590" font-size="6.5" font-weight="500">${label}</text>
    </svg>`;
}

/**
 * Modal universal pentru editare counter (cycles / hours / nominal_cycle_time pentru SACMI).
 * Folosit din pictograma ✏ pe cardul matriței / auxiliarului.
 */
export async function _openEquipmentEditModal(eqId: string, editType: string): Promise<void> {
    let eq: UtilajEditat | undefined = (_allMachinesCache || []).find((m: Machine) => m.id === eqId);
    if (!eq) {
        // Fallback: încărcăm de la server
        try {
            const all: RandMachines[] = await api('/api/machines/all');
            eq = (all || []).find((m: RandMachines) => m.id === eqId);
        } catch (e) { console.error(e); }
    }
    if (!eq) { toast(`Echipament ${eqId} negăsit`); return; }

    let modalHtml = '';
    let onSubmit: (() => Promise<void>) | null = null;

    if (editType === 'hours') {
        const current = parseFloat(eq.operating_hours || '0') || 0;
        modalHtml = `<div class="modal-overlay" id="equip-edit-modal">
            <div class="modal" style="max-width:480px">
                <h3>⏱ Editare ore funcționare</h3>
                <p style="color:var(--text-muted);font-size:.85em;margin-bottom:1em">
                    <strong>${escHtml(eq.name || eq.id)}</strong><br>
                    Citește valoarea curentă de pe HMI / contor fizic și introdu-o aici.
                    De aici încolo, sistemul va incrementa automat orele când echipamentul rulează.
                </p>
                <div class="form-row">
                    <label>Ore curente:</label>
                    <input type="number" id="equip-edit-hours" min="0" step="0.1" value="${current.toFixed(1)}" autofocus>
                </div>
                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" id="equip-edit-cancel">Anulează</button>
                    <button type="button" class="btn btn-primary" id="equip-edit-save">✓ Salvează</button>
                </div>
            </div>
        </div>`;
        onSubmit = async () => {
            const inp = document.getElementById('equip-edit-hours') as HTMLInputElement;
            const val = parseFloat(inp.value);
            if (isNaN(val) || val < 0) { alert('Valoare invalidă'); return; }
            const r = await fetch(`/api/machines/${encodeURIComponent(eqId)}/operating_hours`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operating_hours: val, reset_tracker: true })
            });
            const data: RaspunsScriere = await r.json();
            if (!r.ok) { alert('Eroare: ' + (data.error || 'necunoscut')); return; }
            toast(`Ore salvate: ${val.toFixed(1)} h`);
            loadOverview();
        };
    } else if (editType === 'cycles') {
        const current = parseInt(eq.cycle_count || '0') || 0;
        modalHtml = `<div class="modal-overlay" id="equip-edit-modal">
            <div class="modal" style="max-width:480px">
                <h3>🔄 Editare contor cicluri matriță</h3>
                <p style="color:var(--text-muted);font-size:.85em;margin-bottom:1em">
                    <strong>${escHtml(eq.name || eq.id)}</strong><br>
                    Setează contorul TOTAL de cicluri al matriței (citit pe ștanță sau din log istoric).
                    Sistemul va continua să incrementeze automat cicluri delta din mașina principală.
                </p>
                <div class="form-row">
                    <label>Cicluri totale:</label>
                    <input type="number" id="equip-edit-cycles" min="0" step="1" value="${current}" autofocus>
                </div>
                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" id="equip-edit-cancel">Anulează</button>
                    <button type="button" class="btn btn-primary" id="equip-edit-save">✓ Salvează</button>
                </div>
            </div>
        </div>`;
        onSubmit = async () => {
            const inp = document.getElementById('equip-edit-cycles') as HTMLInputElement;
            const val = parseInt(inp.value);
            if (isNaN(val) || val < 0) { alert('Valoare invalidă'); return; }
            const r = await fetch(`/api/machines/${encodeURIComponent(eqId)}/cycle_count_offset`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cycle_count_offset: val, set_total: true })
            });
            const data: RaspunsScriere = await r.json();
            if (!r.ok) { alert('Eroare: ' + (data.error || 'necunoscut')); return; }
            toast(`Cicluri matriță salvate: ${val.toLocaleString('ro-RO')}`);
            loadOverview();
        };
    } else if (editType === 'nominal') {
        const ct = eq.nominal_cycle_time_sec || '';
        const cph = eq.nominal_cph || '';
        const cav = eq.cavities || '';
        modalHtml = `<div class="modal-overlay" id="equip-edit-modal">
            <div class="modal" style="max-width:520px">
                <h3>⏲ Parametri nominali matriță SACMI</h3>
                <p style="color:var(--text-muted);font-size:.85em;margin-bottom:1em">
                    <strong>${escHtml(eq.name || eq.id)}</strong><br>
                    Sistemul calculează cicluri matriță = timp mașină / timp ciclu.
                    Introdu timpul nominal SAU capace per oră — celălalt se derivă automat.
                </p>
                <div class="form-row">
                    <label>Timp ciclu (sec):</label>
                    <input type="number" id="equip-edit-ct" min="0.1" step="0.1" max="600" value="${ct}" placeholder="ex: 2.0">
                </div>
                <div class="form-row">
                    <label>SAU Capace per oră:</label>
                    <input type="number" id="equip-edit-cph" min="1" step="1" max="100000" value="${cph}" placeholder="ex: 1920">
                </div>
                <div class="form-row">
                    <label>Cuiburi (cavities):</label>
                    <input type="number" id="equip-edit-cav" min="1" step="1" max="1000" value="${cav}" placeholder="ex: 32">
                </div>
                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" id="equip-edit-cancel">Anulează</button>
                    <button type="button" class="btn btn-primary" id="equip-edit-save">✓ Salvează</button>
                </div>
            </div>
        </div>`;
        onSubmit = async () => {
            const ctInp = (document.getElementById('equip-edit-ct') as HTMLInputElement).value;
            const cphInp = (document.getElementById('equip-edit-cph') as HTMLInputElement).value;
            const cavInp = (document.getElementById('equip-edit-cav') as HTMLInputElement).value;
            const body: Partial<Pick<RandMachines, 'nominal_cycle_time_sec' | 'nominal_cph' | 'cavities'>> = {};
            body.nominal_cycle_time_sec = ctInp ? parseFloat(ctInp) : null;
            body.nominal_cph = cphInp ? parseInt(cphInp) : null;
            body.cavities = cavInp ? parseInt(cavInp) : null;
            const r = await fetch(`/api/machines/${encodeURIComponent(eqId)}/nominal_params`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data: RaspunsScriere = await r.json();
            if (!r.ok) { alert('Eroare: ' + (data.error || 'necunoscut')); return; }
            toast('Parametri nominali salvați');
            loadOverview();
        };
    } else {
        return;
    }

    const div = document.createElement('div');
    div.innerHTML = modalHtml;
    document.body.appendChild(div.firstElementChild!);

    const close = () => document.getElementById('equip-edit-modal')?.remove();
    document.getElementById('equip-edit-cancel')?.addEventListener('click', close);
    document.getElementById('equip-edit-modal')?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).id === 'equip-edit-modal') close();
    });
    document.getElementById('equip-edit-save')?.addEventListener('click', async () => {
        try {
            await onSubmit!();
            close();
        } catch (err) { alert('Eroare: ' + err); }
    });
}

/** Alarme senzor din citiri reale (prag warn/crit depășit) — înlocuiește generarea random eliminată în audit 2026-05-09. */
export function refreshSensorAlarmsFromReadings(): void {
    const alarms: Alert[] = [];
    (state.allEquipmentIds || []).forEach((eqId: string) => {
        if (state.moldLinks?.[eqId]) return;
        if (state.machineStatus[eqId] === 'stopped') return;
        const cfgs = (state.sensorConfigs![eqId] || []) as SensorCfg[];
        cfgs.forEach((cfg: SensorCfg) => {
            if (cfg.enabled === false) return;
            const val = getSensorValue(eqId, cfg.sensor_key);
            if (val === undefined) return;
            const sev = getSensorSeverity(val, cfg.warn_threshold, cfg.crit_threshold);
            if (sev === 'normal') return;
            const sfEntry = ost.sensorsFromServer[eqId]?.[cfg.sensor_key];
            const updatedAt = sfEntry && typeof sfEntry === 'object' && sfEntry.updated_at
                ? String(sfEntry.updated_at)
                : undefined;
            alarms.push({
                id: `sensor:${eqId}:${cfg.sensor_key}`,
                machine_id: eqId,
                action: `${cfg.label}: ${val}${cfg.unit || ''} (prag ${sev === 'critical' ? 'critic' : 'atenție'} ${sev === 'critical' ? cfg.crit_threshold : cfg.warn_threshold}${cfg.unit || ''})`,
                severity: sev === 'critical' ? 'critical' : 'warning',
                category: 'SENZOR',
                sensor_key: cfg.sensor_key,
                sensor_label: cfg.label,
                sensor_value: val,
                sensor_unit: cfg.unit,
                warn_threshold: cfg.warn_threshold,
                crit_threshold: cfg.crit_threshold,
                updated_at: updatedAt,
            });
        });
    });
    alarms.sort((a, b) => {
        if (a.severity === 'critical' && b.severity !== 'critical') return -1;
        if (b.severity === 'critical' && a.severity !== 'critical') return 1;
        return 0;
    });
    state.sensorAlarms = alarms;
}

function _alertDetailRow(label: string, value: string): string {
    return `<div class="alert-detail-row"><span class="alert-detail-label">${escHtml(label)}</span><span class="alert-detail-value">${value}</span></div>`;
}

function _closeAlertDetailModal(): void {
    document.getElementById('modal-alert-detail')?.remove();
}

function _findMaintenanceAlert(alertId: string): Alert | null {
    return (state.allAlerts || []).find(a => a.id === alertId) || null;
}

function _findLiveAlarm(alertId: string): Alert | null {
    const combined = [...(state.controllerAlarms || []), ...(state.sensorAlarms || [])];
    return combined.find(a => a.id === alertId) || null;
}

function _navigateToMachineTab(machineId: string, machineName: string, tab: string): void {
    _closeAlertDetailModal();
    (window as FereastraOverview).detailTabActive = tab;
    const role = state.currentUser?.role || 'operator';
    if ((window as FereastraOverview).openLineDetail) {
        (window as FereastraOverview).openLineDetail!(machineId, machineName, role);
    } else {
        openLineDetail(machineId, machineName, role);
    }
}

function _navigateToAlarmHistory(machineId?: string): void {
    _closeAlertDetailModal();
    if (machineId) {
        void openAlarmHistoryForMachine(machineId);
        return;
    }
    if ((window as FereastraOverview).showSection) {
        (window as FereastraOverview).showSection!('sec-alarm_history');
    } else {
        document.querySelector('.nav-item[data-section="alarm_history"]')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    }
    if (typeof loadAlarmHistory === 'function') loadAlarmHistory(1);
    else (window as FereastraOverview).loadAlarmHistory?.(1);
}

export function openMaintenanceAlertDetailModal(alertId: string, lineId?: string): void {
    const a = _findMaintenanceAlert(alertId);
    if (!a) { toast('Alerta nu mai este disponibilă — reîncarcă pagina.'); return; }

    const ivMap = (state as OverviewState).maintenanceIntervalsMap as Record<string, IntervalCuStare> | undefined;
    const iv = ivMap?.[alertId] || null;
    const hours = state.machineHours[a.machine_id] || 0;
    const ih = Number(a.interval || iv?.interval_hours) || 0;
    // 1 nov 2026 (auditul surselor de ore): starea din sursa unică, nu socotită aici cu orele din pagină
    const st = iv?.stare;
    const prog = !st && iv ? intervalHoursProgress(iv as IvProgres, hours) : null;
    const remaining = st ? (st.mod === 'ore' ? st.ore_ramase : (st.zile != null ? st.zile * 24 : null))
        : (a.remaining != null ? a.remaining : (prog ? prog.remaining : null));
    const pctUsed = st ? (st.procent != null ? Math.min(100, st.procent) : null)
        : prog && ih > 0 ? Math.min(100, Math.round(prog.ratio * 100)) : null;

    const sevLabel = a.severity === 'critical'
        ? (a.maintenance_suggestion ? 'CRITIC (sugestie — ești oprit)' : 'CRITIC')
        : 'ATENȚIE';
    const partInfo = [iv?.part_number, iv?.lubricant].filter(Boolean).join(' · ') || '—';
    let docLink = '';
    if (iv?.document_id && iv?.page_number) {
        docLink = `<a href="/api/documents/${iv.document_id}/view?page=${iv.page_number}" target="_blank" rel="noopener" class="btn btn-sm btn-outline">📖 Manual p.${iv.page_number}</a>`;
    } else if (iv?.document_id) {
        docLink = `<a href="/api/documents/${iv.document_id}/view" target="_blank" rel="noopener" class="btn btn-sm btn-outline">📖 Manual</a>`;
    }

    let baselineNote = '';
    if (a.last_log_synthetic) {
        baselineNote = _alertDetailRow('Ultim log', '<span style="color:var(--amber)">📌 Baseline GO-LIVE (fără istoric real)</span>');
    } else if (a.last_log_date) {
        baselineNote = _alertDetailRow('Ultim log', escHtml(a.last_log_date));
    }

    const machineName = iv?.machine_name || a.machine_id;
    const old = document.getElementById('modal-alert-detail');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-alert-detail';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content alert-detail-modal">
            <h2>⚠ Mentenanță preventivă</h2>
            <p class="golive-modal-subtitle">${escHtml(machineName)}${lineId ? ` · Linia ${escHtml(lineId)}` : ''}</p>
            <div class="alert-detail-sev ${a.severity}">${escHtml(sevLabel)}</div>
            <div class="alert-detail-grid">
                ${_alertDetailRow('Acțiune', escHtml(a.action || iv?.action_ro || '—'))}
                ${_alertDetailRow('Interval', ih > 0 ? `${ih} h` : '—')}
                ${_alertDetailRow('Ore curente', `${hours.toFixed(1)} h`)}
                ${_alertDetailRow('Stare', st ? badgeStare(st) : (remaining != null ? (remaining <= 0 ? `<strong style="color:var(--red)">DEPĂȘIT (${Math.abs(Math.round(remaining))} h)</strong>` : `${Math.round(remaining)} h`) : '—'))}
                ${pctUsed != null ? _alertDetailRow('Progres interval', `<span class="alert-detail-progress"><span style="width:${pctUsed}%"></span></span> ${pctUsed}%`) : ''}
                ${_alertDetailRow('Categorie', escHtml(a.category || iv?.category || '—'))}
                ${_alertDetailRow('Piesă / lubrifiant', escHtml(partInfo))}
                ${iv?.interval_text ? _alertDetailRow('Descriere interval', escHtml(iv.interval_text)) : ''}
                ${baselineNote}
                ${a.machine_state && a.machine_state !== 'active' ? _alertDetailRow('Stare mașină', escHtml(a.machine_state)) : ''}
            </div>
            <div class="alert-detail-actions">
                <button type="button" class="btn btn-green" id="ad-maint-machine">🔧 Vezi utilaj</button>
                <button type="button" class="btn btn-outline" id="ad-maint-intervals">📋 Intervale mentenanță</button>
                ${docLink}
                <button type="button" class="btn btn-outline" id="ad-maint-close">Închide</button>
            </div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', _closeAlertDetailModal);
    document.getElementById('ad-maint-close')?.addEventListener('click', _closeAlertDetailModal);
    document.getElementById('ad-maint-machine')?.addEventListener('click', () => {
        _navigateToMachineTab(a.machine_id, machineName, 'parts');
    });
    document.getElementById('ad-maint-intervals')?.addEventListener('click', () => {
        _navigateToMachineTab(a.machine_id, machineName, 'intervals');
    });
}

export function openLiveAlarmDetailModal(alertId: string, lineId?: string): void {
    const a = _findLiveAlarm(alertId);
    if (!a) { toast('Alarma nu mai este activă — reîncarcă pagina.'); return; }

    const isController = a.source === 'controller';
    const isSensor = a.category === 'SENZOR';
    const machineName = a.machine_name || a.machine_id;

    if (isController && a.alarm_code) {
        openControllerAlarmDetailModal({
            machine_id: a.machine_id,
            machine_name: machineName,
            line_id: lineId,
            alarm_code: String(a.alarm_code),
            alarm_type: a.alarm_type,
            severity: a.severity,
            action: a.action,
            cause_ro: a.cause_ro,
            solution_ro: a.solution_ro,
            par1: a.par1,
            par2: a.par2,
            created_at: a.created_at,
            db_id: a.db_id,
            title: '🔔 Alarmă live',
            onHistory: () => _navigateToAlarmHistory(a.machine_id),
            onSensors: () => _navigateToMachineTab(a.machine_id, machineName, 'sensors'),
            onMachine: () => _navigateToMachineTab(a.machine_id, machineName, 'parts'),
        });
        return;
    }

    const sourceLabel = isController ? 'Controler HMI/PLC' : (isSensor ? 'Senzor telemetrie' : 'Live');
    const sevLabel = a.severity === 'critical' ? 'CRITIC' : (a.severity === 'info' ? 'INFO' : 'ATENȚIE');

    let detailRows = '';
    detailRows += _alertDetailRow('Sursă', escHtml(sourceLabel));
    detailRows += _alertDetailRow('Utilaj', escHtml(machineName) + (lineId ? ` <span style="color:var(--text-muted)">(Linia ${escHtml(lineId)})</span>` : ''));
    detailRows += _alertDetailRow('Severitate', `<span class="alert-detail-sev inline ${a.severity}">${escHtml(sevLabel)}</span>`);
    detailRows += _alertDetailRow('Mesaj', escHtml(a.action || '—'));

    if (isSensor) {
        const key = a.sensor_key || '';
        const label = a.sensor_label || key || '—';
        const val = a.sensor_value ?? getSensorValue(a.machine_id, key);
        const unit = a.sensor_unit || '';
        detailRows += _alertDetailRow('Senzor', escHtml(label) + (key ? ` <code style="font-size:.75rem;color:var(--text-muted)">(${escHtml(key)})</code>` : ''));
        if (val !== undefined) detailRows += _alertDetailRow('Valoare curentă', `<strong>${escHtml(String(val))}${escHtml(unit)}</strong>`);
        if (a.warn_threshold != null) detailRows += _alertDetailRow('Prag atenție', `${a.warn_threshold}${unit}`);
        if (a.crit_threshold != null) detailRows += _alertDetailRow('Prag critic', `${a.crit_threshold}${unit}`);
        const ts = a.updated_at || getSensorUpdatedAt(a.machine_id, key);
        if (ts) detailRows += _alertDetailRow('Ultima citire', escHtml(formatDateTimeRO(ts) + ' (' + _timeAgo(ts) + ')'));
        const fresh = getSensorFreshness(ts);
        const freshLabel = fresh === 'live' ? '🟢 Live' : (fresh === 'stale' ? '🟡 Întârziat' : '⚫ Offline');
        detailRows += _alertDetailRow('Stare senzor', freshLabel);
    }

    const old = document.getElementById('modal-alert-detail');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-alert-detail';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content alert-detail-modal">
            <h2>🔔 Alarmă live</h2>
            <p class="golive-modal-subtitle">${escHtml(sourceLabel)} · ${escHtml(machineName)}</p>
            <div class="alert-detail-grid">${detailRows}</div>
            <div class="alert-detail-actions">
                <button type="button" class="btn btn-outline" id="ad-live-sensors">📡 Senzori utilaj</button>
                <button type="button" class="btn btn-outline" id="ad-live-machine">🔧 Detalii utilaj</button>
                <button type="button" class="btn btn-outline" id="ad-live-close">Închide</button>
            </div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', _closeAlertDetailModal);
    document.getElementById('ad-live-close')?.addEventListener('click', _closeAlertDetailModal);
    document.getElementById('ad-live-sensors')?.addEventListener('click', () => _navigateToMachineTab(a.machine_id, machineName, 'sensors'));
    document.getElementById('ad-live-machine')?.addEventListener('click', () => _navigateToMachineTab(a.machine_id, machineName, 'parts'));
}

/** Live alarms (controller EM63, senzori) pentru utilajele unei linii — NU mentenanță preventivă. */
export function filterLiveAlarmsForLine(pl: { equipment?: Array<{ id: string }> }): Alert[] {
    const eqIds = new Set((pl.equipment || []).map(e => e.id));
    const live: Alert[] = [];
    (state.controllerAlarms || []).forEach(a => {
        if (eqIds.has(a.machine_id) && !a.acked) live.push(a as unknown as Alert);   // alarmă de controler, afișată ca alertă
    });
    (state.sensorAlarms || []).forEach(a => {
        if (eqIds.has(a.machine_id) && !state.ackedAlarms.includes(a.id)) live.push(a);
    });
    live.sort((a, b) => {
        if (a.severity === 'critical' && b.severity !== 'critical') return -1;
        if (b.severity === 'critical' && a.severity !== 'critical') return 1;
        return 0;
    });
    return live;
}

function _renderMaintenanceAlertRow(a: Alert, pl: { id?: string; equipment?: Array<{ id: string; name?: string | null }> }): string {
    const eq = (pl.equipment || []).find(e => e.id === a.machine_id);
    const machineLabel = eq ? `${pl.id} · ${escHtml(eq.name || eq.id)}` : escHtml(a.machine_id || '—');
    const baselineNote = a.last_log_synthetic
        ? ' <span class="alert-baseline" title="Baseline setat la GO-LIVE — fără istoric de mentenanță reală">📌</span>'
        : (a.last_log_date ? ` <span class="alert-lastlog" title="Ultim log: ${escHtml(a.last_log_date)}">📋</span>` : '');
    return `<div class="line-alert line-alert-clickable ${a.severity}" data-alert-kind="maint" data-alert-id="${escHtml(a.id)}" data-line-id="${escHtml(pl.id || '')}" role="button" tabindex="0" title="Click pentru detalii"><span class="line-alert-machine">${machineLabel}</span> — ⚠ ${escHtml(a.action || 'Mentenanță necesară')}${baselineNote}</div>`;
}

function _renderLiveAlarmRow(a: Alert, pl: { id?: string; equipment?: Array<{ id: string; name?: string | null }> }): string {
    const eq = (pl.equipment || []).find(e => e.id === a.machine_id);
    const machineLabel = eq ? escHtml(eq.name || eq.id) : escHtml(a.machine_id || '—');
    const sourceLabel = a.source === 'controller' ? 'Controler' : (a.category === 'SENZOR' ? 'Senzor' : 'Live');
    const sev = a.severity === 'critical' ? 'critical' : 'warning';
    return `<div class="line-alert line-alert-live line-alert-clickable ${sev}" data-alert-kind="live" data-alert-id="${escHtml(a.id)}" data-line-id="${escHtml(pl.id || '')}" role="button" tabindex="0" title="Click pentru detalii alarmă"><span class="line-alert-source">${escHtml(sourceLabel)}</span> <span class="line-alert-machine">${machineLabel}</span> — 🔔 ${escHtml(truncate(a.action || 'Alarmă activă', 120))}</div>`;
}

/** Overview Fabrică: mentenanță preventivă collapsibilă + alarme live separate. */
export function buildOverviewLineAlertsHtml(
    pl: { id?: string; equipment?: Array<{ id: string; name?: string | null }> },
    lineAlerts: Alert[],
): string {
    const liveAlarms = filterLiveAlarmsForLine(pl);
    if (lineAlerts.length === 0 && liveAlarms.length === 0) return '';

    let html = '<div class="line-alerts-panel">';

    if (lineAlerts.length > 0) {
        const critCount = lineAlerts.filter(a => a.severity === 'critical').length;
        const warnCount = lineAlerts.filter(a => a.severity === 'warning').length;
        const countsLabel = [
            critCount ? `${critCount} critic${critCount !== 1 ? 'e' : ''}` : '',
            warnCount ? `${warnCount} atenție` : '',
        ].filter(Boolean).join(' · ');

        html += '<div class="line-alerts-block line-alerts-maint">';
        html += '<button type="button" class="line-alerts-toggle" aria-expanded="false">';
        html += '<span class="line-alerts-toggle-icon">▶</span>';
        html += `<span class="line-alerts-toggle-label">⚠ Mentenanță preventivă (${lineAlerts.length})</span>`;
        if (countsLabel) html += `<span class="line-alerts-counts">${escHtml(countsLabel)}</span>`;
        html += '</button>';
        html += '<div class="line-alerts-body collapsed">';
        lineAlerts.forEach(a => { html += _renderMaintenanceAlertRow(a, pl); });
        html += '</div></div>';
    }

    if (liveAlarms.length > 0) {
        const liveCrit = liveAlarms.filter(a => a.severity === 'critical').length;
        const liveCounts = liveCrit ? `${liveCrit} critic${liveCrit !== 1 ? 'e' : ''}` : '';
        html += '<div class="line-alerts-block line-alerts-live">';
        html += '<button type="button" class="line-alerts-toggle line-alerts-live-toggle" aria-expanded="false">';
        html += '<span class="line-alerts-toggle-icon">▶</span>';
        html += `<span class="line-alerts-toggle-label">🔔 Alarme live (${liveAlarms.length})</span>`;
        if (liveCounts) html += `<span class="line-alerts-counts">${escHtml(liveCounts)}</span>`;
        html += '</button>';
        html += '<div class="line-alerts-body line-alerts-live-body collapsed">';
        liveAlarms.forEach(a => { html += _renderLiveAlarmRow(a, pl); });
        html += '</div></div>';
    }

    html += '</div>';
    return html;
}

export function buildEquipCard(eq: UtilajStructura, isMain: boolean): string {
    const eqId = eq.id;
    const typeLabel = TYPE_LABELS[eq.machine_type as string] || eq.machine_type;   // coloana permite null (azi n-are niciunul)
    const shared = eq.shared ? ' <span class="badge badge-yellow">SHARED</span>' : '';
    const serves = (eq.serves && eq.serves.length > 1) ? `<span class="eq-serves">→ ${eq.serves.join(', ')}</span>` : '';
    let badgesHtml = '';
    if (eq.parts_count > 0) badgesHtml += `<span class="badge badge-blue">${eq.parts_count}</span>`;
    if (eq.intervals_count > 0) badgesHtml += `<span class="badge badge-green">${eq.intervals_count}</span>`;
    if (eq.errors_count > 0) badgesHtml += `<span class="badge badge-red">${eq.errors_count}</span>`;

    const eqStatus = eq.status || state.machineStatus[eqId] || 'ok';
    const statusCls = eqStatus === 'stopped' ? 'eq-stopped' : eqStatus === 'warning' ? 'eq-warning' : '';
    const statusTag = eqStatus !== 'ok' ? `<span class="eq-status-tag ${eqStatus}">${eqStatus === 'stopped' ? 'OPRIT' : 'ATENȚIE'}${eq.status_since ? ' · ' + eq.status_since : ''}</span>` : '';

    if (eqStatus !== 'ok') state.machineStatus[eqId] = eqStatus as MachineStatus;   // în bază e text liber (PUT …/status)

    // Part 2 (2026-05-15) → 2026-05-24 — Per-machine state row + action buttons.
    // state.opStateMap e populat din /api/machines/operational-state/all.
    // Four-state semantics:
    //   - active            → no extra row
    //   - maintenance       → 🔧 row + "Marchează: e doar avarie" button
    //   - technical_pause   → ⏸ row + "Marchează: oprire pentru mentenanță" button
    //   - inactive          → handled at line header level (GO-LIVE block)
    let _eqTechPauseRow = '';
    try {
        const _opMap: Record<string, StareOperare> = (state as OverviewState).opStateMap || {};
        const _opSt = _opMap[eqId];
        if (isMain && _opSt) {
            const _userRole = state.currentUser?.role || 'operator';
            const _canMark = (_userRole === 'engineer' || _userRole === 'master');
            const _canEditThreshold = (_userRole === 'master');
            const _mState = _opSt.maintenance_state || (_opSt.is_technical_pause ? 'technical_pause' : 'active');
            if (_mState === 'maintenance') {
                const _days = Math.floor((_opSt.offline_duration_hours || 0) / 24);
                const _dayStr = _days > 0 ? `${_days} ${_days === 1 ? 'zi' : 'zile'}` : `${Math.round(_opSt.offline_duration_hours)} h`;
                const _since = (_opSt.offline_since || '').replace('T', ' ').slice(0, 16);
                const _wfId = _opSt.workflow_id;
                const _reason = _opSt.flag_reason || '';
                const _wfLabel = _wfId ? `Workflow #${_wfId}` : (_opSt.flagged_by_user ? 'marcată manual' : '');
                const _clearBtn = (_canMark && _opSt.flagged_by_user)
                    ? `<button type="button" class="btn-mini btn-eq-mark-clear" data-machine="${escHtml(eqId)}" title="Marchează: e doar avarie, nu mentenanță planificată">↺ Avarie, nu mentenanță</button>`
                    : '';
                const _thrBtn = _canEditThreshold
                    ? `<button type="button" class="btn-mini btn-eq-threshold" data-machine="${escHtml(eqId)}" data-threshold="${_opSt.technical_pause_threshold_hours || 8}" title="Editează pragul de pauză tehnică pentru această mașină (default 8h)">⚙ Prag: ${_opSt.technical_pause_threshold_hours || 8}h</button>`
                    : '';
                _eqTechPauseRow = `<div class="eq-tech-pause maintenance" title="Mentenanță în curs (alarmele normale ascunse, criticele apar ca sugestii „cât ești oprit").${_reason ? '\nMotiv: ' + _reason : ''}">🔧 Mentenanță: ${escHtml(_dayStr)}${_since ? ' (din ' + escHtml(_since) + ')' : ''}${_wfLabel ? ' · ' + escHtml(_wfLabel) : ''} ${_clearBtn}${_thrBtn}</div>`;
            } else if (_mState === 'technical_pause') {
                const _days = Math.floor((_opSt.offline_duration_hours || 0) / 24);
                const _dayStr = _days > 0 ? `${_days} ${_days === 1 ? 'zi' : 'zile'}` : `${Math.round(_opSt.offline_duration_hours)} h`;
                const _since = (_opSt.offline_since || '').replace('T', ' ').slice(0, 16);
                const _markBtn = _canMark
                    ? `<button type="button" class="btn-mini btn-eq-mark-stop" data-machine="${escHtml(eqId)}" title="Marchează: oprire planificată pentru mentenanță (alarmele se ascund, criticele apar ca sugestii)">🔧 Oprire pentru mentenanță</button>`
                    : '';
                const _thrBtn = _canEditThreshold
                    ? `<button type="button" class="btn-mini btn-eq-threshold" data-machine="${escHtml(eqId)}" data-threshold="${_opSt.technical_pause_threshold_hours || 8}" title="Editează pragul de pauză tehnică pentru această mașină (default 8h)">⚙ Prag: ${_opSt.technical_pause_threshold_hours || 8}h</button>`
                    : '';
                _eqTechPauseRow = `<div class="eq-tech-pause" title="Mentenanțele calendaristice sunt sărite automat. Marchează „Oprire pentru mentenanță" dacă lucrezi efectiv (alarmele critice rămân vizibile).">⏸ Pauză tehnică: ${escHtml(_dayStr)}${_since ? ' (din ' + escHtml(_since) + ')' : ''} ${_markBtn}${_thrBtn}</div>`;
            } else if (_opSt.flagged_by_user) {
                // Audit 2.25 — steagul „oprire pentru mentenanță" de pe M4 a fost
                // pus pe 18 august la 15:15; mașina a repornit la 16:49 și a produs
                // până la 22:18, dar steagul a rămas aprins ȘI nu mai putea fi
                // anulat: butonul de anulare se afișa doar în starea 'maintenance',
                // în care mașina nu mai era. Nimic nu-l stinge automat. Acum, cât
                // timp steagul e pus, butonul există indiferent de stare, iar
                // contradicția e spusă pe față.
                const _dinCand = (_opSt.flag_started_at || '').replace('T', ' ').slice(0, 16);
                const _motiv = _opSt.flag_reason || '';
                const _clearBtn2 = _canMark
                    ? `<button type="button" class="btn-mini btn-eq-mark-clear" data-machine="${escHtml(eqId)}" title="Scoate marcajul de oprire pentru mentenanță">↺ Scoate marcajul</button>`
                    : '';
                _eqTechPauseRow = `<div class="eq-tech-pause" title="Mașina e marcată ca oprită pentru mentenanță, dar nu mai e oprită.${_motiv ? '\nMotiv: ' + _motiv : ''}">⚠ Marcaj de oprire pentru mentenanță rămas aprins${_dinCand ? ' din ' + escHtml(_dinCand) : ''} — mașina nu mai e oprită ${_clearBtn2}</div>`;
            } else if (_mState === 'no_data') {
                // A treia stare (audit 2.13). Până acum cardul avea doar două:
                // „produce" și „oprită". M5 tăcea din 8 mai, cu contorul mort la
                // 21.731 h, și arăta identic cu M4 — care raportează la minut.
                // „Oprită" pe un utilaj măsurat e o informație; pe unul mut e o
                // presupunere. Restanțele lui ieșeau din contorul acela mort.
                const _zile = _opSt.zile_fara_telemetrie != null
                    ? Math.round(Number(_opSt.zile_fara_telemetrie)) : null;
                const _ultima = (_opSt.ultima_telemetrie || '').replace('T', ' ').slice(0, 16);
                _eqTechPauseRow = `<div class="eq-tech-pause no-data" title="Utilajul nu mai trimite nicio citire. Cât tace, nu știm dacă merge sau stă: ceasul mentenanțelor e înghețat la ultima citire, iar scadențele nu se mai calculează din contorul oprit. De verificat legătura sau colectorul.">🔇 Fără date${_zile != null ? ': ' + _zile + (_zile === 1 ? ' zi' : ' zile') : ''}${_ultima ? ' (ultima citire ' + escHtml(_ultima) + ')' : ''}</div>`;
            }
        }
    } catch (_) { /* defensiv */ }

    // Part 3 (2026-05-15) — GO-LIVE state per utilaj
    // Patru statusuri vizuale (sursa: state.goliveMachineMap din /api/golive/audit):
    //   - 'live'              → GO-LIVE: 15 mai 2026 (verde estompat)
    //   - 'ready_to_golive'   → buton "Confirmă GO-LIVE pe acest utilaj" (engineer/master)
    //   - 'needs_hours'       → ⚠ ORE LIPSĂ + buton "Alocă ore" (engineer/master)
    //   - 'line_not_started'  → ⏸ Linie nepornită (info, fără acțiune din card)
    let _eqGoliveRow = '';
    try {
        const _glMap: Record<string, GoliveUtilaj> = (state as OverviewState).goliveMachineMap || {};
        const _glM = _glMap[eqId];
        const _ur = state.currentUser?.role || 'operator';
        const _canEdit = (_ur === 'engineer' || _ur === 'master');
        if (_glM) {
            if (_glM.status === 'live') {
                const _glDate = (_glM.golive_at || '').slice(0, 10);
                _eqGoliveRow = `<div class="eq-golive live" title="Toate intervalele de mentenanță sunt baseline-d la GO-LIVE. ${_glM.intervals_total} intervale active.">🟢 GO-LIVE: ${escHtml(_glDate || '15 mai 2026')}</div>`;
            } else if (_glM.status === 'needs_hours') {
                const _btn = _canEdit ? `<button type="button" class="btn-mini btn-orange btn-allocate-hours" data-eq="${escHtml(eqId)}" title="Setează orele și marchează GO-LIVE">Alocă ore</button>` : '';
                _eqGoliveRow = `<div class="eq-golive needs-hours" title="Mașina nu are ore confirmate. Setează-le pentru a marca GO-LIVE și a începe calculul scadențelor.">⚠ ORE LIPSĂ ${_btn}</div>`;
            } else if (_glM.status === 'ready_to_golive') {
                const _btn = _canEdit ? `<button type="button" class="btn-mini btn-golive-confirm" data-eq="${escHtml(eqId)}" title="Marchează toate intervalele ca 'la zi' la 15 mai 2026">Confirmă GO-LIVE</button>` : '';
                _eqGoliveRow = `<div class="eq-golive ready" title="Mașina are ore (${(_glM.operating_hours || 0).toFixed(1)}h) — gata pentru GO-LIVE">🟡 Gata pentru GO-LIVE ${_btn}</div>`;
            } else if (_glM.status === 'line_not_started') {
                _eqGoliveRow = `<div class="eq-golive line-inactive" title="Linia ${escHtml(_glM.line || '')} nu este pornită. Folosește butonul ▶ Start Linie din header-ul liniei.">⏸ Linie nepornită</div>`;
            }
        }
    } catch (_) { /* defensiv */ }

    const cfgs = (state.sensorConfigs![eqId] || []) as SensorCfg[];
    let sensorHtml = '';
    const isMold = eq.machine_type === 'mold';
    const moldInactive = isMold && !eq.is_active;
    const userRole = (state.currentUser?.role || 'operator');
    const canEditCounters = (userRole === 'engineer' || userRole === 'master');
    const canEditOffset = (userRole === 'master');

    if (isMold) {
        // Date REALE din DB (cycle_count + operating_hours pe machines.<mold_id>)
        const realCycles = parseInt(eq.cycle_count || '0') || 0;
        const realHours = parseFloat(eq.operating_hours || '0') || 0;
        const cycCfg = cfgs.find((c: SensorCfg) => c.sensor_key === 'cycles');
        const hrCfg = cfgs.find((c: SensorCfg) => c.sensor_key === 'hours');
        const cycSev = cycCfg ? getSensorSeverity(realCycles, cycCfg.warn_threshold, cycCfg.crit_threshold) : 'normal';
        const hrSev = hrCfg ? getSensorSeverity(realHours, hrCfg.warn_threshold, hrCfg.crit_threshold) : 'normal';
        const linkedName = eq.linked_machine_id || '?';
        const cavities = eq.cavities != null && (eq.cavities as number | string) !== '' ? parseInt(eq.cavities, 10) : null;   // INTEGER: '' nu vine
        const cavitiesLine = cavities ? `<div class="mold-linked-info">🔢 ${cavities} cuiburi</div>` : '';
        const activeBadge = moldInactive ? '<div class="mold-active-badge inactive">INACTIVĂ</div>' : '<div class="mold-active-badge active">ACTIVĂ</div>';
        const editCyclesBtn = canEditOffset
            ? `<button class="mold-edit-btn" data-eq-id="${escHtml(eqId)}" data-edit="cycles" title="Editare contor cicluri (master)">✏</button>` : '';
        const editHoursBtn = canEditCounters
            ? `<button class="mold-edit-btn" data-eq-id="${escHtml(eqId)}" data-edit="hours" title="Editare ore funcționare">✏</button>` : '';

        // Pentru SACMI: afișaj timp ciclu nominal + buton edit (DOAR matriță activă pe SACMI)
        let nominalLine = '';
        const linkedMain = (_allMachinesCache || []).find((m: Machine) => m.id === linkedName);
        const isSACMIMold = linkedMain && linkedMain.machine_type === 'compression';
        if (isSACMIMold) {
            const ct = eq.nominal_cycle_time_sec ? `${parseFloat(eq.nominal_cycle_time_sec).toFixed(2)} s` : '— nesetat';
            const editNominalBtn = canEditCounters
                ? `<button class="mold-edit-btn" data-eq-id="${escHtml(eqId)}" data-edit="nominal" title="Editare timp ciclu nominal / capace per oră">✏</button>` : '';
            nominalLine = `<div class="mold-counter-row mold-nominal-row">
                <span class="mold-ctr-icon">⏲</span>
                <span class="mold-ctr-label">T. ciclu</span>
                <span class="mold-ctr-val">${ct}</span>
                ${editNominalBtn}
            </div>`;
        }

        sensorHtml = `<div class="mold-counters">
            ${activeBadge}
            <div class="mold-counter-row">
                <span class="mold-ctr-icon">🔄</span>
                <span class="mold-ctr-label">Cicluri</span>
                <span class="mold-ctr-val sensor-val-live ${cycSev}" data-eq="${eqId}" data-sensor="cycles">${realCycles.toLocaleString('ro-RO')}</span>
                ${editCyclesBtn}
            </div>
            <div class="mold-counter-row">
                <span class="mold-ctr-icon">⏱</span>
                <span class="mold-ctr-label">Ore</span>
                <span class="mold-ctr-val sensor-val-live ${hrSev}" data-eq="${eqId}" data-sensor="hours">${realHours.toFixed(1)}</span>
                ${editHoursBtn}
            </div>
            ${nominalLine}
            <div class="mold-linked-info">📎 ${escHtml(linkedName)}</div>
            ${cavitiesLine}
        </div>`;
    } else {
        // Auxiliare/utilități/control/inspection ȘI mașini principale (P3 fix 2026-05-15):
        // afișează ore proprii pe card + pictogramă editare. Header-ul liniei rămâne pentru
        // ore „linie" (suma/maxim utilaje), iar cardul mașinii principale afișează propria ei valoare.
        const realHours = parseFloat(eq.operating_hours || '0') || 0;
        const editHoursBtn = canEditCounters
            ? `<button class="mold-edit-btn" data-eq-id="${escHtml(eqId)}" data-edit="hours" title="Editare ore funcționare (corectează valoarea curentă; incrementarea automată continuă de la noua valoare)">✏</button>` : '';
        // Sursa orelor: 'em63'/'sfm12ld'/'pac3220'/'manual'/'inherited' din DB (auto_hours_source)
        // Fallback: hardware (meter/em63 protocol) sau utilitate transversală.
        const _eqSrc = (eq.auto_hours_source || '').toString();
        let sourceLabel: string;
        if (_eqSrc === 'contor_masina') sourceLabel = '<span class="hours-source-badge auto" title="Ore citite de pe contorul mașinii (motoare pornite), prin EUROMAP 63">CONTOR MAȘINĂ</span>';
        else if (_eqSrc === 'em63') sourceLabel = '<span class="hours-source-badge auto" title="Cycle_count × cycle_time din EUROMAP 63">EM63</span>';
        else if (_eqSrc === 'sfm12ld') sourceLabel = '<span class="hours-source-badge auto" title="operating_hours direct de la SFM12LD">SFM</span>';
        else if (_eqSrc === 'pac3220') sourceLabel = '<span class="hours-source-badge auto" title="Acumulat când PAC3220 măsoară putere &gt; prag">PAC3220</span>';
        else if (_eqSrc === 'manual') sourceLabel = '<span class="hours-source-badge manual" title="Setat manual din UI — persistă până la următoarea modificare">MANUAL</span>';
        else if (_eqSrc === 'inherited') sourceLabel = '<span class="hours-source-badge inherited" title="Moștenit de la mașina principală / linie">MOȘTENIT</span>';
        else if (eq.meter_connection_protocol === 'modbus_tcp') sourceLabel = '<span class="hours-source-badge auto" title="Power propriu PAC3220">PAC3220</span>';
        else if (eq.line === 'UTIL_AER') sourceLabel = '<span class="hours-source-badge transversal" title="Cumulează când orice linie principală rulează">transversal</span>';
        else sourceLabel = `<span class="hours-source-badge inherited" title="Moștenit din mașina principală a liniei ${escHtml(eq.line || '')}">moștenit</span>`;

        sensorHtml = `<div class="aux-hours-row">
            <span class="aux-hours-icon">⏱</span>
            <span class="aux-hours-label">Ore</span>
            <span class="aux-hours-val" data-eq="${eqId}">${realHours.toFixed(1)}</span>
            ${sourceLabel}
            ${editHoursBtn}
        </div>`;
    }
    const avgPwr = getHourlyAvgPower(eqId);
    let consumDisplay: string;
    // Drift fix 2026-05-11: unitate corectă (kW) — kW/h era dimensional GREȘIT.
    // Putere medie pe ultima oră se afișează ca "X.XX kW (medie 1h)".
    if (avgPwr != null) {
        consumDisplay = parseFloat(String(avgPwr)).toFixed(2) + ' kW (medie 1h)';
    } else {
        const powerNum = getSensorValue(eqId, 'power') ?? getSensorValue(eqId, 'consum');
        if (powerNum != null && !isNaN(parseFloat(String(powerNum)))) {
            consumDisplay = parseFloat(String(powerNum)).toFixed(2) + ' kW';
        } else {
            consumDisplay = (eq.manual_power_kw != null && eq.manual_power_kw > 0) ? parseFloat(eq.manual_power_kw).toFixed(2) + ' kW (manual)' : '— kW';
        }
    }
    // Drift 2 fix 2026-05-11: stocăm `manual_power_kw` în atribut data-* ca să poată fi
    // recuperat ca fallback în updateSensorDisplays() — fără el, refresh-ul periodic
    // suprascria valoarea de plăcuță cu „— kW" când telemetria lipsea temporar.
    const manualKw = (eq.manual_power_kw != null && eq.manual_power_kw > 0) ? parseFloat(String(eq.manual_power_kw)) : 0;
    const consumLine = '<div class="eq-consumption"><span class="eq-consum-label">Consum</span><span class="eq-consum-val" data-eq="' + eqId + '" data-sensor="power" data-manual-kw="' + manualKw + '">' + consumDisplay + '</span></div>';

    const alarmSum = ost.alarmSummary[eqId];
    const alarmBadgeHtml = alarmSum && alarmSum.total_active! > 0
        ? `<span class="eq-alarm-badge ${alarmSum.critical_count! > 0 ? 'critical' : 'warning'}">${alarmSum.total_active} alarme</span>`
        : '';

    // Banner status (audit fix 2026-05-14):
    //  - Ignore stale `machine_state` (>5 min); a stale "Producție" is worse than no banner.
    //  - Fallback to PAC3220 `power` reading if fresh: ≥ 1 kW → "În producție", else "Oprită".
    //  - If we have a stale signal but no fresh data → "● Necunoscut".
    //  - If neither sensor is configured for this machine (mold, aux without telemetry,
    //    most UTIL_AER equipment) → keep banner hidden, same as before.
    //
    // Threshold note: per-line `production_lines.power_threshold_kw` is applied by the
    // backend `power_aggregation.py` for the LINE banner. For individual equipment cards
    // we use 1 kW (per audit spec) which is comfortably above PAC3220 idle/noise.
    const machineStateRaw = getSensorRaw(eqId, 'machine_state');
    const machineStateFresh = machineStateRaw != null && isSensorRealtime(eqId, 'machine_state');
    const powerRaw = getSensorRaw(eqId, 'power') ?? getSensorRaw(eqId, 'consum');
    const powerFresh = (getSensorRaw(eqId, 'power') != null && isSensorRealtime(eqId, 'power'))
                   || (getSensorRaw(eqId, 'consum') != null && isSensorRealtime(eqId, 'consum'));
    const hasAnyStateSignal = machineStateRaw != null || powerRaw != null;

    let machineStateHtml = '';
    if (machineStateFresh && machineStateRaw != null) {
        const stateMap: Record<number, string> = {0:'Invalid',1:'Oprită',2:'Oprită intern',3:'Oprită downstream',4:'Oprită upstream',5:'Oprită servicii',6:'Oprită operator',7:'Producție',8:'Mentenanță',9:'Schimbare format',10:'Pre-încălzire'};
        const stNum = parseInt(String(machineStateRaw), 10);
        const stLabel = stateMap[stNum] || `Stare ${machineStateRaw}`;
        const stClass = stNum === 7 ? 'producing' : (stNum >= 2 && stNum <= 6 ? 'stopped' : 'other');
        machineStateHtml = `<div class="eq-machine-state ${stClass}"><span class="eq-state-dot"></span>${escHtml(stLabel)}</div>`;
    } else if (powerFresh && powerRaw != null) {
        const pwNum = parseFloat(String(powerRaw));
        if (Number.isFinite(pwNum)) {
            if (pwNum >= 1) {
                machineStateHtml = `<div class="eq-machine-state producing" title="Putere ${pwNum.toFixed(2)} kW ≥ 1 kW (PAC3220 LIVE)"><span class="eq-state-dot"></span>În producție</div>`;
            } else {
                machineStateHtml = `<div class="eq-machine-state stopped" title="Putere ${pwNum.toFixed(2)} kW < 1 kW (PAC3220 LIVE)"><span class="eq-state-dot"></span>Oprită</div>`;
            }
        }
    } else if (hasAnyStateSignal) {
        machineStateHtml = `<div class="eq-machine-state other" title="Senzor stare / contor curent fără date recente (>5 min)"><span class="eq-state-dot"></span>Necunoscut</div>`;
    }

    // Drift 4 fix 2026-05-11: afișează `current_mold` din telemetria text (sensor_text_readings).
    // Câmpul e populat de colectorul EM63 din HMI (@ActDescMld) și reprezintă DESCRIEREA matriței
    // active introdusă de operator. Dacă diferă de matrița activă din DB (active_mold_id), arătăm
    // un badge de avertizare ca să se vadă desincronizarea (operator a schimbat matrița în HMI dar
    // nu și în aplicație, sau invers). Logica defensivă: dacă lipsește telemetria, NU schimbăm
    // afișajul existent (matrița din DB rămâne pe cardul matriță separat).
    let moldTelemetryHtml = '';
    try {
        const cmRaw = getSensorRaw(eqId, 'current_mold');
        if (cmRaw != null && String(cmRaw).trim() !== '') {
            const cmTxt = String(cmRaw).trim();
            // Comparare aproximativă cu numele matriței active din DB:
            // active_mold_id e ID-ul matriței (ex: "MOLD M8 5L-MD"); căutăm numele real în cache.
            let dbMoldName = '';
            const activeMoldId = eq.active_mold_id;
            if (activeMoldId && _allMachinesCache) {
                const moldRec = _allMachinesCache.find((m: Machine) => m.id === activeMoldId);
                if (moldRec) dbMoldName = String(moldRec.name || moldRec.id || '');
            }
            // Normalizare simplă (lower + trim) pentru comparare permisivă.
            const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
            const mismatch = dbMoldName !== '' && norm(cmTxt) !== norm(dbMoldName);
            const badge = mismatch
                ? `<span class="eq-mold-warn" title="Diferă de matrița activă din DB: «${escHtml(dbMoldName)}». Verifică sincronizarea HMI ↔ aplicație." style="margin-left:6px;padding:1px 6px;background:#f59e0b;color:#000;border-radius:6px;font-size:.65rem;font-weight:700">⚠ desincronizat</span>`
                : '';
            moldTelemetryHtml = `<div class="eq-mold-telemetry" style="margin-top:6px;padding:4px 8px;background:rgba(0,230,118,.06);border-left:3px solid var(--green,#00e676);border-radius:4px;font-size:.78rem;color:var(--text,#e2e8f0)">
                <span style="opacity:.7">🧩 Matriță HMI:</span> <strong>${escHtml(cmTxt)}</strong>${badge}
            </div>`;
        }
    } catch (_) { /* defensiv: dacă telemetria nu există, lăsăm cardul așa cum e */ }

    const inactiveClass = moldInactive ? ' equip-inactive' : '';
    return `<div class="equip-card ${isMain ? 'equip-main' : 'equip-sub'} ${statusCls}${inactiveClass}" data-eq="${eqId}" data-eq-name="${escHtml(eq.name)}" data-eq-json='${JSON.stringify(eq).replace(/'/g,"&#39;")}'>
        <div class="eq-actions">
            <button class="eq-action-btn edit-eq-btn" data-eq="${eqId}" title="Editează">✏</button>
        </div>
        <div class="eq-header"><span class="eq-id">${eqId}</span><span class="eq-type-tag">${typeLabel}</span>${alarmBadgeHtml}${shared}${serves}</div>
        ${statusTag}
        ${_eqTechPauseRow}
        ${_eqGoliveRow}
        ${machineStateHtml}
        <div class="eq-name">${escHtml(eq.name)}</div>
        ${eq.serial_number ? `<div class="eq-sn">S/N: ${escHtml(eq.serial_number)}</div>` : ''}
        ${sensorHtml}
        ${consumLine}
        ${moldTelemetryHtml}
        <div class="eq-status-btns">
            <button class="eqs-btn ${eqStatus==='ok'?'active':''}" data-eq="${eqId}" data-st="ok">OK</button>
            <button class="eqs-btn warn ${eqStatus==='warning'?'active':''}" data-eq="${eqId}" data-st="warning">⚠</button>
            <button class="eqs-btn stop ${eqStatus==='stopped'?'active':''}" data-eq="${eqId}" data-st="stopped">⏹</button>
        </div>
        ${badgesHtml ? `<div class="mc-badges">${badgesHtml}</div>` : ''}
    </div>`;
}

/* ══════ ROLE-BASED MENU VISIBILITY ══════ */
export function _applyRoleMenus(role: string): void {
    const roleLevel: Record<string, number> = { operator: 1, engineer: 2, master: 3 };
    const userLevel = roleLevel[role] || 1;

    document.querySelectorAll('.nav-group[data-min-role]').forEach((grp: Element) => {
        const minLevel = roleLevel[(grp as HTMLElement).dataset.minRole || ''] || 1;
        if (userLevel >= minLevel) {
            grp.classList.remove('is-hidden');
        } else {
            grp.classList.add('is-hidden');
            grp.classList.remove('open');
        }
    });

    // gating și pe elemente individuale (ex. Transmisii live = inginer+);
    // înainte se filtrau doar grupurile, iar data-min-role pe item era ignorat
    document.querySelectorAll('.nav-item[data-min-role]').forEach((it: Element) => {
        const minLevel = roleLevel[(it as HTMLElement).dataset.minRole || ''] || 1;
        (it as HTMLElement).style.display = userLevel >= minLevel ? '' : 'none';
    });

    /* 26 aug 2026: se citeau DOAR `.nav-group` și `.nav-item`. Restul
       atributelor `data-min-role` din pagină — fila „🛠 Colectoare" din Overview,
       de pildă — nu erau citite de nimeni: markup-ul promitea „doar master", iar
       inginerul vedea fila și lista cheilor de colector. Nu e o breșă azi
       (serverul păzește rutele), dar e o protecție IMAGINARĂ, care devine breșă
       la prima schimbare de roluri. Un atribut care minte e mai rău decât unul
       care lipsește. Prins de proba cap-coadă overview.spec.ts:1010. */
    document.querySelectorAll('[data-min-role]:not(.nav-group):not(.nav-item)')
        .forEach((el: Element) => {
            const minLevel = roleLevel[(el as HTMLElement).dataset.minRole || ''] || 1;
            (el as HTMLElement).style.display = userLevel >= minLevel ? '' : 'none';
        });

    const engGroup = document.getElementById('groupEngineer');
    const masGroup = document.getElementById('groupMaster');
    if (userLevel >= 2 && engGroup && !engGroup.classList.contains('is-hidden')) {
        setTimeout(() => engGroup!.classList.add('open'), 250);
    }
    if (userLevel >= 3 && masGroup && !masGroup.classList.contains('is-hidden')) {
        setTimeout(() => masGroup!.classList.add('open'), 350);
    }

    _updateUserBadge(role);
    // Tema D (29 sep 2026): se aplică abia acum, când se știe rolul — o vede doar masterul.
    aplicaTema(role);

    const btnUsers = document.getElementById('btn-open-users');
    if (btnUsers) btnUsers.style.display = (role === 'master') ? '' : 'none';
    const btn2fa = document.getElementById('btn-open-2fa');
    if (btn2fa) btn2fa.style.display = userLevel >= 1 ? '' : 'none';
    const btnChpwd = document.getElementById('btn-open-chpwd');
    if (btnChpwd) btnChpwd.style.display = userLevel >= 1 ? '' : 'none';
    // «Autentificare» rămânea în bară și DUPĂ login, lângă 🔐 🔑 (audit 4 sep 2026)
    const btnLoginTop = document.getElementById('btn-open-login');
    if (btnLoginTop) btnLoginTop.style.display = userLevel >= 1 ? 'none' : '';
    const btnNewProc = document.getElementById('cal-new-procedure');
    if (btnNewProc) btnNewProc.style.display = userLevel >= 2 ? '' : 'none';

    /* 4 sep 2026 (audit, mecanic simulat): operatorul ateriza pe «Overview»
       cu clepsidra și patru icoane de cont, fără nicio lucrare — și pe telefon
       primul ecran era gol. Mecanicul intră direct în «Lucrările mele»;
       inginerul și masterul rămân pe tablou. */
    const defaultSection = role === 'operator' ? 'mytasks' : 'dashboard';
    const activeLink = document.querySelector('.nav-links a[data-section="' + defaultSection + '"]');
    if (activeLink) {
        document.querySelectorAll('.nav-links a, .nav-links .nav-item').forEach(x => x.classList.remove('active'));
        activeLink.classList.add('active');
        document.querySelectorAll('.section').forEach(s => s.classList.remove('active'));
        const sec = document.getElementById('sec-' + defaultSection);
        if (sec) sec.classList.add('active');
    }
}

function _updateUserBadge(role: string): void {
    const avatar = document.getElementById('userAvatar');
    const nameEl = document.getElementById('userName');
    const roleEl = document.getElementById('userRole');
    if (!avatar || !nameEl || !roleEl) return;

    const username = state.currentUser?.username;
    const roleLabels: Record<string, string> = { operator: 'OPERATOR', engineer: 'INGINER', master: 'MASTER' };
    const avatarCls: Record<string, string> = { operator: 'avatar-operator', engineer: 'avatar-engineer', master: 'avatar-master' };
    const roleCls: Record<string, string> = { operator: 'role-c-operator', engineer: 'role-c-engineer', master: 'role-c-master' };

    if (username) {
        nameEl.textContent = username;
        const parts = username.split(/[\s._-]+/);
        avatar.textContent = parts.length >= 2
            ? (parts[0][0] + parts[1][0]).toUpperCase()
            : username.substring(0, 2).toUpperCase();
    } else {
        nameEl.textContent = roleLabels[role] || 'Operator';
        avatar.textContent = (roleLabels[role] || 'OP').substring(0, 2);
    }

    roleEl.textContent = roleLabels[role] || 'OPERATOR';
    roleEl.className = 'user-role ' + (roleCls[role] || 'role-c-operator');
    avatar.className = 'user-avatar ' + (avatarCls[role] || 'avatar-operator');
}

/* ══════ DASHBOARD PRODUCȚIE ══════ */
// Reîncărcare a cardurilor de linie, chemată după acțiuni care schimbă starea
// (finalizarea unei intervenții, scoaterea marcajului de mentenanță) — 20 aug 2026.
(window as FereastraOverview).refreshLinePanels = () => {
    try { loadDashboard(); } catch (_e) { /* dacă nu suntem pe tablou, nu contează */ }
    try { (window as FereastraOverview).loadOverview?.(); } catch (_e) { /* idem */ }
};

/** Tema D: antetul Producției (câte linii merg / stau + „Toate liniile" / „Cer atenție").
 *  Comutatorul scrie în filtrul existent `#dash-status-filter` — o singură logică de filtrare. */
function _antetProductieD(pd: PanelData, sf: string): void {
    const sec = document.getElementById('sec-dashboard');
    const cards = document.getElementById('dash-cards');
    if (!sec || !cards) return;
    let antet = document.getElementById('d-dash-antet');
    if (!antet) {
        antet = document.createElement('div');
        antet.id = 'd-dash-antet';
        antet.className = 'd-doar';
        sec.insertBefore(antet, cards);
        antet.addEventListener('click', (e) => {
            const b = (e.target as HTMLElement).closest('[data-dfiltru]') as HTMLElement | null;
            const sel = document.getElementById('dash-status-filter') as HTMLSelectElement | null;
            if (!b || !sel) return;
            sel.value = b.dataset.dfiltru || '';
            try { localStorage.setItem('fm_d_productie', sel.value); } catch (_e) { /* fără stocare */ }
            sel.onchange?.(new Event('change'));
        });
    }
    antet.innerHTML = antetProductieD(pd, sf);
}

export async function loadDashboard(): Promise<void> {
    const container = document.getElementById('dash-cards') as HTMLElement | null;
    /* Clic pe TOT cardul de linie = „Detalii linie" (audit 20 sep 2026, §3.10):
     * pe tabletă omul apasă cardul, nu butonul mic din colț — și nu se întâmpla
     * nimic. Butoanele, chip-urile și linkurile din card își păstrează treaba lor;
     * doar restul suprafeței duce la detalii. Legat o singură dată, delegat, ca să
     * supraviețuiască fiecărei re-randări. */
    if (container && !(container as HTMLElement & { _clicLinie?: boolean })._clicLinie) {
        (container as HTMLElement & { _clicLinie?: boolean })._clicLinie = true;
        const deschide = (card: HTMLElement) => {
            const w = window as FereastraOverview;
            if (card.classList.contains('dl-card')) {   // tema D: fereastra liniei
                if (card.dataset.line && card.dataset.line !== '_utils') deschideLinieD(card.dataset.line);
                return;
            }
            if (typeof w.openLineLayoutDetail === 'function') {
                w.openLineLayoutDetail(card.dataset.line || '', card.dataset.product || '');
            }
        };
        container.addEventListener('click', (ev) => {
            const t = ev.target as HTMLElement;
            if (t.closest('button, a, select, input, .dash-eq-chip, .lp-alarm-badge')) return;
            const card = t.closest('.dash-line-card') as HTMLElement | null;
            if (card) deschide(card);
        });
        container.addEventListener('keydown', (ev) => {
            const e = ev as KeyboardEvent;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            const card = (e.target as HTMLElement).closest('.dash-line-card') as HTMLElement | null;
            if (card && e.target === card) { e.preventDefault(); deschide(card); }
        });
    }
    const lineFilter = document.getElementById('dash-line-filter') as HTMLSelectElement | null;
    const statusFilter = document.getElementById('dash-status-filter') as HTMLSelectElement | null;
    if (!container) return;

    try {
        // Drift fix 2026-05-11: aliniem Dashboard cu Overview Fabrică pentru status linie.
        // Adăugăm /api/lines/status în paralel ca să avem `running_state` real (pe baza
        // pragului de putere din colector). Dacă o linie e raportată `stopped` de colector,
        // Dashboard-ul va afișa "OPRITĂ" indiferent de statusul derivat din alarme/echipamente.
        const [structRes, ivRes, alertsRes, lineStatusRes] = await Promise.all([
            fetch('/api/factory_structure'),
            fetch('/api/maintenance_intervals'),
            fetch('/api/ai/proactive_alerts').catch(() => ({ json: () => ({}) })),
            fetch('/api/lines/status').catch(() => ({ json: () => ({ lines: [] }) })),
        ]);
        const struct: StructuraFabrica = await structRes.json();
        // ruta trimite direct lista; `intervals` (rezerva veche) nu există
        const ivData: IntervalCuStare[] & { intervals?: undefined } = await ivRes.json();
        const intervals: IntervalCuStare[] = ivData.intervals || ivData || [];
        const lines = struct.production_lines || struct.lines || [];

        // Sincronizare ore (aceeași sursă ca Overview) — computeAlerts folosește state.machineHours
        lines.forEach((pl: LinieStructura) => {
            const mainM = pl.main_machine;
            if (mainM && mainM.operating_hours != null) {
                const h = parseFloat(mainM.operating_hours) || 0;
                state.machineHours[pl.id] = h;
                state.machineHours[mainM.id] = h;
            }
            (pl.equipment || []).forEach((eq: UtilajStructura) => {
                if (eq.operating_hours != null) {
                    state.machineHours[eq.id] = parseFloat(eq.operating_hours) || 0;
                }
            });
        });

        const maintAlerts = computeAlerts(intervals);
        let sensorAlerts: AlertaProactiva[] = [];
        try {
            // `{}` = rezerva din .catch de mai sus; `alerts` nu-l trimite ruta (dă direct lista)
            const ad: AlertaProactiva[] | { alerts?: undefined } = await alertsRes.json();
            sensorAlerts = Array.isArray(ad) ? ad : (ad?.alerts || []);
        } catch (e) { /* ignore */ }
        // hide_all: proactive_alerts filtrate și pe backend; și aici la nivel linie
        const allAlerts = [...maintAlerts, ...sensorAlerts];
        let qualityAlerts: AlertaCalitate[] = [];
        try { qualityAlerts = await _loadQualityAlerts(); } catch(e) {}
        // Map id-linie → snapshot status live (running/stopped/unknown + power_kw + collector_status)
        const linesStatusMap: Record<string, StareLinie> = {};
        try {
            const ls: { lines: StareLinie[] } = await lineStatusRes.json();
            const arr = Array.isArray(ls?.lines) ? ls.lines : [];
            arr.forEach((l: StareLinie) => { if (l && l.id) linesStatusMap[l.id] = l; });
        } catch (e) {
            console.warn('[loadDashboard] /api/lines/status indisponibil', e);
        }
        // Salvăm și pe state pentru consum extern (debug, alte module)
        try { (state as OverviewState).linesStatusMap = linesStatusMap; } catch (_) {}

        const utilGroups = struct.utilities || {};
        const utilLines = Object.entries(utilGroups).map(([uKey, u]) => ({
            id: uKey,
            product: u.label || uKey,
            line_type: 'utilitate',
            group_type: 'utility',
            icon: u.icon || '⚙',
            main_machine: (u.machines || [])[0] || {},
            equipment: u.machines || [],
            production_rate: 0,
            consumption: 0,
        }));
        const allLines: LinieTablou[] = [...lines, ...utilLines];

        if (lineFilter!.options.length <= 1) {
            allLines.forEach(l => {
                const o = document.createElement('option');
                o.value = l.id; o.textContent = (l.group_type === 'utility' ? '⚙ ' : '') + l.id + (l.product ? ' — ' + l.product : '');
                lineFilter!.appendChild(o);
            });
        }

        // Faza B (iul 2026): sursă unică server-side pentru carduri. Dacă
        // /api/lines/panel răspunde, cardurile se randează din el (identice cu
        // Administrarea); altfel cădem pe randarea legacy de mai jos.
        let panelData: PanelData | null = null;
        try {
            panelData = await fetchLinesPanel();
        } catch (e) {
            console.warn('[loadDashboard] /api/lines/panel indisponibil — fallback legacy', e);
        }

        // Auto-refresh 15s cât timp secțiunea Dashboard e vizibilă — kW live,
        // kWh, alarme și semnale se mișcă fără reload de pagină.
        const _w = window as FereastraOverview;
        if (_w._lpDashTimer) clearInterval(_w._lpDashTimer);
        _w._lpDashTimer = setInterval(async () => {
            const sec = document.getElementById('sec-dashboard');
            if (!sec || !sec.classList.contains('active')) {
                clearInterval(_w._lpDashTimer!); _w._lpDashTimer = null;   // rulează doar cât e pornit, deci are id
                return;
            }
            try {
                panelData = await fetchLinesPanel();
                renderCards();
            } catch (e) { /* păstrăm ultima randare la eroare tranzitorie */ }
        }, 15000);

        function renderCardsFromPanel(pd: PanelData) {
            const lf = lineFilter!.value;
            const sf = statusFilter!.value;
            let arr = pd.lines;
            if (lf && lf !== '_utils') arr = arr.filter(l => l.id === lf);
            if (sf === 'alert') arr = arr.filter(l =>
                l.dominant_signal || (l.alarms.critical + l.alarms.warning) > 0 || l.health.overdue > 0);
            if (sf === 'stopped') arr = arr.filter(l => l.status === 'stopped');
            const d = temaD();
            if (d) _antetProductieD(pd, sf);
            let html = arr.map(l => d ? buildLineCardD(l) : buildLineCardHtml(l, { admin: false })).join('');
            if (!sf && (!lf || lf === '_utils')) html += d ? buildUtilitiesCardD(pd.utilities_card) : buildUtilitiesCardHtml(pd.utilities_card);
            container!.innerHTML = html || (d ? '<div class="alarm-empty">Nicio linie nu cere atenție acum.</div>' : '<div class="alarm-empty">Niciun rezultat.</div>');
            wireLinePanelContainer(container!);
            if (d) incarcaPozeD(container!);
        }

        function renderCards() {
            if (panelData && Array.isArray(panelData.lines) && panelData.lines.length) {
                renderCardsFromPanel(panelData);
                return;
            }
            const lf = lineFilter!.value;
            const sf = statusFilter!.value;
            let filtered = allLines;
            if (lf) filtered = filtered.filter(pl => pl.id === lf);

            if (sf === 'alert') filtered = filtered.filter(pl => {
                return allAlerts.some(a => (pl.equipment || []).some((eq: UtilajStructura) => eq.id === a.machine_id))
                    || (pl.equipment || []).some((eq: UtilajStructura) => eq.status === 'error' || eq.status === 'maintenance');
            });
            if (sf === 'stopped') filtered = filtered.filter(pl => (pl.equipment || []).some((eq: UtilajStructura) => eq.status === 'stopped'));

            if (!filtered.length) {
                container!.innerHTML = '<div class="alarm-empty">Niciun rezultat.</div>';
                return;
            }

            container!.innerHTML = filtered.map(pl => {
                const mainM = pl.main_machine || (pl.equipment || [])[0] || {};
                const eqCount = (pl.equipment || []).length;
                const product = pl.product || mainM.product || '';
                const lineType = (pl.line_type || '').toUpperCase();
                const consumption = pl.consumption || 0;
                const prodRate = pl.production_rate || 0;

                const lineSuppressed = shouldSuppressLineMaintenanceAlerts(pl);
                const lineAlerts = lineSuppressed
                    ? []
                    : filterMaintenanceAlertsForLine(allAlerts, pl, pl);
                const hasErrors = (pl.equipment || []).some((eq: UtilajStructura) => eq.status === 'error');
                const hasStopped = (pl.equipment || []).some((eq: UtilajStructura) => eq.status === 'stopped');
                const hasQAlerts = lineSuppressed ? false : qualityAlerts.some(a => a.line_id === pl.id);
                // Drift fix 2026-05-11: combinăm cu `running_state` live din /api/lines/status.
                // Realitatea live câștigă: dacă colectorul (sub pragul de putere) marchează linia
                // OPRITĂ, ignorăm starea derivată din alarme. Pentru `running` neacoperit de
                // alarme/erori, marcăm explicit ACTIV. Necunoscut → fallback la heuristica veche.
                const liveSt = linesStatusMap[pl.id];
                const liveRun = liveSt && liveSt.running_state; // 'running' | 'stopped' | undefined/'unknown'
                let lineStatus: 'ok' | 'warning' | 'error' | 'stopped';
                if (lineSuppressed) lineStatus = 'stopped';
                else if (hasErrors) lineStatus = 'error';
                else if (liveRun === 'stopped') lineStatus = 'stopped';
                else if (hasStopped) lineStatus = 'stopped';
                else if (lineAlerts.length > 0 || hasQAlerts) lineStatus = 'warning';
                else lineStatus = 'ok';
                const stClass = lineStatus === 'ok' ? 'dash-status-ok' : lineStatus === 'error' ? 'dash-status-error' : lineStatus === 'stopped' ? 'dash-status-stopped' : 'dash-status-maintenance';
                let stLabel = lineSuppressed ? 'NEPORNITĂ'
                    : lineStatus === 'ok' ? 'ACTIV' : lineStatus === 'error' ? 'EROARE' : lineStatus === 'stopped' ? 'OPRITĂ' : 'ATENȚIE';
                // Sufixe live pentru transparență (când există snapshot real)
                if (liveRun === 'running' && lineStatus === 'ok') stLabel = 'PRODUCȚIE';
                if (liveRun === 'stopped' && lineStatus === 'stopped') stLabel = 'OPRITĂ';

                const hours = parseFloat(mainM.operating_hours) || 0;
                const lineIv = lineSuppressed ? [] : intervals.filter((iv: IntervalCuStare) =>
                    (iv.machine_id === mainM.id || iv.machine_id === pl.id) && shouldShowMaintenanceInterval(iv as IvVizibil)
                );
                let nextMaint = '—';
                if (!lineSuppressed && lineIv.length) {
                    // starea din sursa unică (1 nov 2026) — zilnicele sunt în planul zilei
                    const soonest = lineIv.reduce((best: { rem: number; text: string; action: string | null }, iv: IntervalCuStare) => {
                        const rem = oreRamaseStare(iv.stare);
                        if (rem == null || iv.stare?.mod === 'zilnic') return best;
                        return rem < best.rem ? { rem, text: iv.stare!.text, action: iv.action_ro || iv.interval_text } : best;
                    }, { rem: 1e9, text: '', action: '' });
                    if (soonest.rem < 1e9) nextMaint = `${(soonest.action || '').substring(0, 60)} — ${soonest.text}`;
                }

                const moldEq = (pl.equipment || []).find((e: UtilajStructura) => e.machine_type === 'mold' && e.is_active);
                const moldInfo = moldEq ? `${moldEq.name || moldEq.id}` + (moldEq.cycle_count ? ` · ${parseInt(moldEq.cycle_count).toLocaleString()} cicluri` : '') : '';

                const lineQAlerts = qualityAlerts.filter(a => a.line_id === pl.id);
                let alertsHtml = '';
                const combinedAlerts = [...lineAlerts.map(a => ({...a, atype: 'maint'})), ...lineQAlerts.map(a => ({...a, atype: 'quality'}))];
                if (combinedAlerts.length > 0) {
                    alertsHtml = '<div class="dash-alerts">';
                    const first = combinedAlerts[0];
                    if (first.atype === 'quality') {
                        alertsHtml += `<div class="dash-alert-row dash-alert-primary dash-alert-quality">🔍 <strong>Calitate</strong> — ${(first.action || 'Verificare necesară').substring(0, 100)}</div>`;
                    } else {
                        const eq = (pl.equipment || []).find((e: UtilajStructura) => e.id === first.machine_id);
                        alertsHtml += `<div class="dash-alert-row dash-alert-primary">⚠ <strong>${eq ? (eq.display_name || eq.id) : first.machine_id}</strong> — ${(first.action || 'Mentenanță necesară').substring(0, 100)}</div>`;
                    }
                    if (combinedAlerts.length > 1) {
                        alertsHtml += `<div class="dash-alert-row dash-alert-more">+ încă ${combinedAlerts.length - 1} alerte mentenanță</div>`;
                    }
                    alertsHtml += '</div>';
                }

                const eqWarnings = (pl.equipment || []).filter((eq: UtilajStructura) => eq.status === 'error' || eq.status === 'maintenance' || eq.status === 'stopped');
                let eqStatusHtml = '';
                if (eqWarnings.length > 0) {
                    eqStatusHtml = eqWarnings.map((eq: UtilajStructura) => {
                        const s = eq.status;
                        const cls = s === 'error' ? 'dash-eq-error' : s === 'stopped' ? 'dash-eq-stopped' : 'dash-eq-warn';
                        const eqLbl = eq.display_name || eq.id;
                        return `<span class="${cls}">${escHtml(eqLbl)}: ${eq.status_note || s}</span>`;
                    }).join('');
                    eqStatusHtml = '<div class="dash-eq-statuses">' + eqStatusHtml + '</div>';
                }

                // Bug fix 2026-05-13: backend-attributed power. Falls back to
                // legacy local sum only when /api/factory_structure didn't
                // include the new fields (older deploys).
                let lineAvgPower = 0, hasPower = false;
                if (typeof pl.power_kw_attributed === 'number') {
                    lineAvgPower = pl.power_kw_attributed;
                    hasPower = true;
                } else {
                    (pl.equipment || []).forEach((eq: UtilajStructura) => {
                        const a = getHourlyAvgPower(eq.id);
                        const p = a ?? getSensorValue(eq.id, 'power') ?? getSensorValue(eq.id, 'consum');
                        const mp = eq.manual_power_kw;
                        if (p != null && !isNaN(p)) { lineAvgPower += parseFloat(String(p)); hasPower = true; }
                        else if (mp != null && mp > 0) { lineAvgPower += parseFloat(mp); hasPower = true; }
                    });
                }
                const isLineOff = (pl.power_status === 'stopped') || (liveRun === 'stopped');
                const powerDisplay = !hasPower
                    ? '—'
                    : (isLineOff
                        ? (lineAvgPower > 0.01
                            ? lineAvgPower.toFixed(2) + ' kW (OPRITĂ — utilaje proprii)'
                            : '0.00 kW (OPRITĂ)')
                        : lineAvgPower.toFixed(2) + ' kW (medie 1h)');
                const isUtility = pl.group_type === 'utility';
                const prodDisplay = isUtility ? null : (prodRate > 0 ? Math.round(prodRate) + ' buc/h' : '—');
                const eqIdsStr = (pl.equipment || []).map((e: UtilajStructura) => e.id).join(',');
                const eqJsonStr = JSON.stringify((pl.equipment||[]).map((e: UtilajStructura)=>({id:e.id,mp:e.manual_power_kw||0}))).replace(/'/g,"&#39;");

                const eqList = (pl.equipment || []).map((eq: UtilajStructura) => {
                    const _eqIcons = {injection:'⚙️',compression:'⚙️',mold:'🔲',chiller:'❄️',cooler:'🌡️',compressor:'💨',dryer:'🔥',reservoir:'🛢️',control:'🖥️',inspection:'👁️',tampoprint:'🖨️',auxiliary:'🔧'};
                    const icon = (_eqIcons as Record<string, string>)[eq.machine_type as string] || '🔧';   // null → '🔧'
                    const eqSt = eq.status === 'ok' ? '' : eq.status === 'error' ? ' dash-eq-err' : eq.status === 'stopped' ? ' dash-eq-stp' : '';
                    const eqLabel = eq.display_name || eq.id;
                    const alSum = ost.alarmSummary[eq.id];
                    let dotCls = 'dash-eq-dot-ok';
                    if (alSum?.critical_count) dotCls = 'dash-eq-dot-crit';
                    else if (alSum?.total_active) dotCls = 'dash-eq-dot-warn';
                    else if (eqSt) dotCls = 'dash-eq-dot-warn';
                    return `<button type="button" class="dash-eq-chip${eqSt}" title="${eq.name || eq.id}" onclick="event.stopPropagation(); openLineDetail('${eq.id}','${(eq.name||'').replace(/'/g,'\\&#39;')}', '${state.currentUser?.role || 'operator'}')">
                        <span class="dash-eq-dot ${dotCls}"></span>${icon} ${escHtml(eqLabel)}
                    </button>`;
                }).join('');

                return `<div class="dash-line-card ${lineStatus !== 'ok' ? 'dash-line-alert' : ''}" data-line="${pl.id}" data-product="${escHtml(product || '')}" role="button" tabindex="0" title="Detalii linie">
                    <div class="dash-line-header">
                        <div class="dash-line-title">
                            <span class="dash-line-id">${pl.id}</span>
                            <span class="dash-line-type">${lineType}</span>
                            <span class="dash-line-product">${product}</span>
                            <span class="dash-line-eqcount">${eqCount} utilaje</span>
                        </div>
                        <div class="dash-line-metrics">
                            ${prodDisplay !== null ? `<span class="dash-metric" title="Productivitate">🏭 ${prodDisplay}</span>` : ''}
                            <span class="dash-metric" title="Consum energie mediu/oră (cu distribuție utilaje partajate)">⚡ <span class="line-power-val" data-line-id="${pl.id}" data-line-eq="${eqIdsStr}" data-line-eq-json='${eqJsonStr}'>${powerDisplay}</span></span>
                            <span class="dash-metric" title="Ore funcționare mașină principală" ${hours === 0 ? 'style="color:var(--yellow)"' : ''}>🕐 ${hours > 0 ? hours.toFixed(1) + ' h' : '0 h ⚠'}</span>
                            <span class="dash-status ${stClass}">${stLabel}</span>
                        </div>
                    </div>

                    ${alertsHtml}${eqStatusHtml}

                    ${!isUtility ? `<div class="dash-line-info">
                        <div class="dash-info-grid">
                            ${moldInfo ? `<div class="dash-info-item"><strong>${moldInfo}</strong>Matriță activă</div>` : ''}
                            ${nextMaint !== '—' ? `<div class="dash-info-item"><strong>${nextMaint}</strong>Următoarea revizie</div>` : ''}
                        </div>
                    </div>` : ''}

                    <div class="dash-eq-list"><span class="dash-eq-label">Utilaje</span>${eqList}</div>

                    <div class="dash-actions dash-actions-primary">
                        <button type="button" class="dash-btn dash-btn-tasks" onclick="event.stopPropagation(); window._dashMyTasks('${pl.id}')">🔧 Lucrările mele</button>
                        <button type="button" class="dash-btn dash-btn-alarms" onclick="event.stopPropagation(); window.openAlarmHistoryForLine('${pl.id}')">🔔 Alarme linie</button>
                    </div>
                    <div class="dash-actions dash-actions-secondary">
                        <button type="button" class="dash-btn dash-btn-ai" onclick='event.stopPropagation(); _dashAskAI(${JSON.stringify(pl.id)},${JSON.stringify(mainM.id)})'>Întreabă AI</button>
                        ${!isUtility ? `<button type="button" class="dash-btn dash-btn-product" onclick="event.stopPropagation(); openProductQuality('${pl.id}','${(product||'').replace(/'/g,'\\&#39;')}')">Detalii Produs</button>` : ''}
                        <button type="button" class="dash-btn dash-btn-layout" onclick="event.stopPropagation(); openLineLayoutDetail('${pl.id}','${(product||'').replace(/'/g,'\\&#39;')}')">Detalii linie</button>
                    </div>
                </div>`;
            }).join('');
        }

        // tema D: alegerea „Toate liniile / Cer atenție" rămâne pe dispozitiv
        if (temaD() && !(statusFilter as HTMLSelectElement & { _dRestaurat?: boolean })._dRestaurat) {
            (statusFilter as HTMLSelectElement & { _dRestaurat?: boolean })._dRestaurat = true;
            try { const v = localStorage.getItem('fm_d_productie'); if (v === 'alert' || v === '') statusFilter!.value = v; } catch (_e) { /* */ }
        }
        lineFilter!.onchange = renderCards;
        statusFilter!.onchange = renderCards;
        renderCards();
    } catch (err) {
        container.innerHTML = `<div class="alarm-empty">Eroare încărcare: ${(err as Error).message}</div>`;
    }
}

export function _dashUrgent(machineId: string, machineName: string): void {
    const link = document.querySelector('.nav-links a[data-section="journal"]');
    if (link) (link as HTMLElement).click();
    setTimeout(() => {
        const sel = document.getElementById('urg-machine');
        if (sel) { (sel as HTMLInputElement).value = machineId; sel.dispatchEvent(new Event('change')); }
    }, 200);
}

/** Dashboard → Lucrările mele filtrate pe linie. */
export function _dashMyTasks(lineId: string): void {
    const link = document.querySelector('.nav-item[data-section="mytasks"], .nav-links a[data-section="mytasks"]');
    if (link) (link as HTMLElement).click();
    setTimeout(() => {
        const lineSel = document.getElementById('mytasks-line-filter') as HTMLSelectElement | null;
        if (lineSel && lineId) {
            lineSel.value = lineId;
            lineSel.dispatchEvent(new Event('change'));
        }
    }, 400);
}

/**
 * Deschide Chat AI cu context linie (toate utilajele) + mașina principală pentru RAG/unele tool-uri.
 * `lineId` = id linie (ex. M1); `mainMachineId` = utilaj principal (ex. IMM M1 CAPAC 48).
 */
export function _dashAskAI(lineId: string, mainMachineId: string): void {
    try {
        const w = window as unknown as { __chatLineSuppressClear?: boolean; __chatLineId?: string };
        w.__chatLineSuppressClear = true;
        w.__chatLineId = (lineId || '').trim();
    } catch (_) { /* ignore */ }
    const link = document.querySelector('.nav-links a[data-section="chat"]');
    if (link) (link as HTMLElement).click();
    setTimeout(() => {
        const sel = document.getElementById('chat-machine');
        if (sel) {
            (sel as HTMLInputElement).value = mainMachineId || '';
            sel.dispatchEvent(new Event('change'));
        }
        setTimeout(() => {
            try {
                (window as unknown as { __chatLineSuppressClear?: boolean }).__chatLineSuppressClear = false;
            } catch (_) { /* ignore */ }
        }, 0);
    }, 200);
}

/* ── „DE REZOLVAT" (setup to-do fabrică) ─────────────────────────── */

async function _refreshSetupTodoCard(): Promise<void> {
    const el = document.getElementById('setup-todo-count');
    const card = document.getElementById('setup-todo-card');
    if (!el) return;
    try {
        const data: TodoConfigurare = await api('/api/factory/setup-todo');
        el.textContent = String(data.total);
        if (card) {
            card.classList.remove('red', 'orange', 'green');
            card.classList.add(data.total === 0 ? 'green' : data.total > 15 ? 'red' : 'orange');
        }
    } catch (_e) { el.textContent = '—'; }
}

async function openSetupTodoModal(): Promise<void> {
    let ov = document.getElementById('setup-todo-overlay');
    if (!ov) {
        ov = document.createElement('div');
        ov.id = 'setup-todo-overlay';
        ov.className = 'detail-overlay hidden';
        ov.innerHTML = `<div class="detail-panel mp-panel" style="max-width:760px">
            <div class="mp-header">
                <div class="mp-title-block">
                    <div class="mp-breadcrumb">FABRICA ▸ CONFIGURARE</div>
                    <div class="mp-title">De rezolvat</div>
                    <div class="mp-identity">golurile de configurare și date — lista se golește pe măsură ce le rezolvi</div>
                </div>
                <button type="button" class="mp-close" id="setup-todo-close" title="Închide">✕</button>
            </div>
            <div class="mp-body" id="setup-todo-body"></div>
        </div>`;
        document.body.appendChild(ov);
        ov.querySelector('#setup-todo-close')?.addEventListener('click', () => ov!.classList.add('hidden'));
    }
    const body = ov.querySelector('#setup-todo-body') as HTMLElement;
    body.innerHTML = '<div class="mp-loading">Se scanează configurarea fabricii…</div>';
    ov.classList.remove('hidden');
    let data: TodoConfigurare;
    try {
        data = await api('/api/factory/setup-todo');
    } catch (e) {
        body.innerHTML = `<div class="mp-loading">Eroare: ${escHtml((e as Error)?.message || String(e))}</div>`;
        return;
    }
    let html = '';
    data.categories.forEach((c: TodoCategorie) => {
        html += `<div class="mp-section-title"><span>${c.icon} ${escHtml(c.label)} — ${c.items.length}</span></div>`;
        if (!c.items.length) {
            html += '<div class="mp-empty">✓ Nimic de rezolvat aici</div>';
            return;
        }
        c.items.forEach((it: TodoElement) => {
            html += `<button type="button" class="mp-row mp-row-click" data-todo-machine="${escHtml(it.machine_id)}">
                <span class="sevdot mp-dot-warn"></span>
                <span class="mp-row-main"><b>${escHtml(it.label)}</b> — ${escHtml(it.detail)}
                <span class="mp-row-sub">cum rezolvi: ${escHtml(it.fix)}</span></span>
                <span class="mp-row-go">›</span></button>`;
        });
    });
    if (data.total === 0) html = '<div class="mp-empty" style="font-size:1.05rem;padding:24px 0">✓ Totul configurat — nimic de rezolvat!</div>' + html;
    body.innerHTML = html;
    body.querySelectorAll('[data-todo-machine]').forEach(row => {
        row.addEventListener('click', () => {
            const mid = (row as HTMLElement).dataset.todoMachine || '';
            ov!.classList.add('hidden');
            (window as FereastraOverview).openMachinePanel?.(mid);
        });
    });
    // la redeschidere, actualizează și cardul din stats
    _refreshSetupTodoCard();
}

/**
 * ⚙ Configurare linie (butonul din cardul unificat, doar în Administrare):
 * pliază/depliază zona admin legacy de sub card (ore, GO-LIVE, matrițe,
 * utilaje, ștergere). În alt context (fără zonă) cade pe modalul Sumar Linie.
 */
(window as FereastraOverview).openLineConfig = (lineId: string): void => {
    const zone = document.querySelector(
        `.line-group[data-line-id="${CSS.escape(lineId)}"] .lp-admin-zone`
    ) as HTMLElement | null;
    if (zone) {
        const show = zone.style.display === 'none';
        zone.style.display = show ? '' : 'none';
        if (show) zone.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
    }
    const w = window as FereastraOverview;
    if (typeof w.openLineSummary === 'function') w.openLineSummary(lineId);
};

function _linePowerSummary(pl: LinieStructura): string {
    // Bug fix 2026-05-13: prefer the backend-attributed value from
    // /api/factory_structure so Overview Fabrică shows the same number as
    // Dashboard and Line Detail (no double-counting of shared chillers).
    if (typeof pl.power_kw_attributed === 'number') {
        const kw = pl.power_kw_attributed;
        if (pl.power_status === 'stopped') {
            return kw > 0.01
                ? kw.toFixed(2) + ' kW (linie OPRITĂ — utilaje proprii)'
                : '0.00 kW (linie OPRITĂ)';
        }
        return kw.toFixed(2) + ' kW (medie 1h)';
    }
    // Fallback for the brief window before the first hourly_avg fetch.
    let sum = 0;
    let has = false;
    (pl.equipment || []).forEach((e: UtilajStructura) => {
        const a = getHourlyAvgPower(e.id);
        const p = a ?? getSensorValue(e.id, 'power') ?? getSensorValue(e.id, 'consum');
        const mp = e.manual_power_kw;
        if (p != null && !isNaN(Number(p))) {
            sum += parseFloat(String(p));
            has = true;
        } else if (mp != null && mp > 0) {
            sum += parseFloat(String(mp));
            has = true;
        }
    });
    return has ? sum.toFixed(2) + ' kW (medie 1h)' : '— kW';
}

export async function loadOverview(): Promise<void> {
    const grid = document.getElementById('machine-grid') as HTMLElement | null;
    const statsEl = document.getElementById('stats-cards') as HTMLElement | null;
    try {
        const results = await Promise.all([
            api('/api/factory_structure').catch(() => null),
            api('/api/stats').catch(() => ({ parts: 0, errors: 0, intervals: 0, documents: 0 })),
            api('/api/maintenance_intervals').catch(() => []),
            loadSensorConfigs().catch(() => []),
            loadMoldCounters().catch(() => ({})),
            api('/api/lines/status').catch(() => ({ lines: [] })),
            // Part 2 (2026-05-15): pauză tehnică per mașină (offline_since > prag)
            api('/api/machines/operational-state/all').catch(() => []),
            // Part 3 (2026-05-15): GO-LIVE audit (linii inactive M2/M9, mașini cu/fără ore)
            api('/api/golive/audit').catch(() => null),
        ]);
        const factory: StructuraFabrica | null = results[0];
        const stats: Statistici = results[1] && typeof results[1] === 'object' ? results[1] : { parts: 0, errors: 0, intervals: 0, documents: 0 };
        const intervals: IntervalCuStare[] = Array.isArray(results[2]) ? results[2] : [];
        // ── P1: status live per linie (running_state real, power live, heartbeat colector) ──
        // Cache într-un map { lineId -> LineStatusRow } folosit la stats (Linii Active)
        // și la badge per-line (sincronizare manual + live).
        const linesStatusArr: StareLinie[] = (results[5] && Array.isArray(results[5].lines)) ? results[5].lines : [];
        const linesStatusMap: Record<string, StareLinie> = {};
        linesStatusArr.forEach(ls => { if (ls && ls.id) linesStatusMap[ls.id] = ls; });
        (state as OverviewState).linesStatusMap = linesStatusMap;

        // Part 2: map machine_id → operational state (is_technical_pause, offline_since)
        const opStateArr: StareOperare[] = Array.isArray(results[6]) ? results[6] : [];
        const opStateMap: Record<string, StareOperare> = {};
        opStateArr.forEach(s => { if (s && s.machine_id) opStateMap[s.machine_id] = s; });
        (state as OverviewState).opStateMap = opStateMap;

        // Part 3 (2026-05-15): GO-LIVE audit → maps machine_id → status, line_id → is_active
        const goliveAudit: AuditGolive | null = results[7] && typeof results[7] === 'object' ? results[7] : null;
        const goliveMachineMap: Record<string, GoliveUtilaj> = {};
        const goliveLineMap: Record<string, GoliveLinie> = {};
        if (goliveAudit && Array.isArray(goliveAudit.machines)) {
            goliveAudit.machines.forEach((m: GoliveUtilaj) => { if (m && m.id) goliveMachineMap[m.id] = m; });
        }
        if (goliveAudit && Array.isArray(goliveAudit.lines)) {
            goliveAudit.lines.forEach((l: GoliveLinie) => { if (l && l.id) goliveLineMap[l.id] = l; });
        }
        (state as OverviewState).goliveMachineMap = goliveMachineMap;
        (state as OverviewState).goliveLineMap = goliveLineMap;

        if (!factory || !factory.production_lines || !Array.isArray(factory.production_lines)) {
            if (grid) grid.innerHTML = '<div class="alarm-empty"><span class="icon">⚠</span>Structură fabrică invalidă sau server indisponibil. Verifică conexiunea. <button type="button" class="btn btn-sm" onclick="loadOverview()">Reîncearcă</button></div>';
            if (statsEl) statsEl.innerHTML = '<div class="stat-card"><div class="label">Eroare</div><div class="value">—</div></div>';
            return;
        }

        // AppState.machines e tipat `Machine` (types.ts); aici intră mașinile principale din /api/factory_structure
        // (null la linia fără mașină principală). Nu le citește nimeni.
        state.machines = factory.production_lines.map((pl: LinieStructura) => pl.main_machine) as Machine[];

        const allEqIds: string[] = [];
        const eqTypes = {};
        ost.activeMoldIds = new Set();
        factory.production_lines.forEach((pl: LinieStructura) => {
            // Sincronizare ore din DB → state.machineHours (sursa de adevăr pentru alerte)
            // Pentru mașina principală: cheia e pl.id (linia) ȘI pl.main_machine.id
            const mainM = pl.main_machine;
            if (mainM && mainM.operating_hours != null) {
                const h = parseFloat(mainM.operating_hours) || 0;
                state.machineHours[pl.id] = h;
                state.machineHours[mainM.id] = h;
            }
            (pl.equipment || []).forEach((eq: UtilajStructura) => {
                if (!allEqIds.includes(eq.id)) { allEqIds.push(eq.id); (eqTypes as Record<string, string | null>)[eq.id] = eq.machine_type; }
                if (eq.machine_type === 'mold' && eq.is_active) ost.activeMoldIds!.add(eq.id);
                // Sincronizare ore pentru TOATE echipamentele (matrițe + auxiliare)
                // → necesar ca computeAlerts să poată calcula scadențele lor
                if (eq.operating_hours != null) {
                    state.machineHours[eq.id] = parseFloat(eq.operating_hours) || 0;
                }
                const c = eq.consumption;
                if (c != null && (c as number | string) !== '' && !isNaN(parseFloat(c))) {   // vine număr: '' nu apare
                    state.consumptionHistory[eq.id] = state.consumptionHistory[eq.id] || [];
                    state.consumptionHistory[eq.id].push(parseFloat(c));
                    if (state.consumptionHistory[eq.id].length > 60) state.consumptionHistory[eq.id].shift();
                }
            });
        });
        // Sincronizare ore pentru utilități transversale (UTIL_AER: compresoare, uscător, rezervor)
        Object.values(factory.utilities || {}).forEach(u => {
            (u.machines || []).forEach((m: UtilajStructura) => {
                if (!allEqIds.includes(m.id)) { allEqIds.push(m.id); (eqTypes as Record<string, string | null>)[m.id] = m.machine_type; }
                if (m.operating_hours != null) {
                    state.machineHours[m.id] = parseFloat(m.operating_hours) || 0;
                }
            });
        });
        state.allEquipmentIds = allEqIds;
        state.equipmentTypes = eqTypes;
        try { localStorage.setItem('machineHours', JSON.stringify(state.machineHours)); } catch(e) {}

        const alerts = computeAlerts(intervals);
        state.allAlerts = alerts;

        const intervalsMap: Record<string, IntervalCuStare> = {};
        intervals.forEach((iv: IntervalCuStare) => {
            if (iv.machine_id && iv.id != null) intervalsMap[`${iv.machine_id}:${iv.id}`] = iv;
        });
        (state as OverviewState).maintenanceIntervalsMap = intervalsMap;

        refreshSensorAlarmsFromReadings();

        let openCases: RandDiagnosticCases[] = [];
        try {
            const ocRes: { data: RandDiagnosticCases[] } = await api('/api/diagnostic_cases?open=1&limit=50');
            openCases = (ocRes && ocRes.data) || [];
        } catch (_) { openCases = []; }
        state.openCaseAlerts = openCases.map((c: RandDiagnosticCases) => ({
            id: 'diagcase:' + c.id,
            machine_id: c.machine_id || '—',
            action: `Caz de rezolvat: ${(c.symptom || 'Simptom')}${c.cause_found ? ' — ' + (c.cause_found.length > 40 ? c.cause_found.slice(0, 40) + '…' : c.cause_found) : ''}`,
            severity: 'open_case',
            outcome: c.outcome,
            caseId: c.id
        }));

    const combinedAlerts = [...(state.controllerAlarms || []), ...state.allAlerts, ...(state.sensorAlarms || []), ...(state.openCaseAlerts || [])];
    const unackedCount = combinedAlerts.filter(a => {
        if (a.source === 'controller') return !a.acked;
        return !state.ackedAlarms.includes(a.id);
    }).length;
    updateAlarmBadge(unackedCount);

    const alertsByMachine = {};
    alerts.forEach(a => { ((alertsByMachine as Record<string, Alert[]>)[a.machine_id] ||= []).push(a); });
    const intervalsByMachine = {};
    // machine_id null (coloana îl permite, azi n-are niciunul) → cheia 'null'
    intervals.forEach(iv => { ((intervalsByMachine as Record<string, IntervalCuStare[]>)[iv.machine_id as string] ||= []).push(iv); });

    // P1.1: Linii Active = running_state real din /api/lines/status (nu state local)
    // Filtrăm doar liniile de producție (group_type !== 'utility') ca să corespundă cu numitorul.
    const productionLineIds = new Set(factory.production_lines.map((pl: LinieStructura) => pl.id));
    const runningCount = linesStatusArr.filter(ls =>
        productionLineIds.has(ls.id) && ls.running_state === 'running'
    ).length;
    // Numitorul număra și liniile dezafectate (M2 și M9, `is_active=0`), deci
    // primul indicator din administrare arăta „4/10" în loc de „4/8" — audit,
    // capitolul de date greșite. O linie nepornită nu poate fi „activă", deci
    // n-are ce căuta nici în numitor.
    const totalProductionLines = factory.production_lines.filter(
        (pl: LinieStructura) => pl.is_active === undefined || pl.is_active === 1 || (pl.is_active as number | boolean) === true).length;   // vine int(is_active): `true` nu apare
    const linesActiveCls = runningCount === 0 ? 'red' : (runningCount < totalProductionLines ? 'orange' : 'green');

    const partsNum = typeof stats.parts === 'number' ? stats.parts : parseInt(stats.parts, 10) || 0;
    const docsNum = typeof stats.documents === 'number' ? stats.documents : parseInt(stats.documents, 10) || 0;
    const intervalsNum = typeof stats.intervals === 'number' ? stats.intervals : parseInt(stats.intervals, 10) || (intervals && intervals.length) || 0;
    // 2026-07-05 (feedback Dan): "Piese în DB" inducea în eroare (părea stoc,
    // era numărul de piese extrase din manuale) → înlocuit cu "De rezolvat":
    // golurile de configurare care cer acțiune manuală (se golește pe măsură
    // ce sunt rezolvate). Click → modal cu categorii + utilaje.
    if (statsEl) statsEl.innerHTML = `
        <div class="stat-card ${linesActiveCls}"><div class="label">Linii Active</div><div class="value">${runningCount}/${totalProductionLines}</div></div>
        <div class="stat-card"><div class="label">Echipamente</div><div class="value">${factory.total_machines != null ? factory.total_machines : '—'}</div></div>
        <div class="stat-card" id="setup-todo-card" style="cursor:pointer" title="Configurări și date lipsă care cer acțiune manuală — click pentru listă"><div class="label">🛠 De rezolvat</div><div class="value" id="setup-todo-count">…</div></div>
        <div class="stat-card orange" title="Numărul de documente tehnice procesate (manuale, cataloage piese, fișe service)"><div class="label">Documente</div><div class="value">${docsNum}</div></div>
        ${(() => {
            // Audit, capitolul de date greșite: cifra asta (187) și cardurile de sub
            // ea (155 restanțe) apăreau pe același ecran și păreau că se contrazic.
            // Nu măsurau același lucru: aici intră și lucrările care ABIA URMEAZĂ
            // (sub 25% din interval rămas), acolo doar cele depășite. Le arătăm
            // despărțit, ca omul să știe ce citește.
            const _depasite = alerts.filter((a: Alert) => a.severity === 'critical').length;
            const _vin = alerts.length - _depasite;
            const _cazuri = (state.openCaseAlerts || []).length;
            const _tot = alerts.length + _cazuri;
            const _detaliu = [
                _depasite ? `${_depasite} depășite` : '',
                _vin ? `${_vin} vin în curând` : '',
                _cazuri ? `${_cazuri} cazuri deschise` : '',
            ].filter(Boolean).join(' · ');
            return `<div class="stat-card ${_tot > 0 ? 'red' : ''}" title="Depășite = intervalul e consumat. Vin în curând = sub un sfert din interval rămas.">
                <div class="label">Alerte Mentenanță</div>
                <div class="value">${_tot}</div>
                ${_detaliu ? `<div class="stat-sub" style="font-size:.7rem;opacity:.8">${_detaliu}</div>` : ''}
            </div>`;
        })()}
        <div class="stat-card"><div class="label">Intervale</div><div class="value">${intervalsNum}</div></div>
    `;
    void partsNum; // păstrat în /api/stats; nu se mai afișează aici
    _refreshSetupTodoCard();
    document.getElementById('setup-todo-card')?.addEventListener('click', () => openSetupTodoModal());

    if (grid) grid.innerHTML = '';

    // Faza B2b (iul 2026): cardul unificat (identic cu Dashboard) și în
    // Administrare; blocul admin legacy (ore, GO-LIVE, matrițe, utilaje,
    // ștergere) devine zona pliabilă „⚙ Configurare" de sub card.
    let _ovPanelMap: Record<string, PanelLine> = {};
    try {
        const _ovPanel = await fetchLinesPanel();
        (_ovPanel?.lines || []).forEach((l: PanelLine) => { _ovPanelMap[l.id] = l; });
    } catch (e) {
        console.warn('[loadOverview] /api/lines/panel indisponibil — carduri legacy', e);
    }

    factory.production_lines.forEach((pl: LinieStructura) => {
        // Mașina principală a liniei (obiect cu id/name/operating_hours/etc.).
        // Bug fix 2026-05-11: era referit ca `mainM` direct la linia 1331 fără
        // a fi declarat în acest scope, rezultat ReferenceError la runtime
        // ("mainM is not defined") care întrerupea construirea cardurilor
        // pentru linii — Overview Fabrică rămânea gol.
        const mainM = pl.main_machine;
        // 2026-05-16: line hours decoupled from the main machine. Prefer the
        // line-level value when the API provides it (always present after the
        // 2026-05-16 backfill); fall back to the main-machine hours map only
        // for very-old browser caches that haven't fetched the new payload.
        const _mainMachHours = state.machineHours[pl.id] || 0;
        const _lineHoursRaw = pl.operating_hours;
        const hours = (typeof _lineHoursRaw === 'number')
            ? _lineHoursRaw
            : _mainMachHours;
        // Source semantics:
        // - if line is 'manual' → MANUAL badge (master typed a value here).
        // - if line is 'main_machine' (default) → show the underlying main
        //   machine's source badge (EM63/SFM/PAC3220/MANUAL/MOȘTENIT) so the
        //   technician still sees where the number actually came from.
        const _lineHoursSource = (pl.operating_hours_source || 'main_machine') as string;
        const _mainMachSource = (mainM && mainM.auto_hours_source) ? String(mainM.auto_hours_source) : '';
        const _hoursSource = _lineHoursSource === 'manual' ? 'manual'
            : _lineHoursSource === 'aggregate' ? 'aggregate'
            : _mainMachSource;
        let _hoursBadge = '';
        if (_lineHoursSource === 'manual') {
            const _setAt = pl.manual_hours_set_at || '';
            _hoursBadge = `<span class="hours-source-badge manual" title="Ore linie setate manual${_setAt ? ' la ' + escHtml(String(_setAt).replace('T',' ').slice(0,16)) : ''} (decuplate de mașina principală)">MANUAL</span>`;
        }
        else if (_hoursSource === 'contor_masina') _hoursBadge = '<span class="hours-source-badge auto" title="Ore citite de pe contorul mașinii principale (motoare pornite), prin EUROMAP 63">CONTOR MAȘINĂ</span>';
        else if (_hoursSource === 'em63') _hoursBadge = '<span class="hours-source-badge auto" title="Ore calculate din contoare EUROMAP 63 (cycle_count × cycle_time)">EM63</span>';
        else if (_hoursSource === 'sfm12ld') _hoursBadge = '<span class="hours-source-badge auto" title="Ore citite direct de la SFM12LD">SFM</span>';
        else if (_hoursSource === 'pac3220') _hoursBadge = '<span class="hours-source-badge auto" title="Ore acumulate când contorul PAC3220 măsoară putere > prag">PAC3220</span>';
        else if (_hoursSource === 'manual') _hoursBadge = '<span class="hours-source-badge manual" title="Valoare introdusă manual în UI">MANUAL</span>';
        else if (_hoursSource === 'inherited') _hoursBadge = '<span class="hours-source-badge inherited" title="Moștenit de la utilajele liniei">MOȘTENIT</span>';
        const stManual = state.machineStatus[pl.id] || 'ok';
        // P1.4: combinăm status manual cu running_state din /api/lines/status.
        // Dacă colectorul raportează linia ca oprită (running_state==='stopped'), forțăm OPRITĂ
        // indiferent de status manual — realitatea live câștigă.
        const liveSt = linesStatusMap[pl.id];
        let st = stManual;
        if (liveSt && liveSt.running_state === 'stopped') st = 'stopped';

        // Part 2 (2026-05-15) → 2026-05-24 — Four-state badges on line header.
        //   - 'active'           → no extra badge (status pill says ACTIV)
        //   - 'maintenance'      → 🔧 MENTENANȚĂ ÎN CURS · X zile (workflow #N)
        //   - 'technical_pause'  → ⏸ PAUZĂ TEHNICĂ · X zile + ❄ Ore înghețate de DATA
        //   - 'inactive'         → handled in the GO-LIVE block below (⚠ LINIE NEPORNITĂ)
        // Source of truth: `/api/machines/operational-state/all` payload field
        // `maintenance_state` (computed by services/technical_pause.py).
        const _opState = mainM ? opStateMap[mainM.id] : null;
        const _mState: string = (_opState && _opState.maintenance_state) || 'active';
        const _isTechPause = _mState === 'technical_pause';
        const _isMaintenance = _mState === 'maintenance';
        let _techPauseBadge = '';
        let _frozenHoursBadge = '';
        let _maintBadge = '';
        if (_opState && (_isTechPause || _isMaintenance)) {
            const _offlineSince = _opState.offline_since || '';
            const _days = Math.floor((_opState.offline_duration_hours || 0) / 24);
            const _dayStr = _days > 0 ? `${_days} ${_days === 1 ? 'zi' : 'zile'}` : `${Math.round(_opState.offline_duration_hours)} h`;
            const _shortDate = _offlineSince ? _offlineSince.replace('T', ' ').slice(0, 10) : '';
            if (_isMaintenance) {
                const _wfId = _opState.workflow_id;
                const _flag = !!_opState.flagged_by_user;
                const _reason = _opState.flag_reason || '';
                const _label = _wfId ? `🔧 MENTENANȚĂ ÎN CURS · Workflow #${_wfId}` :
                               _flag ? `🔧 MENTENANȚĂ ÎN CURS · marcată manual` :
                                       `🔧 MENTENANȚĂ ÎN CURS`;
                const _maintTip = (_reason ? `Motiv: ${_reason}\n` : '') +
                    `Linia oprită din ${_offlineSince.replace('T', ' ').slice(0, 16)} (${_dayStr}). ` +
                    `Alarmele normale sunt ascunse; doar criticele apar ca sugestii „cât ești oprit".`;
                _maintBadge = `<span class="badge-maintenance" title="${escHtml(_maintTip)}">${_label} · ${escHtml(_dayStr)}</span>`;
            } else {
                const _tooltip = `Linie oprită din ${_offlineSince.replace('T', ' ').slice(0, 16)} (${_dayStr}). Mentenanțele calendaristice sunt sărite automat.`;
                _techPauseBadge = `<span class="badge-technical-pause" title="${escHtml(_tooltip)}">⏸ PAUZĂ TEHNICĂ · ${escHtml(_dayStr)}</span>`;
            }
            // Frozen-hours badge is shown for BOTH 'technical_pause' and 'maintenance'
            // — the line clock freezes in either case (see line_hours_propagator.py).
            const _frozenTip = _shortDate
                ? `Orele liniei nu mai cresc din ${_shortDate} (offline > ${_opState.technical_pause_threshold_hours || 8}h). Alarmele nu mai avansează spre scadență cât timp linia e oprită.`
                : 'Orele liniei sunt înghețate cât timp linia e oprită.';
            _frozenHoursBadge = `<span class="badge-frozen-hours" title="${escHtml(_frozenTip)}">❄ Ore înghețate${_shortDate ? ' de ' + escHtml(_shortDate) : ''}</span>`;
            if (st === 'ok') st = 'stopped';
        }
        const stLabel = st === 'warning' ? 'ATENȚIE' : st === 'stopped' ? 'OPRITĂ' : 'ACTIV';

        const lineIntervals = shouldSuppressLineMaintenanceAlerts(pl)
            ? []
            : ((intervalsByMachine as Record<string, IntervalCuStare[]>)[pl.id] || []).filter(shouldShowMaintenanceInterval as (iv: IntervalCuStare) => boolean);   // vezi IvVizibil
        // P1.2: gauge sănătate doar când avem date reale (intervale + ore funcționare).
        // Altfel returnăm null și ascundem gauge-ul, ca să nu afișăm 100% artificial.
        let healthPct: number | null = null;
        // 1 nov 2026 (auditul surselor de ore): din starea de pe server — înainte pe orele LINIEI, nu ale mașinii
        if (lineIntervals.length > 0 && hours > 0) {
            let worstRatio = 0, cuStare = 0;
            lineIntervals.forEach((iv: IntervalCuStare) => {
                const st = iv.stare;
                if (!st || (st.mod !== 'ore' && st.mod !== 'calendar')) return;
                cuStare++;
                worstRatio = Math.max(worstRatio, st.depasit ? 1 : Math.min(1, (st.procent ?? 0) / 100));
            });
            if (cuStare) healthPct = Math.round((1 - worstRatio) * 100);
        }
        const healthColor = healthPct == null ? '#6b7280' : healthPct > 60 ? '#00e676' : healthPct > 30 ? '#ffaa00' : '#ff3d3d';

        const lineAlerts = filterMaintenanceAlertsForLine(state.allAlerts, pl, pl);
        const alertsHtml = buildOverviewLineAlertsHtml(pl, lineAlerts);

        const injectionMachines = (pl.equipment || []).filter((e: UtilajStructura) => e.machine_type === 'injection' || e.machine_type === 'compression');
        let activeMoldsHtml = '';
        injectionMachines.forEach((inj: UtilajStructura) => {
            const moldsOnLine = (pl.equipment || []).filter(
                (e: UtilajStructura) => e.machine_type === 'mold' && (e.linked_machine_id || '').trim() === inj.id
            );
            if (moldsOnLine.length === 0) return;
            const currentActive = inj.active_mold_id || '';
            let opts = '<option value="">— niciuna —</option>';
            moldsOnLine.forEach((m: UtilajStructura) => {
                opts += `<option value="${escHtml(m.id)}" ${m.id === currentActive ? 'selected' : ''}>${escHtml(m.name || m.id)}</option>`;
            });
            activeMoldsHtml += `<div class="active-mold-row"><label>Matriță activă ${inj.id}:</label><select class="active-mold-select" data-machine="${escHtml(inj.id)}">${opts}</select></div>`;
        });

        const powerStr = _linePowerSummary(pl);
        const bucRate =
            pl.production_rate != null && pl.production_rate > 0 ? Math.round(pl.production_rate) + ' buc/h' : '— buc/h';

        // P4.1: cardul mare consolidat — tot ce era în #ov-line-tools intră acum în header.
        // Pentru master: butoane edit ore + status manual + șterge linia direct în header.
        // Pentru operator: doar info (gauge, ore, status badge), header click → openLineDetail.
        const eqCount = (pl.equipment || []).length;
        const stClass = st === 'stopped' ? 'stopped' : st === 'warning' ? 'warning' : 'ok';
        const gaugeHtml = healthPct != null
            ? buildGaugeSVG(healthPct, 'SĂNĂT.', healthColor)
            : `<span class="line-no-health" title="Sănătatea va fi calculată după ce înregistrezi ore funcționare și sunt definite intervale">— sănăt.</span>`;

        const isMaster = state.currentUser.role === 'master';
        const userRoleHere = state.currentUser?.role || 'operator';
        const canStartLine = (userRoleHere === 'engineer' || userRoleHere === 'master');
        // Bug fix 2026-05-09: data-machine trebuie să fie ID-ul mașinii principale (mainM.id),
        // NU al liniei (pl.id). Ex: pentru linia "M1" mașina principală e "IMM M1 CAPAC 48".
        // Pentru SACMI M3/M4 e identic accidental (line id = machine id) — pentru restul nu era.
        const mainMachineId = (mainM && mainM.id) ? mainM.id : pl.id;

        // Part 3 (2026-05-15) — GO-LIVE / Linie inactivă
        // Sursa de adevăr: state.goliveLineMap[pl.id] (din /api/golive/audit).
        // Trei stări vizuale:
        //   1. is_active=0  → badge ⚠ LINIE NEPORNITĂ + buton ▶ Start Linie (engineer/master)
        //   2. is_active=1, started_at!=null → badge 🟢 GO-LIVE 15 mai 2026
        //   3. is_active=1, started_at=null  → badge ⚠ Pre-GO-LIVE (legacy fără start oficial)
        const _glLine = goliveLineMap[pl.id];
        let _goliveLineBadge = '';
        let _startLineBtn = '';
        if (_glLine) {
            if (_glLine.is_active === 0) {
                _goliveLineBadge = `<span class="golive-badge inactive" title="Linia ${escHtml(pl.id)} NU este pornită oficial. Apasă butonul ▶ Start Linie pentru a o activa.">⚠ LINIE NEPORNITĂ</span>`;
                if (canStartLine) {
                    _startLineBtn = `<button type="button" class="btn-start-line" data-line-id="${escHtml(pl.id)}" title="Pornește oficial linia ${escHtml(pl.id)}">▶ Start Linie</button>`;
                }
            } else if (_glLine.started_at) {
                const _stShort = String(_glLine.started_at).slice(0, 10);
                _goliveLineBadge = `<span class="golive-badge live" title="Linie pornită oficial pe ${escHtml(_glLine.started_at)} (${_glLine.machines_live}/${_glLine.machines_count} utilaje LIVE)">🟢 GO-LIVE ${escHtml(_stShort)}</span>`;
            } else {
                _goliveLineBadge = `<span class="golive-badge pregolive" title="Linie activă (legacy) — fără start oficial înregistrat">⚠ Pre-GO-LIVE</span>`;
            }
        }
        const toolsHtml = isMaster ? `
            <div class="line-tools" data-line-id="${pl.id}">
                <div class="line-tools-hours" title="Editează orele de funcționare ale mașinii principale (${escHtml(mainMachineId)})">
                    <input type="number" min="0" step="0.1" value="${hours}" data-machine="${escHtml(mainMachineId)}" data-line="${pl.id}" class="hours-input" aria-label="Ore funcționare ${pl.id}">
                    <button type="button" class="btn-mini btn-green save-hours" data-machine="${escHtml(mainMachineId)}" data-line="${pl.id}" title="Salvează ore pe mașina principală">✓</button>
                </div>
                <div class="mc-status-btns line-tools-status">
                    <button type="button" class="s-ok ${stManual === 'ok' ? 'active' : ''}" data-machine="${pl.id}" data-st="ok" title="Marchează OK manual">OK</button>
                    <button type="button" class="s-warn ${stManual === 'warning' ? 'active' : ''}" data-machine="${pl.id}" data-st="warning" title="Marchează ATENȚIE manual">⚠</button>
                    <button type="button" class="s-stop ${stManual === 'stopped' ? 'active' : ''}" data-machine="${pl.id}" data-st="stopped" title="Marchează STOP manual">⏹</button>
                </div>
                <button type="button" class="btn-mini btn-red btn-delete-line" data-line="${pl.id}" title="Șterge linia (necesită eliberare utilaje)">🗑</button>
            </div>` : '';

        let equipHtml = '';
        (pl.equipment || []).forEach((eq: UtilajStructura) => { equipHtml += buildEquipCard(eq, eq.id === pl.main_machine?.id); });

        const section = document.createElement('div');
        section.className = 'line-group';
        section.dataset.lineId = pl.id;
        const _legacyAdminHtml = `
            <div class="line-group-header" data-line-id="${pl.id}">
                <div class="line-id-block">
                    <span class="line-id">${escHtml(pl.id)}</span>
                    <span class="line-product-inline">${escHtml(pl.product || '—')}</span>
                    <span class="eq-count-badge">${eqCount} utilaje</span>
                </div>
                <div class="line-header-right">
                    ${gaugeHtml}
                    <span class="line-hours-display" title="${_lineHoursSource === 'manual'
                        ? 'Ore funcționare LINIE (manual, decuplate de mașina principală)' + (pl.manual_hours_set_at ? ' — setat ' + escHtml(String(pl.manual_hours_set_at).replace('T',' ').slice(0,16)) : '')
                        : 'Ore funcționare LINIE (auto, sincron cu mașina principală — sursă: ' + escHtml(_hoursSource || 'nesetat') + ')'}">⏱ ${hours.toFixed(1)} h ${_hoursBadge}</span>
                    <span class="mc-status ${stClass}">${stLabel}</span>
                    ${_maintBadge}
                    ${_techPauseBadge}
                    ${_frozenHoursBadge}
                    ${_goliveLineBadge}
                    ${_startLineBtn}
                    ${toolsHtml}
                </div>
            </div>
            ${alertsHtml}
            ${activeMoldsHtml ? `<div class="active-molds-row">${activeMoldsHtml}</div>` : ''}
            <div class="equip-grid">${equipHtml}</div>
        `;
        const _plPanel = _ovPanelMap[pl.id];
        section.innerHTML = _plPanel
            ? (temaD() ? buildLineCardD(_plPanel, { admin: true }) : buildLineCardHtml(_plPanel, { admin: true }))
              + `<div class="lp-admin-zone" style="display:none">${_legacyAdminHtml}</div>`
            : _legacyAdminHtml;
        // 2026-05-16: click pe header deschide noul modal "Sumar Linie"
        // (compact, status + ore linie decuplate + listă utilaje), NU modalul
        // mașinii principale. Pentru detaliul mașinii principale, există
        // butonul "⭐ Mașina principală" în footer-ul modalului Sumar Linie.
        // PĂSTRĂM exclude-area .line-tools (controale master) și extindem la
        // butoane/inputs ca să nu se deschidă din greșeală.
        section.querySelector('.line-group-header')?.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.line-tools')) return;
            // Nu deschide modalul când click-ul a fost pe un control interactiv
            // din header (start-line, butoane, input-uri de ore, etc.).
            if (target.closest('button, input, select, a, .btn-start-line, .btn-delete-line')) return;
            try {
                (window as FereastraOverview).openLineSummary
                    ? (window as FereastraOverview).openLineSummary!(pl.id)
                    : openLineSummary(pl.id);
            } catch (_) {
                // Defensive fallback to the legacy main-machine modal in case
                // the new modal isn't loaded for some reason.
                const role = state.currentUser?.role || 'operator';
                openLineDetail(pl.id, pl.product || '', role);
            }
        });
        grid!.appendChild(section);
    });

    // Acțiunile cardului unificat (chips, semnal, ⚙) — delegare pe grid.
    if (grid) wireLinePanelContainer(grid);
    if (grid && temaD()) incarcaPozeD(grid);


    Object.entries(factory.utilities || {}).forEach(([uKey, util]: [string, GrupUtilitati]) => {
        if (!util || !util.machines || util.machines.length === 0) return;
        const section = document.createElement('div');
        section.className = 'line-group';
        let equipHtml = '';
        util.machines.forEach((m: UtilajStructura) => { equipHtml += buildEquipCard(m, false); });
        section.innerHTML = `
            <div class="line-group-header">
                <div class="line-id-block">
                    <span class="line-id">${util.icon || '⚙'}</span>
                    <span class="line-product-inline">${(util.label || uKey).toUpperCase()}</span>
                    <span class="eq-count-badge">${util.machines.length}</span>
                </div>
            </div>
            <div class="equip-grid">${equipHtml}</div>
        `;
        grid!.appendChild(section);
    });

    const thresholdsDiv = document.createElement('div');
    thresholdsDiv.className = 'overview-group';
    thresholdsDiv.innerHTML = `<h3 class="group-title">PRAGURI ALARME SENZORI</h3>`;
    const thGrid = document.createElement('div');
    thGrid.className = 'threshold-grid';
    const typeGroups = {};
    Object.entries(state.sensorConfigs || {}).forEach(([mid, cfgs]) => {
        const mtype = state.equipmentTypes![mid] || 'other';
        if (!(typeGroups as Record<string, SensorConfig[]>)[mtype]) (typeGroups as Record<string, SensorConfig[]>)[mtype] = cfgs;
    });
    const thLabels = { injection:'⚙️ Injecție', compression:'🔄 Compresie', compressor:'💨 Compresor', chiller:'❄️ Chiller', cooler:'🌡️ Răcitor', auxiliary:'⚙️ Auxiliar', inspection:'👁️ Inspecție', control:'🖥️ Control', tampoprint:'🖨️ Tampo', dryer:'🔥 Uscător', reservoir:'🛢️ Rezervor' };
    Object.entries(typeGroups).forEach(([mtype, cfgs]) => {
        let rows = '';
        (cfgs as SensorCfg[]).forEach((cfg: SensorCfg) => {
            if (!cfg.enabled) return;
            rows += `<div class="th-row"><span class="th-sensor">${escHtml(cfg.label.substring(0,8))}</span><span class="th-warn">⚠${cfg.warn_threshold}${cfg.unit}</span><span class="th-crit">🔴${cfg.crit_threshold}${cfg.unit}</span></div>`;
        });
        thGrid.innerHTML += `<div class="th-card"><div class="th-type">${(thLabels as Record<string, string>)[mtype]||mtype}</div>${rows}</div>`;
    });
    thresholdsDiv.appendChild(thGrid);
    grid!.appendChild(thresholdsDiv);

    const configBar = document.createElement('div');
    configBar.className = 'overview-config-bar';
    if (state.currentUser.role === 'master') {
        configBar.innerHTML = `
            <button class="btn-config" id="btn-open-add-line">＋ Adaugă Linie</button>
            <button class="btn-config orange" id="btn-open-add-equip">＋ Adaugă Utilaj</button>
            <button class="btn-config" id="btn-open-shared-sensors">📡 Senzori comuni</button>
            <button class="btn-config" id="btn-open-counters">🔌 Contoare</button>
        `;
        grid!.insertBefore(configBar, grid!.firstChild);
    }

    $$('.save-hours').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const mid = btn.dataset.machine;
            const lineId = (btn as HTMLElement).dataset.line || mid;
            if (!mid) { toast('Eroare: lipsește ID mașină principală'); return; }
            const input = $(`.hours-input[data-machine="${mid}"]`);
            const val = parseFloat((input as HTMLInputElement).value);
            if (isNaN(val) || val < 0) { toast('Valoare invalidă'); return; }
            // Refactor 2026-05-15: folosim endpoint-ul dedicat care:
            //  1) Setează operating_hours + operating_hours_offset (audit baseline)
            //  2) Setează manual_hours_set_at = NOW() + auto_hours_source = 'manual'
            //  3) Setează last_running_check = NOW() (delta începe de aici)
            //  4) Insert în operating_hours_history (audit trail)
            //  5) Răspunde sincron (NU mai e queue in-memory pierdut la restart)
            // Anterior se folosea /api/config/machines/{id}/counters care queue-uia într-un
            // buffer in-memory + NU seta offset → telemetria putea suprascrie valoarea.
            try {
                const r = await fetch(`/api/machines/${encodeURIComponent(mid)}/operating_hours`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ operating_hours: val, reset_tracker: true, source: 'manual' }),
                });
                if (!r.ok) {
                    const err = await r.text();
                    toast(`Eroare salvare: ${err.slice(0, 100)}`);
                    return;
                }
                state.machineHours[mid] = val;
                state.machineHours[lineId!] = val;
                try { localStorage.setItem('machineHours', JSON.stringify(state.machineHours)); } catch(e) {}
                recordHoursHistory(mid, val);
                toast(`Ore salvate pe ${mid}: ${val.toFixed(1)} h (manual)`);
                loadOverview();
            } catch (err) {
                toast(`Eroare rețea: ${err}`);
            }
        });
    });
    $$('.mc-status-btns button').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            state.machineStatus[btn.dataset.machine!] = btn.dataset.st as MachineStatus;   // ok / warning / stopped din butoane
            localStorage.setItem('machineStatus', JSON.stringify(state.machineStatus));
            loadOverview();
        });
    });
    $$('.hours-input').forEach(inp => { inp.addEventListener('click', e => e.stopPropagation()); });

    // Handler pentru pictograme ✏ pe carduri matrițe + auxiliare (editare cycles/hours/nominal)
    $$('.mold-edit-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const eqId = (btn as HTMLElement).dataset.eqId;
            const editType = (btn as HTMLElement).dataset.edit;
            if (!eqId || !editType) return;
            await _openEquipmentEditModal(eqId, editType);
        });
    });

    $$('.equip-card').forEach(card => {
        card.style.cursor = 'pointer';
        card.addEventListener('click', (e) => {
            if ((e.target as HTMLElement).closest('.eq-actions') || (e.target as HTMLElement).closest('.eq-status-btns')) return;
            openLineDetail(card.dataset.eq, card.dataset.eqName);
        });
    });

    $$('.edit-eq-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const card = btn.closest('.equip-card');
            const lineGroup = card!.closest('[data-line-id]');
            const fromLineId = lineGroup ? (lineGroup as HTMLElement).dataset.lineId : '';
            try { openEditEquipModal(JSON.parse((card as HTMLElement).dataset.eqJson!), fromLineId!); } catch(err) { toast('Eroare la deschidere'); }
        });
    });

    $$('.eqs-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const eqId = btn.dataset.eq;
            const st = btn.dataset.st;
            let note = '';
            if (st !== 'ok') {
                const raspuns = await askText(`${eqId} — ${st === 'stopped' ? 'OPRIT' : 'ATENȚIE'}`, { label: 'Notă (opțional):' });
                if (raspuns === null) return;
                note = raspuns;
            }
            /* Rezultatul salvării nu era verificat deloc: starea se scria întâi
               local, cererea pleca într-un `try/catch` gol, iar `loadOverview()`
               repicta apoi de pe server. Dacă serverul refuza (utilaj inexistent,
               drepturi, rețea), cardul clipea pe starea nouă și revenea singur —
               fără niciun mesaj. Omul rămânea convins că a marcat mașina. */
            try {
                const res = await fetch(`/api/config/machines/${encodeURIComponent(eqId!)}/status`, {
                    method: 'PUT', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: st, status_note: note }),
                });
                if (!res.ok) {
                    const d: RaspunsScriere = await res.json().catch(() => ({}));
                    toast(d.error || `Nu s-a putut salva starea pentru ${eqId} (${res.status})`, 'error');
                    return;
                }
            } catch (err) {
                toast('Eroare de rețea la salvarea stării: ' + ((err as Error)?.message || String(err)), 'error');
                return;
            }
            state.machineStatus[eqId!] = st as MachineStatus;   // ok / warning / stopped din butoane
            localStorage.setItem('machineStatus', JSON.stringify(state.machineStatus));
            loadOverview();
        });
    });

    $$('.btn-delete-line').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const lid = btn.dataset.line;
            if (!lid) return;
            if (state.currentUser.role !== 'master') {
                toast('Doar nivel master poate șterge linii.', 'error');
                return;
            }
            if (!await askConfirm(`Sigur ștergi linia ${lid}? Trebuie să nu mai aibă utilaje asociate.`)) return;
            try {
                const res = await fetch(`/api/config/lines/${encodeURIComponent(lid)}`, { method: 'DELETE' });
                const data: RaspunsScriere = await res.json();
                if (!res.ok) { toast(data.error || 'Eroare la ștergere linie', 'error'); return; }
                toast(`Linia ${lid} a fost ștearsă.`);
                loadOverview();
            } catch (err) {
                toast('Eroare: ' + (err as Error).message, 'error');
            }
        });
    });

    $$('.active-mold-select').forEach(sel => {
        sel.addEventListener('change', async (e) => {
            const machineId = sel.dataset.machine;
            const value = (sel as HTMLInputElement).value ? (sel as HTMLInputElement).value.trim() : '';
            try {
                const res = await fetch(`/api/config/machines/${encodeURIComponent(machineId!)}/active_mold`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ active_mold_id: value || null })
                });
                const data: RaspunsScriere = await res.json();
                if (!res.ok) { toast(data.error || 'Eroare'); return; }
                toast(value ? `Matriță activă: ${value}` : 'Matriță activă: niciuna');
                loadOverview();
            } catch (err) { toast('Eroare: ' + (err as Error).message); }
        });
    });

    document.getElementById('btn-open-add-line')?.addEventListener('click', () => openAddLineModal());
    document.getElementById('btn-open-add-equip')?.addEventListener('click', () => openAddEquipModal());
    document.getElementById('btn-open-shared-sensors')?.addEventListener('click', () => openSharedSensorsModal());
    document.getElementById('btn-open-counters')?.addEventListener('click', () => openCountersModal());

    const sidebarStats = document.getElementById('sidebar-stats');
    if (sidebarStats) sidebarStats.innerHTML = `
        <span>🏭 ${runningCount}/${factory.production_lines.length} active</span>
        <span>📦 ${partsNum.toLocaleString()} piese</span>
        <span>⚠ ${alerts.length + (state.openCaseAlerts || []).length} alerte</span>
    `;
    } catch (err) {
        console.error('loadOverview', err);
        const errMsg = (err as Error)?.message || String(err) || 'eroare necunoscută';
        const errStack = ((err as Error)?.stack || '').split('\n').slice(0, 3).join(' | ');
        try { (state as OverviewState).lastOverviewError = { message: errMsg, stack: (err as Error)?.stack || '', ts: Date.now() }; } catch(_) {}
        if (grid) grid.innerHTML = '<div class="alarm-empty"><span class="icon">⚠</span>Nu s-au putut încărca datele. Verifică conexiunea și serverul. <button type="button" class="btn btn-sm" onclick="loadOverview()">Reîncearcă</button><div style="margin-top:8px;font-size:.7rem;color:var(--text-muted);font-family:monospace;text-align:left;max-width:800px">Detaliu: ' + escHtml(errMsg) + (errStack ? '<br><span style="opacity:.65">' + escHtml(errStack.substring(0, 240)) + '</span>' : '') + '</div></div>';
        if (statsEl) statsEl.innerHTML = '<div class="stat-card"><div class="label">Eroare</div><div class="value">—</div></div>';
    }
}

/* ═══ P3.2: LIVE LINE DETAIL MODAL (read-only) ═══════════════════
 * Deschis la click pe mini-card din "Status linii (live)".
 * Afișează informativ: status colector, putere live, ultimii senzori
 * citiți pentru fiecare utilaj de pe linie. Read-only — fără butoane
 * de editare. Pentru editare, utilizatorul folosește cardul mare cu
 * utilaje (deschis cu click pe header).
 */
function _llTimeAgoSec(sec: number | null | undefined): string {
    if (sec == null) return '—';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}min`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}z`;
}

function _llBuildSensorTile(eqId: string, sensorKey: string, label: string, unit: string, val: number | undefined, sev: string): string {
    if (val == null || isNaN(val)) {
        return `<div class="ll-sens ll-sens-na">
            <span class="ll-sens-label">${escHtml(label)}</span>
            <span class="ll-sens-val">—</span>
        </div>`;
    }
    const sevCls = sev === 'critical' ? 'll-sens-crit' : sev === 'warning' ? 'll-sens-warn' : 'll-sens-ok';
    return `<div class="ll-sens ${sevCls}" data-eq="${escHtml(eqId)}" data-sensor="${escHtml(sensorKey)}">
        <span class="ll-sens-label">${escHtml(label)}</span>
        <span class="ll-sens-val">${val.toFixed(unit === 'mm/s' ? 2 : 1)}<small>${escHtml(unit || '')}</small></span>
    </div>`;
}

let _llRefreshTimer: number | null = null;

export async function openLineLiveDetail(lineId: string): Promise<void> {
    if (!lineId) return;

    // Eliminăm un modal existent dacă deja e deschis (re-deschidere rapidă pe alt card).
    const existing = document.getElementById('modal-line-live');
    if (existing) existing.remove();
    if (_llRefreshTimer) { clearInterval(_llRefreshTimer); _llRefreshTimer = null; }

    const overlay = document.createElement('div');
    overlay.id = 'modal-line-live';
    overlay.className = 'config-modal-overlay';
    overlay.innerHTML = `
        <div class="config-modal ll-modal">
            <div class="config-modal-header">
                <h3>📡 <span id="ll-title">${escHtml(lineId)}</span> <span class="ll-readonly-badge">DOAR INFORMATIV</span></h3>
                <button class="config-modal-close" type="button" aria-label="Închide">&times;</button>
            </div>
            <div class="config-modal-body" id="ll-body">
                <div style="text-align:center;color:var(--text-muted);padding:2rem">Se încarcă datele live…</div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const close = () => {
        overlay.remove();
        if (_llRefreshTimer) { clearInterval(_llRefreshTimer); _llRefreshTimer = null; }
    };
    overlay.querySelector('.config-modal-close')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function escHandler(e) {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', escHandler);
        }
    });

    const renderBody = async () => {
        try {
            // Re-fetch /api/lines/status pentru valorile live (10s freshness via auto-refresh).
            const [statusRes, structRes, sensorsRes]: [{ lines: StareLinie[] }, StructuraFabrica | null,
                Record<string, Record<string, CitireSenzor>>] = await Promise.all([
                api('/api/lines/status').catch(() => ({ lines: [] })),
                api('/api/factory_structure').catch(() => null),
                api('/api/telemetry/sensors/latest').catch(() => ({})),
            ]);
            const lines = (statusRes && statusRes.lines) || [];
            const lineStatus = lines.find((l: StareLinie) => l.id === lineId);
            // Caută linia atât în production_lines cât și în utilities (pentru TRAFO/UTIL_AER etc.)
            let lineInfo: LinieLive | null | undefined = null;
            let isUtility = false;
            if (structRes) {
                lineInfo = (structRes.production_lines || []).find((p: LinieStructura) => p.id === lineId);
                if (!lineInfo && structRes.utilities && structRes.utilities[lineId]) {
                    const u = structRes.utilities[lineId];
                    lineInfo = {
                        id: lineId,
                        product: u.label || lineId,
                        equipment: u.machines || [],
                    };
                    isUtility = true;
                }
            }

            const body = document.getElementById('ll-body');
            const title = document.getElementById('ll-title');
            if (!body) return;
            if (!lineInfo) {
                body.innerHTML = `<div style="color:var(--text-muted);text-align:center;padding:2rem">Linia <b>${escHtml(lineId)}</b> nu a fost găsită în structura fabricii.</div>`;
                return;
            }
            if (title) {
                title.innerHTML = `${escHtml(lineInfo.id)} — ${escHtml(lineInfo.product || '')}${isUtility ? ' <span style="font-size:.6rem;color:var(--text-muted);background:var(--bg);padding:1px 5px;border-radius:3px">UTILITATE</span>' : ''}`;
            }

            // ─── Status badges ───
            const stRun = lineStatus?.running_state;
            const runBadge = stRun === 'running'
                ? '<span class="ll-state ll-state-run">▶ ÎN PRODUCȚIE</span>'
                : stRun === 'stopped'
                    ? '<span class="ll-state ll-state-stop">■ OPRITĂ</span>'
                    : '<span class="ll-state ll-state-unk">? necunoscut</span>';
            const collStr = lineStatus?.collector_status || 'unknown';
            const collColor = collStr === 'green' ? '#10b981' : collStr === 'yellow' ? '#fbbf24' : collStr === 'red' ? '#ef4444' : '#6b7280';
            const collBadge = `<span class="ll-coll" style="--col:${collColor}" title="Vârsta heartbeat: ${escHtml(_llTimeAgoSec(lineStatus?.collector_hb_age_sec))}">● Colector ${escHtml(collStr.toUpperCase())}</span>`;

            // ─── Power și freshness ───
            const powerKw = lineStatus?.power_kw;
            const powerAge = lineStatus?.data_age_sec;
            const powerHtml = powerKw != null
                ? `<div class="ll-power-big">${powerKw.toFixed(2)}<span class="ll-power-unit">kW</span></div>
                   <div class="ll-power-meta">
                     <span title="Vârsta ultimei citiri">🕓 ${escHtml(_llTimeAgoSec(powerAge))}</span>
                     ${lineStatus?.power_meter_id ? `<span title="Meter sursă">📟 ${escHtml(lineStatus.power_meter_id)}</span>` : ''}
                   </div>`
                : '<div class="ll-power-big ll-power-na">— kW</div><div class="ll-power-meta">Niciun meter conectat / fără date</div>';

            // ─── Lista utilaje + senzori live ───
            const equipment = lineInfo.equipment || [];
            const equipHtml = equipment.length === 0
                ? '<div style="color:var(--text-muted);text-align:center;padding:1rem">Niciun utilaj configurat pe această linie.</div>'
                : equipment.map((eq: UtilajStructura) => {
                    const eqSensors = (sensorsRes && sensorsRes[eq.id]) || {};
                    const cfgs: SensorCfg[] = (state.sensorConfigs![eq.id] || []) as SensorCfg[];
                    // Top senzori live (max 6) — sortăm critic→warning→normal→fără-date
                    const tiles: string[] = [];
                    cfgs.slice(0, 6).forEach((cfg: SensorCfg) => {
                        if (!cfg.enabled) return;
                        const obj = eqSensors[cfg.sensor_key];
                        const v = obj && typeof obj === 'object' ? obj.value : obj;
                        const num = (v != null && !isNaN(Number(v))) ? Number(v) : undefined;
                        const sev = num != null ? getSensorSeverity(num, cfg.warn_threshold, cfg.crit_threshold) : 'normal';
                        tiles.push(_llBuildSensorTile(eq.id, cfg.sensor_key, cfg.label || cfg.sensor_key, cfg.unit || '', num, sev));
                    });
                    const sensorsBlock = tiles.length > 0
                        ? `<div class="ll-eq-sensors">${tiles.join('')}</div>`
                        : '<div class="ll-eq-empty">— niciun senzor configurat —</div>';

                    // Putere per utilaj (din telemetrie sau manual_power_kw fallback)
                    const pwrObj = eqSensors.power || eqSensors.power_total || eqSensors.consum;
                    const pwrVal = pwrObj && typeof pwrObj === 'object' ? pwrObj.value : pwrObj;
                    const pwrStr = pwrVal != null && !isNaN(Number(pwrVal))
                        ? `${Number(pwrVal).toFixed(2)} kW`
                        : (eq.manual_power_kw && eq.manual_power_kw > 0 ? `${parseFloat(eq.manual_power_kw).toFixed(2)} kW (manual)` : '— kW');

                    // `main_machine` e rândul utilajului, nu id-ul (12 oct 2026: comparația cu obiectul nu ieșea niciodată
                    // adevărată și eticheta PRINCIPAL nu apărea)
                    const isMain = lineInfo.main_machine && eq.id === lineInfo.main_machine.id ? '<span class="ll-eq-main-badge">PRINCIPAL</span>' : '';
                    const eqHeaderLbl = eq.display_name || eq.id;
                    return `<div class="ll-eq-card">
                        <div class="ll-eq-head">
                            <div class="ll-eq-title">
                                <span class="ll-eq-id">${escHtml(eqHeaderLbl)}</span>
                                ${isMain}
                            </div>
                            <span class="ll-eq-pwr" title="Putere instantanee">${escHtml(pwrStr)}</span>
                        </div>
                        <div class="ll-eq-name">${escHtml(eq.name || '')}</div>
                        ${sensorsBlock}
                    </div>`;
                }).join('');

            body.innerHTML = `
                <div class="ll-status-row">
                    ${runBadge}
                    ${collBadge}
                    ${lineStatus?.power_gating_enabled ? '<span class="ll-coll" style="--col:#fbbf24" title="Power gating activ">⚡ Gating ON</span>' : ''}
                </div>
                <div class="ll-power-block">
                    ${powerHtml}
                </div>
                <hr class="ll-sep">
                <h4 class="ll-section-title">Utilaje (${equipment.length}) — citiri live</h4>
                ${equipHtml}
                <hr class="ll-sep">
                <p class="ll-readonly-note">
                    Toate datele afișate sunt informative și se actualizează automat la 10 secunde.
                    Pentru editare ore funcționare, status manual sau ștergere linie, închide această fereastră
                    și folosește cardul mare cu utilaje din pagina principală.
                </p>
            `;
        } catch (e) {
            const body = document.getElementById('ll-body');
            if (body) body.innerHTML = `<div style="color:var(--red);text-align:center;padding:2rem">Eroare la încărcare: ${escHtml((e as Error)?.message || String(e))}</div>`;
        }
    };

    await renderBody();
    // Auto-refresh la 10s pentru date live
    _llRefreshTimer = window.setInterval(() => {
        if (!document.getElementById('modal-line-live')) {
            if (_llRefreshTimer) { clearInterval(_llRefreshTimer); _llRefreshTimer = null; }
            return;
        }
        renderBody();
    }, 10000);
}

/* ═══════════════════════════════════════════════════════════════════════════
   PART 3 (2026-05-15) — GO-LIVE modale + indicator global + handlers
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Modal: pornire oficială linie inactivă (M2 / M9).
 * Permite completare ore pe utilajele liniei + validare mașină principală.
 */
async function openStartLineModal(lineId: string): Promise<void> {
    const audit: AuditGolive | null = await api('/api/golive/audit').catch(() => null);
    if (!audit) { toast('Nu pot încărca starea GO-LIVE'); return; }
    const lineEntry = (audit.lines || []).find((l: GoliveLinie) => l.id === lineId);
    if (!lineEntry) { toast(`Linia ${lineId} nu există`); return; }
    if (lineEntry.is_active === 1) { toast(`Linia ${lineId} este deja activă`); return; }

    const machinesOnLine = (audit.machines || []).filter((m: GoliveUtilaj) =>
        (m.line || '').split(',')[0].trim() === lineId
    );

    const old = document.getElementById('modal-start-line');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-start-line';
    modal.className = 'golive-modal';
    let rowsHtml = '';
    machinesOnLine.forEach((m: GoliveUtilaj) => {
        const isMain = m.machine_type === 'injection' || m.machine_type === 'compression';
        const oh = m.operating_hours || 0;
        rowsHtml += `<tr class="${isMain ? 'main-machine-row' : ''}">
            <td>${escHtml(m.id)}${isMain ? ' <span class="badge badge-main">PRINCIPAL</span>' : ''}</td>
            <td>${escHtml(m.machine_type || '—')}</td>
            <td><input type="number" min="0" step="0.1" class="sl-hours-input" data-machine="${escHtml(m.id)}" value="${oh}" placeholder="0"></td>
            <td>${m.intervals_total} intervale</td>
        </tr>`;
    });

    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content">
            <h2>▶ Pornește oficial linia ${escHtml(lineId)}</h2>
            <p class="golive-modal-subtitle">${escHtml(lineEntry.product || '')}</p>
            <div class="golive-modal-warning">
                <strong>Linia ${escHtml(lineId)}</strong> va fi marcată ca pornită oficial pe <strong>data de azi</strong>.
                Toate utilajele cu ore vor primi GO-LIVE simultan, iar intervalele de mentenanță vor fi
                marcate ca "la zi". Mașina principală TREBUIE să aibă ore &gt; 0.
            </div>
            <table class="golive-modal-table">
                <thead><tr><th>Utilaj</th><th>Tip</th><th>Ore funcționare</th><th>Intervale</th></tr></thead>
                <tbody>${rowsHtml}</tbody>
            </table>
            <div class="golive-modal-actions">
                <button type="button" class="btn btn-outline" id="sl-cancel">Anulează</button>
                <button type="button" class="btn btn-green" id="sl-start">▶ Pornește linia ${escHtml(lineId)}</button>
            </div>
            <div class="golive-modal-result" id="sl-result"></div>
        </div>`;
    document.body.appendChild(modal);

    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('sl-cancel')?.addEventListener('click', () => modal.remove());

    document.getElementById('sl-start')?.addEventListener('click', async () => {
        const inputs = modal.querySelectorAll('.sl-hours-input') as NodeListOf<HTMLInputElement>;
        const machinesPayload: { id: string | undefined; operating_hours: number }[] = [];
        inputs.forEach(inp => {
            const v = parseFloat(inp.value);
            if (!isNaN(v) && v >= 0) {
                machinesPayload.push({ id: inp.dataset.machine, operating_hours: v });
            }
        });
        const resultEl = document.getElementById('sl-result');
        if (resultEl) resultEl.innerHTML = '<div style="color:#999">Se procesează…</div>';
        try {
            const r = await fetch(`/api/golive/line/${encodeURIComponent(lineId)}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ machines: machinesPayload })
            });
            const data: RezultatStartLinie = await r.json();
            if (!r.ok) {
                if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml(data.message || data.error || data.status || 'Eroare necunoscută')}</div>`;
                return;
            }
            const sm: Partial<RezumatStartLinie> = data.summary || {};
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-success">
                ✅ Linia <strong>${escHtml(lineId)}</strong> este acum LIVE.<br>
                <small>Utilaje GO-LIVE: ${sm.machines_golive || 0}/${sm.machines_total || 0}, intervale baseline: ${sm.intervals_baselined || 0}, evenimente sintetice: ${sm.synthetic_events_added || 0}, istorie folosită: ${sm.history_used || 0}.</small>
            </div>`;
            toast(`Linia ${lineId} pornită oficial`);
            setTimeout(() => { modal.remove(); loadOverview(); loadGoliveStatus(); }, 1800);
        } catch (e) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml((e as Error).message || String(e))}</div>`;
        }
    });
}

/**
 * Modal: alocă ore pentru un utilaj fără ore + opțional rulează GO-LIVE imediat.
 */
async function openAllocateHoursModal(machineId: string): Promise<void> {
    const old = document.getElementById('modal-allocate-hours');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-allocate-hours';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content small">
            <h2>⚙ Alocă ore — ${escHtml(machineId)}</h2>
            <p class="golive-modal-subtitle">Setează valoarea actuală a contorului de ore funcționare.</p>
            <div class="golive-modal-form-row">
                <label>Ore funcționare:</label>
                <input type="number" min="0" step="0.1" id="ah-hours" placeholder="ex: 1500" autofocus>
            </div>
            <div class="golive-modal-actions">
                <button type="button" class="btn btn-outline" id="ah-cancel">Anulează</button>
                <button type="button" class="btn btn-green" id="ah-confirm">Setează &amp; GO-LIVE</button>
            </div>
            <div class="golive-modal-result" id="ah-result"></div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('ah-cancel')?.addEventListener('click', () => modal.remove());
    document.getElementById('ah-confirm')?.addEventListener('click', async () => {
        const v = parseFloat((document.getElementById('ah-hours') as HTMLInputElement).value);
        if (isNaN(v) || v < 0) { toast('Valoare ore invalidă'); return; }
        const resultEl = document.getElementById('ah-result');
        if (resultEl) resultEl.innerHTML = '<div style="color:#999">Se procesează…</div>';
        try {
            const r = await fetch(`/api/golive/machine/${encodeURIComponent(machineId)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operating_hours: v })
            });
            const data: RezultatGolive = await r.json();
            if (!r.ok) {
                if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml(data.message || data.error || data.status || 'Eroare')}</div>`;
                return;
            }
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-success">✅ GO-LIVE OK · ${data.intervals_baselined || 0} intervale (sintetice: ${data.synthetic_events_added || 0}, istorie: ${data.history_used || 0}).</div>`;
            toast(`${machineId}: GO-LIVE confirmat`);
            setTimeout(() => { modal.remove(); loadOverview(); loadGoliveStatus(); }, 1800);
        } catch (e) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml((e as Error).message || String(e))}</div>`;
        }
    });
}

/**
 * Modal: confirmă GO-LIVE pentru un utilaj care are deja ore (status=ready_to_golive).
 */
async function openConfirmGoliveModal(machineId: string): Promise<void> {
    const audit: AuditGolive | null = await api('/api/golive/audit?hypothetical=1').catch(() => null);
    const m = (audit?.machines || []).find((x: GoliveUtilaj) => x.id === machineId);
    const hyp: Partial<IpotezaGolive> = m?.hypothetical || {};
    const old = document.getElementById('modal-confirm-golive');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-confirm-golive';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content small">
            <h2>🟢 Confirmă GO-LIVE — ${escHtml(machineId)}</h2>
            <p class="golive-modal-subtitle">Toate intervalele de mentenanță vor fi marcate ca "la zi" la <strong>15 mai 2026</strong>.</p>
            <div class="golive-modal-info">
                <div>Ore funcționare actuale: <strong>${(m?.operating_hours || 0).toFixed(1)} h</strong></div>
                <div>Intervale total: <strong>${m?.intervals_total || 0}</strong></div>
                <div>Vor fi create evenimente sintetice: <strong>${hyp.synthetic_events_added || 0}</strong></div>
                <div>Istorie reală folosită: <strong>${hyp.history_used || 0}</strong> (logs din 15 mar - 15 mai 2026)</div>
            </div>
            <div class="golive-modal-actions">
                <button type="button" class="btn btn-outline" id="cg-cancel">Anulează</button>
                <button type="button" class="btn btn-green" id="cg-confirm">🟢 Confirmă GO-LIVE</button>
            </div>
            <div class="golive-modal-result" id="cg-result"></div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('cg-cancel')?.addEventListener('click', () => modal.remove());
    document.getElementById('cg-confirm')?.addEventListener('click', async () => {
        const resultEl = document.getElementById('cg-result');
        if (resultEl) resultEl.innerHTML = '<div style="color:#999">Se procesează…</div>';
        try {
            const r = await fetch(`/api/golive/machine/${encodeURIComponent(machineId)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const data: RezultatGolive = await r.json();
            if (!r.ok) {
                if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml(data.message || data.error || data.status || 'Eroare')}</div>`;
                return;
            }
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-success">✅ GO-LIVE OK · ${data.intervals_baselined || 0} intervale baseline.</div>`;
            toast(`${machineId}: GO-LIVE`);
            setTimeout(() => { modal.remove(); loadOverview(); loadGoliveStatus(); }, 1500);
        } catch (e) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml((e as Error).message || String(e))}</div>`;
        }
    });
}

/**
 * Indicator global GO-LIVE în topbar.
 * Verde dacă toate mașinile sunt LIVE; portocaliu dacă mai sunt pending.
 */
export async function loadGoliveStatus(): Promise<void> {
    const el = document.getElementById('golive-ind');
    const dot = document.getElementById('golive-ind-dot');
    const lbl = document.getElementById('golive-ind-label');
    if (!el || !dot || !lbl) return;
    try {
        const s: StareGolive = await api('/api/golive/status');
        if (!s) return;
        el.style.display = '';
        const live = s.machines_live || 0;
        const total = s.machines_total || 0;
        const pending = s.machines_pending_hours || 0;
        const inactive = s.machines_line_inactive || 0;
        const linesActive = s.lines_active || 0;
        const linesPending = s.lines_pending || 0;
        const cls = (live === total - inactive && pending === 0 && linesPending === 0) ? 'on' : 'warning';
        dot.className = `ai-ind-dot ${cls}`;
        lbl.textContent = `GO-LIVE ${live}/${total}`;
        el.title = `GO-LIVE: ${live}/${total} utilaje active · ${pending} fără ore · ${inactive} pe linie inactivă · linii active ${linesActive}, pending ${linesPending}`;
    } catch (_) { /* nu blocăm UI dacă endpoint indisponibil */ }
}

// Hook delegated handlers pe document (handle re-render după loadOverview)
document.addEventListener('click', (e: MouseEvent) => {
    const t = e.target as HTMLElement;
    if (!t) return;
    if (t.classList.contains('btn-start-line')) {
        e.stopPropagation();
        const lid = t.dataset.lineId || '';
        if (lid) openStartLineModal(lid);
    } else if (t.classList.contains('btn-allocate-hours')) {
        e.stopPropagation();
        const eq = t.dataset.eq || '';
        if (eq) openAllocateHoursModal(eq);
    } else if (t.classList.contains('btn-golive-confirm')) {
        e.stopPropagation();
        const eq = t.dataset.eq || '';
        if (eq) openConfirmGoliveModal(eq);
    } else if (t.classList.contains('btn-eq-mark-stop')) {
        e.stopPropagation();
        const eq = t.dataset.machine || '';
        if (eq) openMarkMaintenanceStopModal(eq);
    } else if (t.classList.contains('btn-eq-mark-clear')) {
        e.stopPropagation();
        const eq = t.dataset.machine || '';
        if (eq) clearMaintenanceStop(eq);
    } else if (t.classList.contains('btn-eq-threshold')) {
        e.stopPropagation();
        const eq = t.dataset.machine || '';
        const cur = parseFloat(t.dataset.threshold || '8') || 8;
        if (eq) openThresholdEditModal(eq, cur);
    } else if (t.closest('.line-alerts-toggle')) {
        e.stopPropagation();
        const btn = t.closest('.line-alerts-toggle') as HTMLElement;
        const body = btn.nextElementSibling as HTMLElement | null;
        if (!body || !body.classList.contains('line-alerts-body')) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        body.classList.toggle('collapsed', expanded);
        const icon = btn.querySelector('.line-alerts-toggle-icon');
        if (icon) icon.textContent = expanded ? '▶' : '▼';
    } else if (t.closest('.line-alert-clickable')) {
        e.stopPropagation();
        const row = t.closest('.line-alert-clickable') as HTMLElement;
        const kind = row.dataset.alertKind;
        const alertId = row.dataset.alertId;
        const lineId = row.dataset.lineId || undefined;
        if (!alertId || !kind) return;
        if (kind === 'maint') openMaintenanceAlertDetailModal(alertId, lineId);
        else if (kind === 'live') openLiveAlarmDetailModal(alertId, lineId);
    }
});

/* ═══════════════════════════════════════════════════════════════════════════
   FOUR-STATE MODEL (2026-05-24) — Modal "Marchează: oprire pentru mentenanță"
   ═══════════════════════════════════════════════════════════════════════════ */
async function openMarkMaintenanceStopModal(machineId: string): Promise<void> {
    const old = document.getElementById('modal-mark-maintenance-stop');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-mark-maintenance-stop';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content small">
            <h2>🔧 Marchează oprire pentru mentenanță</h2>
            <p class="golive-modal-subtitle">Mașina <strong>${escHtml(machineId)}</strong> va fi marcată ca <em>mentenanță în curs</em>. Alarmele normale se ascund; doar criticele apar ca sugestii „cât ești oprit".</p>
            <div class="golive-modal-form-row">
                <label>Motiv (opțional):</label>
                <textarea id="mms-reason" rows="3" placeholder="Ex: Schimbare furtunuri HP, înlocuire matriță…" style="width:100%;padding:8px;border-radius:8px;border:1px solid var(--border, #334155);background:rgba(255,255,255,.05);color:var(--text);font-family:inherit"></textarea>
            </div>
            <div class="golive-modal-actions">
                <button type="button" class="btn btn-outline" id="mms-cancel">Anulează</button>
                <button type="button" class="btn btn-green" id="mms-confirm">🔧 Marchează</button>
            </div>
            <div class="golive-modal-result" id="mms-result"></div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('mms-cancel')?.addEventListener('click', () => modal.remove());
    document.getElementById('mms-confirm')?.addEventListener('click', async () => {
        const reason = (document.getElementById('mms-reason') as HTMLTextAreaElement).value.trim();
        const resultEl = document.getElementById('mms-result');
        if (resultEl) resultEl.innerHTML = '<div style="color:#999">Se procesează…</div>';
        try {
            const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/mark_maintenance_stop`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason })
            });
            const data: RaspunsOprire = await r.json();
            if (!r.ok) {
                if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml(data.error || data.detail || 'Eroare')}</div>`;
                return;
            }
            toast(`${machineId}: marcată mentenanță în curs`);
            setTimeout(() => { modal.remove(); loadOverview(); }, 800);
        } catch (e) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml((e as Error)?.message || String(e))}</div>`;
        }
    });
}

async function clearMaintenanceStop(machineId: string): Promise<void> {
    const ok = await import('./core').then(m => m.customConfirm(
        `Marchezi că ${machineId} e doar avarie (nu mentenanță planificată)? Mașina va reveni la „pauză tehnică" auto-clasificată; alarmele rămân ascunse până se repornește efectiv linia.`,
        '↺ Confirmare — Avarie, nu mentenanță'
    ));
    if (!ok) return;
    try {
        const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/clear_maintenance_stop`, {
            method: 'POST',
            credentials: 'include',
        });
        const data: RaspunsOprire = await r.json();
        if (!r.ok) {
            toast(`Eroare: ${data.error || data.detail || 'necunoscută'}`, 'error');
            return;
        }
        toast(`${machineId}: flag mentenanță șters`);
        loadOverview();
    } catch (e) {
        toast(`Eroare: ${(e as Error)?.message || String(e)}`, 'error');
    }
}

async function openThresholdEditModal(machineId: string, current: number): Promise<void> {
    const old = document.getElementById('modal-threshold-edit');
    if (old) old.remove();

    const modal = document.createElement('div');
    modal.id = 'modal-threshold-edit';
    modal.className = 'golive-modal';
    modal.innerHTML = `
        <div class="golive-modal-backdrop"></div>
        <div class="golive-modal-content small">
            <h2>⚙ Prag pauză tehnică — ${escHtml(machineId)}</h2>
            <p class="golive-modal-subtitle">După câte ore offline mașina trece în „pauză tehnică" (clock înghețat, alarmele se ascund). Default: 8h.</p>
            <div class="golive-modal-form-row">
                <label>Ore (1 – 720):</label>
                <input type="number" min="1" max="720" step="1" id="thr-hours" value="${current}" style="width:100%;padding:8px;border-radius:8px;border:1px solid var(--border, #334155);background:rgba(255,255,255,.05);color:var(--text)">
            </div>
            <div class="golive-modal-actions">
                <button type="button" class="btn btn-outline" id="thr-cancel">Anulează</button>
                <button type="button" class="btn btn-green" id="thr-confirm">Salvează</button>
            </div>
            <div class="golive-modal-result" id="thr-result"></div>
        </div>`;
    document.body.appendChild(modal);
    modal.querySelector('.golive-modal-backdrop')?.addEventListener('click', () => modal.remove());
    document.getElementById('thr-cancel')?.addEventListener('click', () => modal.remove());
    document.getElementById('thr-confirm')?.addEventListener('click', async () => {
        const v = parseFloat((document.getElementById('thr-hours') as HTMLInputElement).value);
        const resultEl = document.getElementById('thr-result');
        if (isNaN(v) || v < 1 || v > 720) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ Valoare invalidă (1–720)</div>`;
            return;
        }
        if (resultEl) resultEl.innerHTML = '<div style="color:#999">Se salvează…</div>';
        try {
            const r = await fetch(`/api/machines/${encodeURIComponent(machineId)}/threshold_pause`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ hours: v })
            });
            const data: RaspunsScriere = await r.json();
            if (!r.ok) {
                if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml(data.error || 'Eroare')}</div>`;
                return;
            }
            toast(`${machineId}: prag pauză tehnică = ${v}h`);
            setTimeout(() => { modal.remove(); loadOverview(); }, 800);
        } catch (e) {
            if (resultEl) resultEl.innerHTML = `<div class="golive-modal-error">❌ ${escHtml((e as Error)?.message || String(e))}</div>`;
        }
    });
}

// Refresh GO-LIVE status la fiecare loadOverview (auto-poll la 30s)
setInterval(() => loadGoliveStatus(), 30000);

/* ═══ OVERVIEW TAB SWITCH ══════════════════════════════════════ */
document.querySelectorAll('.ov-tab').forEach(btn => {
    btn.addEventListener('click', () => {
        document.querySelectorAll('.ov-tab').forEach(b => {
            b.classList.remove('active');
            (b as HTMLElement).style.borderBottomColor = 'transparent';
            (b as HTMLElement).style.color = 'var(--text-muted)';
        });
        btn.classList.add('active');
        (btn as HTMLElement).style.borderBottomColor = 'var(--accent)';
        (btn as HTMLElement).style.color = 'var(--accent)';
        const tab = (btn as HTMLElement).dataset.ovtab;
        document.getElementById('ov-panel-config')!.style.display = tab === 'config' ? '' : 'none';
        document.getElementById('ov-panel-layout')!.style.display = tab === 'layout' ? '' : 'none';
        if (tab === 'layout') loadFactoryLayout();
    });
});
